import {
  Drawer,
  Form,
  message,
  Select,
  Table,
  InputNumber,
  Breadcrumb,
  Dropdown,
  Space,
  DatePicker,
  Input,
  Modal,
  Typography,
  Popover,
  Tooltip,
  Popconfirm,
  Descriptions,
  Tag,
  Button,
} from "antd";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Icon } from "../../../components/Component";
import { Alert, Col } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { tagRender } from "../projects/projectCreation";
import { tasksApis } from "../../../api/NewTasks";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { activityApis } from "../../../api/Activity";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { EditAccess } from "../../../components/accessControl/AccessControl";
import { projectsApis } from "../../../api/projects";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import dayjs from "dayjs";
import ReminderPopper from "./ReminderPopper";
import RecurringFrequencyPopper from "./RecurringFrequencyPopper";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { updateTaskListDetailsModalSlice } from "../../../store/slices/projectManagement/task-list-view/TaskListDetailModalSlice";
import { ReactTyped } from "react-typed";
import { DownOutlined, EditOutlined } from "@ant-design/icons";
import { getLabel } from "../phases/PhaseFieldDetailsUpdate";
import CheckListDrawer from "./CheckListDrawer";
import IssueApprovalDrawer from "../issues/IssueApprovalDrawer";
import DocumentsTab from "../projectDocument/DocumentsTab";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdown";
import contractorsApis from "../../../api/master/contractors";
import useSequenceNumbering from "../../../hooks/useSequenceNumbering";
import {
  SequenceInput,
  SequenceConfigModal,
} from "../../../components/sequence";
import "../shared/project.css";
import { useWbsName } from "../shared/useWbsCodes";
import "../projects/hub/hub.css";
import {
  computeTaskPriceBreakdown,
  DEFAULT_CESS_PERCENT,
  DEFAULT_GST_PERCENT,
} from "../shared/taskPrice";
import TaskPriceBreakdownPanel from "../shared/TaskPriceBreakdownPanel";
const { Text } = Typography;

// Extend SelectOption to include projectUserId and roleName
interface ProjectUserOption extends SelectOption {
  projectUserId: number;
  roleName: string;
}

interface ProjectUser {
  userId: string;
  userName: string;
  projectUserId: number;
  roleName: string;
}

interface ProjectTag {
  tagName: string;
  tagId: number;
}

interface Priority {
  priorityName: string;
  priorityId: number;
}

interface WorkCategory {
  workCategoryName: string;
  workCategoryId: number;
}

interface BudgetLine {
  lineId: number;
  headerId: number;
  budgetId: string;
  budgetLineName: string;
  budgetValue: number;
  allocatedLineValue: number;
}

interface ActivityBudgetInfo {
  activityBudgetLineId?: number;
  budgetLineName?: string;
  budgetValue?: number;
  allocatedAmount?: number;
}

interface ActivityContractorInfo {
  activityContractorId?: number;
  contractorName?: string;
}

interface ActivityResource {
  activityResourceId: number;
  laborTypeId: number;
  laborTypeName: string;
  laborCategoryId: number;
  laborCategory: string;
  status: string;
}

interface ActivityBoq {
  activityBoqId: number;
  boqTargetNumber: string;
  boqTitle?: string;
  lines: Array<{
    materialName: string;
    categoryName: string;
    allotedQuantity: number;
    taskAllocatedQuantity?: number;
    taskAvailableQuantity?: number;
    uomName: string;
    activityBoqLineId: number;
  }>;
}

interface ActivityBoqOption extends SelectOption {
  activityBoqId: number;
  boqTargetNumber: string;
  boqTitle?: string;
  boqLineName?: string;
  lines: Array<{
    materialName: string;
    categoryName: string;
    allotedQuantity: number;
    taskAllocatedQuantity?: number;
    taskAvailableQuantity?: number;
    uomName: string;
    activityBoqLineId: number;
  }>;
  // Site hierarchy fields (at BOQ level)
  siteStructureUnitId?: number;
  siteStructureSubUnitId?: number;
  floorNo?: string;
  locationId?: number;
}

interface BoqLine {
  boqTargetNumber: string;
  boqTitle?: string;
  boqLineName?: string;
  categoryName: string;
  materialName: string;
  allotedQuantity: number;
  taskAllocatedQuantity?: number;
  taskAvailableQuantity?: number;
  taskAllotedQuantity?: number;
  uomName: string;
  activityBoqId: number;
  activityBoqLineId: number;
  key: string;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  dataIndex: string;
  title: string;
  inputType: "number" | "text";
  record: BoqLine;
  index: number;
  children: React.ReactNode;
  form: any;
  outputFactor?: number;
}

// Max digits allowed in any numeric input field
const MAX_NUMBER_DIGITS = 10;

// Restrict numeric inputs to digits only (no letters/symbols/emojis), a single
// decimal point, and at most MAX_NUMBER_DIGITS digits.
const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const controlKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];
  if (controlKeys.includes(e.key)) return;
  if (e.key === ".") {
    if ((e.currentTarget.value || "").includes(".")) e.preventDefault();
    return;
  }
  if (!/^[0-9]$/.test(e.key)) {
    e.preventDefault();
    return;
  }
  const digitCount = (e.currentTarget.value || "").replace(/\D/g, "").length;
  const hasSelection =
    e.currentTarget.selectionStart !== e.currentTarget.selectionEnd;
  if (digitCount >= MAX_NUMBER_DIGITS && !hasSelection) {
    e.preventDefault();
  }
};

// Keep alphanumeric characters plus - / ( ) _ (strips spaces, other symbols, emojis)
const sanitizeAlphaNumeric = (value: string) =>
  (value || "").replace(/[^a-zA-Z0-9()_\/-]/g, "");

// Isolated live total for the tools add-row. Using useWatch here means only this
// tiny component re-renders as the user types rate/quantity — the parent form
// (and its inputs) don't re-render, so the cursor/focus stays put.
const ToolTotalPreview: React.FC<{ control: any }> = ({ control }) => {
  const quantity = useWatch({ control, name: "toolQuantity" });
  const rate = useWatch({ control, name: "toolRate" });
  const total = (Number(quantity) || 0) * (Number(rate) || 0);
  return (
    <input
      type="text"
      className="form-control"
      readOnly
      disabled
      style={{ height: "38px" }}
      value={total ? total.toFixed(2) : ""}
    />
  );
};

// Live total for the tools rate-analysis add-row (RA qty * RA rate).
const ToolRateAnalysisTotalPreview: React.FC<{ control: any }> = ({
  control,
}) => {
  const quantity = useWatch({ control, name: "toolRateAnalysisQty" });
  const rate = useWatch({ control, name: "toolRateAnalysisRate" });
  const total = (Number(quantity) || 0) * (Number(rate) || 0);
  return (
    <input
      type="text"
      className="form-control"
      readOnly
      disabled
      style={{ height: "38px" }}
      value={total ? total.toFixed(2) : ""}
    />
  );
};

// A single split-material line captured under a tool/machinery row.
export interface ToolMaterialItem {
  // Present only for materials that already exist in the DB (edit flow) — used
  // to build the updated/deleted diff. Absent for newly added materials.
  taskToolMaterialId?: number;
  materialId: number;
  materialName?: string;
  subMaterialId?: number;
  subMaterialName?: string;
  quantity?: number;
  workUomId?: number;
  workUomName?: string;
  rate?: number;
  rateAnalysisQty?: number;
  rateAnalysisUomId?: number;
  rateAnalysisUomName?: string;
  rateAnalysisRate?: number;
}

// Max split-materials per tool (keeps the drawer list bounded / page healthy).
export const MAX_TOOL_MATERIALS = 50;

// Self-contained manager for a tool's split-cost materials (diesel, spares...).
// Rendered inside a per-tool drawer. Keeps its own add-row state so the giant
// parent form is untouched, and paginates the list so 20+ materials stay smooth.
// Exported so the task EDIT form (TaskInformationById) can reuse the exact same
// add/list/validate UX without duplicating it.
export const ToolMaterialsManager: React.FC<{
  materials: ToolMaterialItem[];
  toolOverallTotal: number;
  materialOptions: SelectOption[];
  workUomsList: SelectOption[];
  showRateAnalysis: boolean;
  outputFactor: number;
  disabled?: boolean;
  rateAnalysisLabel: string;
  overallLabel: string;
  onChange: (materials: ToolMaterialItem[]) => void;
}> = ({
  materials,
  toolOverallTotal,
  materialOptions,
  workUomsList,
  showRateAnalysis,
  outputFactor,
  disabled,
  rateAnalysisLabel,
  overallLabel,
  onChange,
}) => {
  const [materialId, setMaterialId] = useState<number | null>(null);
  const [subMaterialId, setSubMaterialId] = useState<number | null>(null);
  const [subMaterialOptions, setSubMaterialOptions] = useState<SelectOption[]>(
    [],
  );
  const [quantity, setQuantity] = useState<string>("");
  const [uomId, setUomId] = useState<number | null>(null);
  const [rate, setRate] = useState<string>("");
  const [raQty, setRaQty] = useState<string>("");
  const [raUomId, setRaUomId] = useState<number | null>(null);
  const [raRate, setRaRate] = useState<string>("");
  // Index of the material row being edited in place, or null when adding new.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // Resolved sub-material names for already-saved rows (the API returns only
  // ids; we fetch the names per distinct material so the list shows them).
  const [subMaterialNameMap, setSubMaterialNameMap] = useState<
    Record<number, string>
  >({});

  const distinctMaterialIdsKey = Array.from(
    new Set(
      (materials || [])
        .map((m) => m.materialId)
        .filter((id): id is number => id != null),
    ),
  )
    .sort((a, b) => a - b)
    .join(",");

  useEffect(() => {
    let active = true;
    const ids = distinctMaterialIdsKey
      ? distinctMaterialIdsKey.split(",").map(Number)
      : [];
    if (!ids.length) return;
    (async () => {
      const map: Record<number, string> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const res =
              await tasksApis.fetchMachinerySubMaterialDropDown(id);
            (res?.data || []).forEach((s: any) => {
              map[s.id ?? s.subMaterialId] =
                s.subMaterialName ?? s.name ?? s.label;
            });
          } catch {
            /* ignore */
          }
        }),
      );
      if (active) setSubMaterialNameMap(map);
    })();
    return () => {
      active = false;
    };
  }, [distinctMaterialIdsKey]);

  const resetAddRow = () => {
    setMaterialId(null);
    setSubMaterialId(null);
    setSubMaterialOptions([]);
    setQuantity("");
    setUomId(null);
    setRate("");
    setRaQty("");
    setRaUomId(null);
    setRaRate("");
    setEditingIndex(null);
  };

  // Load an existing material row into the add/edit form for in-place editing.
  const startEdit = async (index: number) => {
    const m = (materials || [])[index];
    if (!m) return;
    setEditingIndex(index);
    setMaterialId(m.materialId ?? null);
    setQuantity(m.quantity != null ? String(m.quantity) : "");
    setUomId(m.workUomId ?? null);
    setRate(m.rate != null ? String(m.rate) : "");
    setRaQty(m.rateAnalysisQty != null ? String(m.rateAnalysisQty) : "");
    setRaUomId(m.rateAnalysisUomId ?? null);
    setRaRate(m.rateAnalysisRate != null ? String(m.rateAnalysisRate) : "");
    setSubMaterialId(m.subMaterialId ?? null);
    // Load sub-material options for the chosen material so its current
    // sub-material renders selected.
    if (m.materialId) {
      try {
        const response = await tasksApis.fetchMachinerySubMaterialDropDown(
          m.materialId,
        );
        const { data, status } = response || {};
        if (status) {
          setSubMaterialOptions(
            (data || []).map((item: any) => ({
              label: item.subMaterialName ?? item.name ?? item.label,
              value: item.id ?? item.subMaterialId ?? item.value,
            })),
          );
        }
      } catch {
        /* ignore */
      }
    }
  };

  // Cascading sub-material dropdown — load when the material changes.
  const handleMaterialChange = async (value: number | null) => {
    setMaterialId(value);
    setSubMaterialId(null);
    setSubMaterialOptions([]);
    if (!value) return;
    try {
      const response = await tasksApis.fetchMachinerySubMaterialDropDown(value);
      const { data, status } = response || {};
      if (status) {
        setSubMaterialOptions(
          (data || []).map((item: any) => ({
            label: item.subMaterialName ?? item.name ?? item.label,
            value: item.id ?? item.subMaterialId ?? item.value,
          })),
        );
      }
    } catch (e) {
      message.error("Failed to fetch sub-materials");
    }
  };

  const isEditing = editingIndex != null;
  const lineTotal = (Number(quantity) || 0) * (Number(rate) || 0);
  const raLineTotal = (Number(raQty) || 0) * (Number(raRate) || 0);
  // Sum of all OTHER rows (exclude the row being edited) for the cap check.
  const otherRowsTotal = (materials || []).reduce(
    (sum, m, i) =>
      i === editingIndex
        ? sum
        : sum + (Number(m.quantity) || 0) * (Number(m.rate) || 0),
    0,
  );
  const existingTotal = (materials || []).reduce(
    (sum, m) => sum + (Number(m.quantity) || 0) * (Number(m.rate) || 0),
    0,
  );

  const uomLabel = (id?: number | null) =>
    workUomsList.find((u: SelectOption) => u.value === id)?.label || "";

  const handleAdd = () => {
    if (!materialId) {
      message.error("Please select a material");
      return;
    }
    if (!isEditing && (materials || []).length >= MAX_TOOL_MATERIALS) {
      message.error(`You can add up to ${MAX_TOOL_MATERIALS} materials per tool`);
      return;
    }
    // Duplicate check ignores the row being edited.
    const isDuplicate = (materials || []).some(
      (m, i) =>
        i !== editingIndex &&
        m.materialId === materialId &&
        (m.subMaterialId ?? null) === (subMaterialId ?? null),
    );
    if (isDuplicate) {
      message.error("This material / sub-material is already added");
      return;
    }
    // Edge case: split-material totals cannot exceed the tool's overall total.
    if (
      toolOverallTotal > 0 &&
      Math.round((otherRowsTotal + lineTotal) * 100) / 100 >
        Math.round(toolOverallTotal * 100) / 100
    ) {
      message.error(
        `Total of split materials cannot exceed the tool/machinery total (${toolOverallTotal.toFixed(2)})`,
      );
      return;
    }
    const materialName = materialOptions.find(
      (m) => m.value === materialId,
    )?.label;
    const subMaterialName = subMaterialOptions.find(
      (m) => m.value === subMaterialId,
    )?.label;
    const item: ToolMaterialItem = {
      // Preserve the DB id when editing an existing row so the update diff
      // treats it as an update, not a delete + insert.
      ...(isEditing && (materials || [])[editingIndex]?.taskToolMaterialId != null
        ? { taskToolMaterialId: (materials || [])[editingIndex].taskToolMaterialId }
        : {}),
      materialId,
      materialName: typeof materialName === "string" ? materialName : undefined,
      ...(subMaterialId
        ? {
            subMaterialId,
            subMaterialName:
              typeof subMaterialName === "string" ? subMaterialName : undefined,
          }
        : {}),
      ...(quantity !== "" ? { quantity: Number(quantity) } : {}),
      ...(uomId != null
        ? { workUomId: Number(uomId), workUomName: uomLabel(uomId) || undefined }
        : {}),
      ...(rate !== "" ? { rate: Number(rate) } : {}),
      // Rate analysis only when active.
      ...(showRateAnalysis && raQty !== ""
        ? { rateAnalysisQty: Number(raQty) }
        : {}),
      ...(showRateAnalysis && raUomId != null
        ? {
            rateAnalysisUomId: Number(raUomId),
            rateAnalysisUomName: uomLabel(raUomId) || undefined,
          }
        : {}),
      ...(showRateAnalysis && raRate !== ""
        ? { rateAnalysisRate: Number(raRate) }
        : {}),
    };
    if (isEditing) {
      onChange(
        (materials || []).map((m, i) => (i === editingIndex ? item : m)),
      );
    } else {
      onChange([...(materials || []), item]);
    }
    resetAddRow();
  };

  const numberInputStyle = { height: "38px", width: "100%" } as const;
  const allocationPct =
    toolOverallTotal > 0 ? (existingTotal / toolOverallTotal) * 100 : 0;
  const overAllocated = allocationPct > 100.0001;
  // The add limit never blocks an in-place edit of an existing row.
  const limitReached =
    !isEditing && (materials || []).length >= MAX_TOOL_MATERIALS;

  return (
    <div className="tool-materials-manager">
      {/* Keep every add-row control the same height so Qty / UOM / Rate / Total
          line up across the Rate-Analysis and Overall panels. */}
      <style>{`
        .tool-materials-manager .ant-select-single,
        .tool-materials-manager .ant-select-single .ant-select-selector { height: 38px !important; }
        .tool-materials-manager .ant-select-single .ant-select-selector .ant-select-selection-item,
        .tool-materials-manager .ant-select-single .ant-select-selector .ant-select-selection-placeholder { line-height: 36px !important; }
        .tool-materials-manager .ant-input-number { height: 38px; }
        .tool-materials-manager .ant-input-number-input { height: 36px; }
        .tool-materials-manager .form-control,
        .tool-materials-manager .input-group { height: 38px; }
        .tool-materials-manager .form-label { margin-bottom: 4px; }
      `}</style>
      {/* Allocation progress */}
      <div className="mb-3">
        <div
          className="d-flex justify-content-between align-items-center mb-1"
          style={{ fontSize: "13px" }}
        >
          <span style={{ fontWeight: 600, color: "#004293" }}>
            Allocated split-material cost
          </span>
          <span style={{ color: overAllocated ? "#ff4d4f" : "#595959" }}>
            ₹{existingTotal.toFixed(2)}
            {toolOverallTotal > 0 ? ` of ₹${toolOverallTotal.toFixed(2)}` : ""}
            {" · "}
            {(materials || []).length}/{MAX_TOOL_MATERIALS} materials
          </span>
        </div>
        {toolOverallTotal > 0 && (
          <div
            style={{
              height: "6px",
              background: "#f0f0f0",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.min(100, allocationPct)}%`,
                height: "100%",
                background: overAllocated ? "#ff4d4f" : "#52c41a",
                transition: "width .2s",
              }}
            />
          </div>
        )}
      </div>

      {/* Add-material form */}
      <div
        className="p-3 mb-3"
        style={{
          background: "#fafafa",
          borderRadius: "8px",
          border: "1px solid #f0f0f0",
        }}
      >
        <div className="row g-2">
          <div className="col-6">
            <label className="form-label">
              Material <span style={{ color: "#ff4d4f" }}>*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select material (e.g. HSD, Spares)"
              options={materialOptions}
              value={materialId ?? undefined}
              onChange={handleMaterialChange}
              disabled={disabled || limitReached}
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </div>
          <div className="col-6">
            <label className="form-label">Sub-material</label>
            <Select
              style={{ width: "100%" }}
              placeholder={
                materialId ? "Select sub-material" : "Select a material first"
              }
              options={subMaterialOptions}
              value={subMaterialId ?? undefined}
              onChange={(v) => setSubMaterialId(v ?? null)}
              disabled={disabled || limitReached || !materialId}
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </div>
        </div>

        <div className="row g-2 mt-1">
          {showRateAnalysis && (
            <div className="col-6">
              <div
                className="p-2 h-100"
                style={{ background: "#e6f4ff", borderRadius: "6px" }}
              >
                <div
                  className="mb-2"
                  style={{ fontWeight: 600, color: "#004293", fontSize: "12px" }}
                >
                  {rateAnalysisLabel}
                </div>
                <div className="row g-2 align-items-end">
                  <div className="col-3">
                    <label className="form-label">Qty</label>
                    <InputNumber
                      style={numberInputStyle}
                      min={0}
                      max={9999999999}
                      onKeyDown={handleNumberKeyDown}
                      placeholder="Qty"
                      value={raQty === "" ? undefined : Number(raQty)}
                      disabled={disabled || limitReached}
                      onChange={(v) => {
                        const val = v == null ? "" : String(v);
                        setRaQty(val);
                        const f = Number(outputFactor) || 0;
                        if (f > 0 && val !== "") {
                          setQuantity(
                            String(Number((f * Number(val)).toFixed(2))),
                          );
                        }
                      }}
                    />
                  </div>
                  <div className="col-3">
                    <label className="form-label">UOM</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="UOM"
                      options={workUomsList}
                      value={raUomId ?? undefined}
                      disabled={disabled || limitReached}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      onChange={(v) => {
                        setRaUomId(v ?? null);
                        setUomId(v ?? null);
                      }}
                    />
                  </div>
                  <div className="col-3">
                    <label className="form-label">Rate</label>
                    <InputNumber
                      style={numberInputStyle}
                      prefix="₹"
                      min={0}
                      max={9999999999}
                      onKeyDown={handleNumberKeyDown}
                      placeholder="Rate"
                      value={raRate === "" ? undefined : Number(raRate)}
                      disabled={disabled || limitReached}
                      onChange={(v) => {
                        const val = v == null ? "" : String(v);
                        setRaRate(val);
                        setRate(val);
                      }}
                    />
                  </div>
                  <div className="col-3">
                    <label className="form-label">Total</label>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      disabled
                      style={{ height: "38px" }}
                      value={raLineTotal ? raLineTotal.toFixed(2) : ""}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={showRateAnalysis ? "col-6" : "col-12"}>
            <div
              className="p-2 h-100"
              style={{ background: "#f6ffed", borderRadius: "6px" }}
            >
              <div
                className="mb-2"
                style={{ fontWeight: 600, color: "#004293", fontSize: "12px" }}
              >
                {overallLabel}
              </div>
              <div className="row g-2 align-items-end">
                <div className="col-3">
                  <label className="form-label">Qty</label>
                  <InputNumber
                    style={numberInputStyle}
                    min={0}
                    max={9999999999}
                    onKeyDown={handleNumberKeyDown}
                    placeholder="Qty"
                    value={quantity === "" ? undefined : Number(quantity)}
                    disabled={disabled || limitReached}
                    onChange={(v) => setQuantity(v == null ? "" : String(v))}
                  />
                </div>
                <div className="col-3">
                  <label className="form-label">UOM</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="UOM"
                    options={workUomsList}
                    value={uomId ?? undefined}
                    disabled={disabled || limitReached}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    onChange={(v) => setUomId(v ?? null)}
                  />
                </div>
                <div className="col-3">
                  <label className="form-label">Rate</label>
                  <InputNumber
                    style={numberInputStyle}
                    prefix="₹"
                    min={0}
                    max={9999999999}
                    onKeyDown={handleNumberKeyDown}
                    placeholder="Rate"
                    value={rate === "" ? undefined : Number(rate)}
                    disabled={disabled || limitReached}
                    onChange={(v) => setRate(v == null ? "" : String(v))}
                  />
                </div>
                <div className="col-3">
                  <label className="form-label">Total</label>
                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    disabled
                    style={{ height: "34px" }}
                    value={lineTotal ? lineTotal.toFixed(2) : ""}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2 d-flex justify-content-between align-items-center">
          <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
            {isEditing
              ? "Editing a material — update the fields and click Update."
              : limitReached
              ? `Limit reached — max ${MAX_TOOL_MATERIALS} materials per tool.`
              : "UOM & Rate auto-fill from Rate Analysis and can be edited."}
          </span>
          <div className="d-flex" style={{ gap: "8px" }}>
            {isEditing && (
              <Button
                size="small"
                onClick={resetAddRow}
                disabled={disabled}
              >
                Cancel
              </Button>
            )}
            <Button
              type="primary"
              size="small"
              onClick={handleAdd}
              disabled={disabled || limitReached}
            >
              <em className={`icon ni ni-${isEditing ? "check" : "plus"}`} />{" "}
              {isEditing ? "Update Material" : "Add Material"}
            </Button>
          </div>
        </div>
      </div>

      {/* Materials list */}
      {(materials || []).length > 0 && (
        <Table
          className="mt-1"
          size="small"
          bordered
          pagination={
            (materials || []).length > 8
              ? { pageSize: 8, size: "small", showSizeChanger: false }
              : false
          }
          scroll={{ x: "max-content" }}
          rowKey={(_, idx) => `${idx}`}
          dataSource={materials}
          columns={[
            {
              title: "Material",
              dataIndex: "materialName",
              key: "materialName",
              render: (t: any, r: ToolMaterialItem) =>
                t ||
                materialOptions.find((m) => m.value === r.materialId)?.label ||
                r.materialId,
            },
            {
              title: "Sub-material",
              dataIndex: "subMaterialName",
              key: "subMaterialName",
              render: (t: any, r: ToolMaterialItem) =>
                t ||
                (r.subMaterialId != null
                  ? subMaterialNameMap[r.subMaterialId] || ""
                  : ""),
            },
            ...(showRateAnalysis
              ? [
                  {
                    title: rateAnalysisLabel,
                    align: "center" as const,
                    onHeaderCell: () => ({
                      style: { background: "#e6f4ff", fontWeight: 600 },
                    }),
                    children: [
                      {
                        title: "Qty",
                        dataIndex: "rateAnalysisQty",
                        key: "rateAnalysisQty",
                        render: (t: any) => t ?? "",
                      },
                      {
                        title: "UOM",
                        dataIndex: "rateAnalysisUomId",
                        key: "raUom",
                        render: (t: any, r: ToolMaterialItem) =>
                          uomLabel(t) || r.rateAnalysisUomName || "",
                      },
                      {
                        title: "Rate",
                        dataIndex: "rateAnalysisRate",
                        key: "rateAnalysisRate",
                        render: (t: any) => t ?? "",
                      },
                      {
                        title: "Total",
                        key: "raTotal",
                        render: (_: any, r: ToolMaterialItem) => {
                          const tot =
                            (Number(r.rateAnalysisQty) || 0) *
                            (Number(r.rateAnalysisRate) || 0);
                          return tot ? tot.toFixed(2) : "";
                        },
                      },
                    ],
                  },
                ]
              : []),
            {
              title: overallLabel,
              align: "center" as const,
              onHeaderCell: () => ({
                style: { background: "#f6ffed", fontWeight: 600 },
              }),
              children: [
                {
                  title: "Qty",
                  dataIndex: "quantity",
                  key: "quantity",
                  render: (t: any) => t ?? "",
                },
                {
                  title: "UOM",
                  dataIndex: "workUomId",
                  key: "workUomId",
                  render: (t: any, r: ToolMaterialItem) =>
                    uomLabel(t) || r.workUomName || "",
                },
                {
                  title: "Rate",
                  dataIndex: "rate",
                  key: "rate",
                  render: (t: any) => t ?? "",
                },
                {
                  title: "Total",
                  key: "total",
                  render: (_: any, r: ToolMaterialItem) => {
                    const tot =
                      (Number(r.quantity) || 0) * (Number(r.rate) || 0);
                    return tot ? tot.toFixed(2) : "";
                  },
                },
              ],
            },
            {
              title: "Action",
              key: "action",
              align: "center" as const,
              render: (_: any, _r: ToolMaterialItem, idx: number) => (
                <div
                  className="d-flex justify-content-center"
                  style={{ gap: "14px" }}
                >
                  <em
                    className="icon ni ni-edit"
                    title="Edit material"
                    onClick={() => {
                      if (disabled) return;
                      startEdit(idx);
                    }}
                    style={{
                      cursor: disabled ? "not-allowed" : "pointer",
                      color: "#004293",
                    }}
                  ></em>
                  <em
                    className="icon ni ni-trash"
                    title="Remove material"
                    onClick={() => {
                      if (disabled) return;
                      // Keep the edit form in sync if the edited row is removed.
                      if (editingIndex === idx) resetAddRow();
                      onChange((materials || []).filter((_m, i) => i !== idx));
                    }}
                    style={{
                      cursor: disabled ? "not-allowed" : "pointer",
                      color: "#ff4d4f",
                    }}
                  ></em>
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

// Isolated serial-number input for the tools add-row. Controlling the value via
// useWatch (instead of watch in the parent render) keeps re-renders local
// so the cursor/focus doesn't jump while typing.
const ToolSerialNumberInput: React.FC<{
  control: any;
  setValue: (name: any, value: any) => void;
  disabled?: boolean;
}> = ({ control, setValue, disabled }) => {
  const value = useWatch({ control, name: "toolSerialNumber" }) || "";
  return (
    <input
      type="text"
      className="form-control"
      placeholder="Enter serial number"
      maxLength={50}
      disabled={disabled}
      style={{ height: "38px" }}
      value={value}
      onChange={(e) =>
        setValue("toolSerialNumber", sanitizeAlphaNumeric(e.target.value))
      }
    />
  );
};

const TaskCreationEditableCell: React.FC<EditableCellProps> = ({
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  form,
  outputFactor,
  ...restProps
}) => {
  // BOQ rate-analysis qty — pre-fills the overall Allocate Qty via outputFactor.
  if (dataIndex === "boqRateAnalysisQty") {
    const id = record?.activityBoqId;
    return (
      <td {...restProps}>
        <Form.Item name={`rateAnalysisQty_${id}`} style={{ margin: 0 }}>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            max={9999999999}
            onKeyDown={handleNumberKeyDown}
            placeholder="Unit qty"
            onChange={(value) => {
              const factor = Number(outputFactor) || 0;
              if (factor > 0 && value !== null && value !== undefined) {
                form.setFieldsValue({
                  [`allocatedQuantity_${id}`]: Number(
                    (factor * Number(value)).toFixed(2),
                  ),
                });
              }
            }}
          />
        </Form.Item>
      </td>
    );
  }
  // BOQ rate-analysis rate — pre-fills the overall Rate (user can override).
  if (dataIndex === "boqRateAnalysisRate") {
    const id = record?.activityBoqId;
    return (
      <td {...restProps}>
        <Form.Item name={`rateAnalysisRate_${id}`} style={{ margin: 0 }}>
          <InputNumber
            style={{ width: "100%" }}
            prefix="₹"
            min={0}
            max={9999999999}
            onKeyDown={handleNumberKeyDown}
            placeholder="Unit rate"
            onChange={(value) => {
              form.setFieldsValue({ [`rate_${id}`]: value });
            }}
          />
        </Form.Item>
      </td>
    );
  }
  if (dataIndex === "boqRateAnalysisTotalAmount") {
    const id = record?.activityBoqId;
    return (
      <td {...restProps}>
        <Form.Item shouldUpdate noStyle>
          {() => {
            const qty = Number(form.getFieldValue(`rateAnalysisQty_${id}`)) || 0;
            const rate =
              Number(form.getFieldValue(`rateAnalysisRate_${id}`)) || 0;
            const total = qty * rate;
            return <span>{total ? total.toFixed(2) : ""}</span>;
          }}
        </Form.Item>
      </td>
    );
  }
  if (dataIndex === "allocatedQuantity") {
    // Ensure we have a valid quantity values, default to 0 if undefined/null
    const availableQuantity =
      record.taskAvailableQuantity ?? record.allotedQuantity ?? 0;
    const isDisabled = Number(availableQuantity) === 0;
    const fieldName = `${dataIndex}_${record?.activityBoqId}`;
    const existingValue = form.getFieldValue(fieldName);
    const initialQuantity =
      existingValue !== undefined && existingValue !== null
        ? existingValue
        : undefined;

    return (
      <td {...restProps}>
        <Form.Item
          name={fieldName}
          style={{ margin: 0 }}
          initialValue={initialQuantity}
          rules={[
            {
              required: !isDisabled,
              message: `Please Input ${title}!`,
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value === null || value === undefined || value === "") {
                  return Promise.resolve();
                }
                const numericValue = Number(value);
                if (Number.isNaN(numericValue)) {
                  return Promise.reject(new Error("Invalid quantity"));
                }
                if (numericValue < 0) {
                  return Promise.reject(
                    new Error("Quantity cannot be negative"),
                  );
                }
                if (numericValue <= Number(availableQuantity)) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Cannot exceed task available quantity"),
                );
              },
            }),
          ]}
        >
          {isDisabled ? (
            <Tooltip title="Task available quantity is 0. You cannot allocate any quantity.">
              <span style={{ display: "inline-block", width: "100%" }}>
                <InputNumber
                  style={{ width: "100%", pointerEvents: "none" }}
                  disabled
                />
              </span>
            </Tooltip>
          ) : (
            <InputNumber style={{ width: "100%" }} min={0} max={9999999999}
                  onKeyDown={handleNumberKeyDown} />
          )}
        </Form.Item>
      </td>
    );
  }
  if (dataIndex === "boqRate") {
    const fieldName = `rate_${record?.activityBoqId}`;
    // Default the rate to the material's standard price (editable by the user).
    const standardPrice = (record as any)?.standardPrice;
    const initialRate =
      standardPrice !== undefined && standardPrice !== null
        ? Number(standardPrice)
        : undefined;
    return (
      <td {...restProps}>
        <Form.Item
          name={fieldName}
          style={{ margin: 0 }}
          initialValue={initialRate}
          rules={[
            { required: true, message: `Please Input ${title}!` },
            {
              validator(_, value) {
                if (value === null || value === undefined || value === "") {
                  return Promise.resolve();
                }
                const numericValue = Number(value);
                if (Number.isNaN(numericValue)) {
                  return Promise.reject(new Error("Invalid rate"));
                }
                if (numericValue < 0) {
                  return Promise.reject(new Error("Rate cannot be negative"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber style={{ width: "100%" }} prefix="₹" min={0} max={9999999999}
                  onKeyDown={handleNumberKeyDown} />
        </Form.Item>
      </td>
    );
  }
  if (dataIndex === "boqTotalAmount") {
    const id = record?.activityBoqId;
    return (
      <td {...restProps}>
        <Form.Item shouldUpdate noStyle>
          {() => {
            const qty = Number(form.getFieldValue(`allocatedQuantity_${id}`)) || 0;
            const rate = Number(form.getFieldValue(`rate_${id}`)) || 0;
            const total = qty * rate;
            return <span>{total ? total.toFixed(2) : ""}</span>;
          }}
        </Form.Item>
      </td>
    );
  }
  return <td {...restProps}>{children}</td>;
};

const ResourceEditableCell: React.FC<any> = ({
  dataIndex,
  title,
  record,
  children,
  form,
  editing,
  workUomsList,
  outputFactor,
  ...restProps
}) => {
  // Rate analysis qty (unit-basis). Editing it pre-fills the overall quantity
  // as outputFactor * rateAnalysisQty (still user-editable afterwards).
  if (dataIndex === "rateAnalysisQty") {
    const id = record?.activityResourceId;
    const fieldName = `rateAnalysisQty_${id}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item name={fieldName} style={{ margin: 0 }}>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={9999999999}
              onKeyDown={handleNumberKeyDown}
              placeholder="Unit qty"
              onChange={(value) => {
                const factor = Number(outputFactor) || 0;
                if (factor > 0 && value !== null && value !== undefined) {
                  form.setFieldsValue({
                    [`quantity_${id}`]: Number(
                      (factor * Number(value)).toFixed(2),
                    ),
                  });
                }
              }}
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  // Rate analysis UOM — editing pre-fills the overall UOM (user can override).
  if (dataIndex === "rateAnalysisUomId") {
    const id = record?.activityResourceId;
    const fieldName = `rateAnalysisUomId_${id}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item name={fieldName} style={{ margin: 0 }}>
            <Select
              style={{ width: "100%" }}
              placeholder="Select UOM"
              options={workUomsList}
              showSearch
              optionFilterProp="label"
              allowClear
              onChange={(value) => {
                form.setFieldsValue({ [`workUomId_${id}`]: value });
              }}
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  // Rate analysis rate — editing pre-fills the overall rate (user can override).
  if (dataIndex === "rateAnalysisRate") {
    const id = record?.activityResourceId;
    const fieldName = `rateAnalysisRate_${id}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item name={fieldName} style={{ margin: 0 }}>
            <InputNumber
              style={{ width: "100%" }}
              prefix="₹"
              min={0}
              max={9999999999}
              onKeyDown={handleNumberKeyDown}
              placeholder="Unit rate"
              onChange={(value) => {
                form.setFieldsValue({ [`rate_${id}`]: value });
              }}
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "rateAnalysisTotalAmount") {
    const id = record?.activityResourceId;
    return (
      <td {...restProps}>
        <Form.Item shouldUpdate noStyle>
          {() => {
            const qty = Number(form.getFieldValue(`rateAnalysisQty_${id}`)) || 0;
            const rate =
              Number(form.getFieldValue(`rateAnalysisRate_${id}`)) || 0;
            const total = qty * rate;
            return <span>{total ? total.toFixed(2) : ""}</span>;
          }}
        </Form.Item>
      </td>
    );
  }
  if (dataIndex === "resourceCount") {
    const fieldName = `${dataIndex}_${record?.activityResourceId}`;

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            initialValue={record?.resourceCount || undefined}
            rules={[
              {
                type: "number",
                min: 1,
                message: `${title} must be at least 1`,
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={9999999999}
              onKeyDown={handleNumberKeyDown}
              placeholder="Enter count"
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "resourceRate" || dataIndex === "resourceQuantity") {
    const key = dataIndex === "resourceRate" ? "rate" : "quantity";
    const fieldName = `${key}_${record?.activityResourceId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            rules={[
              {
                type: "number",
                min: 0,
                max: 99999999,
                message: `${title} must be between 0 and 99999999`,
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              {...(dataIndex === "resourceRate" ? { prefix: "₹" } : {})}
              min={0}
              max={9999999999}
                  onKeyDown={handleNumberKeyDown}
              placeholder={`Enter ${title.toLowerCase()}`}
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "workUomId") {
    const fieldName = `workUomId_${record?.activityResourceId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item name={fieldName} style={{ margin: 0 }}>
            <Select
              style={{ width: "100%" }}
              placeholder="Select UOM"
              options={workUomsList}
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "resourceTotalAmount") {
    const id = record?.activityResourceId;
    return (
      <td {...restProps}>
        <Form.Item shouldUpdate noStyle>
          {() => {
            const qty = Number(form.getFieldValue(`quantity_${id}`)) || 0;
            const rate = Number(form.getFieldValue(`rate_${id}`)) || 0;
            const count = Number(form.getFieldValue(`resourceCount_${id}`)) || 0;
            const total = qty * rate * count;
            return <span>{total ? total.toFixed(2) : ""}</span>;
          }}
        </Form.Item>
      </td>
    );
  }
  return <td {...restProps}>{children}</td>;
};

interface TaskTag {
  tagName: string;
}

interface TaskBoq {
  activityBoqId: number;
  allotedQuantity: number;
  rate?: number;
  totalAmount?: number;
  rateAnalysisQty?: number;
  rateAnalysisUomId?: number;
  rateAnalysisRate?: number;
}

interface TaskResource {
  activityResourceId: number;
  resourceCount: number;
  rate?: number;
  quantity?: number;
  workUomId?: number;
  totalAmount?: number;
  rateAnalysisQty?: number;
  rateAnalysisUomId?: number;
  rateAnalysisRate?: number;
}

interface TaskUser {
  projectUserId: number;
  taskUserType: string;
}

export interface TaskCreationPayload {
  projectId: number;
  phaseId: number;
  activityId: number;
  taskName: string;
  internalTaskNumber: string;
  taskDescription: string;
  taskOwnerId: number;
  contractorId: number;
  taskActualStartDate: string;
  taskActualEndDate: string;
  taskPlannedStartDate: string;
  taskPlannedEndDate: string;
  priorityId: number;
  isBillable: boolean;
  reminderFlag: boolean;
  workCategoryId: number;
  taskTags: TaskTag[];
  taskBoqs: TaskBoq[];
  taskResources: TaskResource[];
  taskUsers: TaskUser[];
  taskId?: any;
  reminders?: any;
  taskWeightagePercentage: number;
  checklistRequired: boolean;
  checklistLinks: any[];
  approvalRequired?: boolean;
  autoApprovalRemarks?: string;
  stateApprovalHierarchy?: any[];
  documentsRequired?: boolean;
  documentsData?: any[];
  docKeys?: any[];
  documentFiles?: File[];
  workUom?: string;
  budgetLineId?: number;
  budgetValue?: number;
  outputQuantity?: number;
  outputBasisUomId?: number;
}

interface SubTaskCreationPayload {
  projectId: number;
  phaseId: number;
  activityId: number;
  taskId: string | number | null;
  subTaskName: string;
  subTaskDescription: string;
  subTaskOwnerId: number;
  contractorId: number;
  subTaskActualStartDate: string;
  subTaskActualEndDate: string;
  subTaskPlannedStartDate: string;
  subTaskPlannedEndDate: string;
  priorityId: number;
  isBillable: boolean;
  workHoursInSec?: number;
  reminderFlag: boolean;
  subTaskTags: TaskTag[];
  subTaskUsers: TaskUser[];
  reminders?: any;
}

// Imperative surface exposed to TaskCreatePage (page mode only).
export interface TaskCreationPageApi {
  validateStep: (step: number) => Promise<boolean>;
  submit: () => void;
  getBasics: () => Record<string, any>;
}

export interface TaskCreationPageContext {
  projectId: number;
  projectName: string;
  projectStatus?: string;
  phaseId: number;
  phaseName: string;
  activityId: number;
  activityName: string;
}

const TaskCreation: FC<{
  refreshData: () => void;
  // "page" renders the form inline for the full-page stepper (TaskCreatePage)
  // instead of the drawer; all context then comes from pageContext, not Redux.
  mode?: "drawer" | "page";
  activeStep?: number;
  pageContext?: TaskCreationPageContext;
  initialBasics?: Record<string, any>;
  apiRef?: React.MutableRefObject<TaskCreationPageApi | null>;
  onCreated?: () => void;
  onCancel?: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
}> = ({
  refreshData,
  mode = "drawer",
  activeStep = 0,
  pageContext,
  initialBasics,
  apiRef,
  onCreated,
  onCancel,
  onSubmittingChange,
}) => {
  const isPageMode = mode === "page";
  const { data: ActivityInDetailModal } = useSelector(
    (state: RootState) => state.ActivityInDetailModal,
  );
  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal,
  );
  const dispatch = useDispatch();
  const wbsName = useWbsName();
  const location = useLocation();
  const derivedProjectStatus = (location.state as any)?.extra?.screenName;
  const {
    id: sliceActivityId,
    projectResponse: activityProjectResponse,
    phaseResponse: activityPhaseResponse,
    extra: sliceExtra,
  }: any = ActivityInDetailModal || {};
  const id = isPageMode ? pageContext?.activityId : sliceActivityId;
  const extra = isPageMode ? {} : sliceExtra;
  const projectResponse = isPageMode
    ? {
        id: pageContext?.projectId,
        name: pageContext?.projectName,
        status: pageContext?.projectStatus ?? "Active",
      }
    : activityProjectResponse?.id
    ? activityProjectResponse
    : TaskListInDetailModal?.projectResponse;
  const phaseResponse = isPageMode
    ? {
        id: pageContext?.phaseId,
        name: pageContext?.phaseName,
        status: "",
        startDate: "",
        endDate: "",
      }
    : activityPhaseResponse?.id
    ? activityPhaseResponse
    : TaskListInDetailModal?.phaseResponse;
  const projectId = projectResponse?.id;
  const phaseId = phaseResponse?.id;
  const projectName = projectResponse?.name;
  const {
    id: sliceTaskId,
    showTaskCreationModal: sliceShowTaskCreationModal,
    isSubtask: sliceIsSubtask,
    isSubtaskUpdate: sliceIsSubtaskUpdate,
    selectedSubtaskDetails: sliceSelectedSubtaskDetails,
    currentSubtaskId,
    allowEdit,
    subtaskAllowEdit: sliceSubtaskAllowEdit,
    subtaskOpenInEdit,
  } = TaskListInDetailModal || {};
  // Page mode is always an open main-task create; slice state (possibly stale
  // from an earlier drawer session) must not leak in.
  const showTaskCreationModal = isPageMode ? true : sliceShowTaskCreationModal;
  const isSubtask = isPageMode ? false : sliceIsSubtask;
  const isSubtaskUpdate = isPageMode ? false : sliceIsSubtaskUpdate;
  const taskId = isPageMode ? undefined : sliceTaskId;
  const selectedSubtaskDetails = isPageMode
    ? undefined
    : sliceSelectedSubtaskDetails;
  const subtaskAllowEdit = isPageMode ? undefined : sliceSubtaskAllowEdit;
  const { startDate: activityStartDate, endDate: activityEndDate } =
    extra || {};
  const [messageApi, contextHolder] = message.useMessage();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [isEditMode, setIsEditMode] = useState(
    () => !isSubtaskUpdate || !!subtaskOpenInEdit
  );
  const selectedTaskDetails = isPageMode
    ? undefined
    : TaskListInDetailModal.selectedTaskDetails;
  let _allowEdit = true;
  const { status: projectStatus, isLocked, lockedBy } = projectResponse || {};
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [documents, setDocuments] = useState<{
    open: boolean;
    data: any[];
  }>({
    open: false,
    data: [],
  });
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [errorData, setErrorData] = useState<
    | {
        error: string;
        message: string;
      }
    | undefined
  >(undefined);
  useEffect(() => {
    setIsEditMode(!isSubtaskUpdate || !!subtaskOpenInEdit);
  }, [isSubtaskUpdate, subtaskOpenInEdit]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  useEffect(() => {
    if (selectedTaskDetails) {
      if (
        ["closed", "onhold", "archived"]?.includes(
          selectedTaskDetails.status?.toLowerCase(),
        )
      ) {
        _allowEdit = false;
      }
      if (isLocked && lockedBy != currentUser?.id) {
        _allowEdit = false;
      }
    }
  }, [selectedTaskDetails]);

  const methods = useForm<{
    taskPlannedStartDate: any;
    taskPlannedEndDate: any;
    taskOwnerId: number | null;
    contractorId: number | null;
    projectUserId: number[];
    isBillable: string;
    reminderFlag: boolean;
    boqLineIds: number[];
    workCategoryId: number | null;
    workType: string;
    workQuantity?: number;
    workUomId?: number;
    outputQuantity?: number;
    outputBasisUomId?: number | null;
    taskName: string;
    internalTaskNumber: string;
    taskDescription: string;
    priorityId: number | null;
    taskWeightage: number | null;
    ChecklistRequired: string;
    approvalRequired?: string;
    autoApprovalRemarks?: string;
    stateApprovalHierarchy?: any[];
    documentsRequired?: string;
    documentsData?: any[];
    budgetLineId?: number | null;
    allocatedBudgetAmount?: number;
    // GST-inclusive contract rate, plus the two configurable tax percentages.
    // Base / GST / cess / Basic Rate are derived, never form fields.
    rate?: number;
    gstPercent?: number;
    cessPercent?: number;
    toolCount?: string;
    toolSerialNumber?: string;
    toolRate?: string;
    toolQuantity?: string;
    toolWorkUomId?: number | null;
    toolRateAnalysisQty?: string;
    toolRateAnalysisRate?: string;
    toolRateAnalysisUomId?: number | null;
    toolType?: number | null;
    toolMachinery?: number | null;
    hyperlinkName?: string;
    hyperlinkUrl?: string;
    siteId?: string | number | null;
    workLevelSetting?: string;
    siteStructureUnitId?: string | number | null;
    floorNo?: string | null;
    siteStructureSubUnitId?: string | number | null;
    locationId?: string | number | null;
  }>({
    mode: "onChange",
    defaultValues: {
      taskPlannedStartDate: null,
      taskPlannedEndDate: null,
      taskOwnerId: null,
      contractorId: null,
      projectUserId: [],
      isBillable: "",
      reminderFlag: false,
      boqLineIds: [],
      workCategoryId: null,
      workType: "Quantity",
      workQuantity: undefined,
      workUomId: undefined,
      outputQuantity: undefined,
      outputBasisUomId: null,
      taskName: "",
      internalTaskNumber: "",
      taskDescription: "",
      priorityId: null,
      taskWeightage: null,
      ChecklistRequired: "",
      approvalRequired: "no",
      autoApprovalRemarks: "",
      stateApprovalHierarchy: [],
      documentsRequired: "no",
      documentsData: [],
      budgetLineId: null,
      allocatedBudgetAmount: undefined,
      rate: undefined,
      toolCount: "",
      toolSerialNumber: "",
      toolRate: "",
      toolQuantity: "",
      toolWorkUomId: null,
      toolRateAnalysisQty: "",
      toolRateAnalysisRate: "",
      toolRateAnalysisUomId: null,
      toolType: null,
      toolMachinery: null,
      hyperlinkName: "",
      hyperlinkUrl: "",
      siteId: null,
      workLevelSetting: undefined,
      siteStructureUnitId: null,
      floorNo: null,
      siteStructureSubUnitId: null,
      locationId: null,
    },
  });
  const [projectTagsList, setProjectTagsList] = useState<SelectOption[]>([]);
  const [tags, setTags] = useState<{ label: string; value: string }[]>([]);
  const [contractorOptions, setContractorOptions] = useState<any>([]);
  const [allSiteIds, setAllSiteIds] = useState<string>("");
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [projectUsers, setProjectUsers] = useState<ProjectUserOption[]>([]);
  const [remainders, setRemainders] = useState<any>(null);
  const [priorityList, setPriorityList] = useState<SelectOption[]>([]);
  const BILLABLE_OPTIONS: SelectOption[] = [
    { label: "Billable", value: "Billable" },
    { label: "Non Billable", value: "Non Billable" },
  ];
  const [activityBoqList, setActivityBoqList] = useState<ActivityBoqOption[]>(
    [],
  );
  const [allActivityBoqList, setAllActivityBoqList] = useState<
    ActivityBoqOption[]
  >([]);
  const [workCatgoriesList, setWorkCatgoriesList] = useState<SelectOption[]>(
    [],
  );
  const [activityResourcesList, setActivityResourcesList] = useState<
    ActivityResource[]
  >([]);
  const [selectedResourceKeys, setSelectedResourceKeys] = useState<React.Key[]>(
    [],
  );
  const [editingResourceKeys, setEditingResourceKeys] = useState<string[]>([]);
  const [boqLinesDataSource, setBoqLinesDataSource] = useState<BoqLine[]>([]);
  const materialAllocateErrorsRef = useRef<HTMLDivElement>(null);
  const [boqMaterialsError, setBoqMaterialsError] = useState<
    string | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [taskStatusList, setTaskStatusList] = useState<any[]>([]);

  // Store the original subtaskAllowEdit value (based on project/phase/activity status)
  const [originalSubtaskAllowEdit, setOriginalSubtaskAllowEdit] = useState<
    boolean | undefined
  >(undefined);

  const [form] = Form.useForm();
  const [resourceForm] = Form.useForm();
  // Bumped on BOQ / resource value changes so the Cost Summary re-computes live.
  // Safe because the BOQ section is rendered via a plain function call
  // ({CollapsePanel()}), not <CollapsePanel/>, so parent re-renders don't remount
  // (and don't steal focus from) its inputs.
  const [, setCostTick] = useState(0);
  const bumpCostTick = useCallback(() => setCostTick((n) => n + 1), []);
  const [Checklists, setChecklists] = useState({
    open: false,
    data: [],
  });
  const [checklistLinks, setChecklistLinks] = useState([]);

  // Helper function to check if current subtask status allows editing
  const isSubtaskStatusEditable = (subtaskStatus?: string) => {
    if (!subtaskStatus) return false;
    return !["closed", "archived", "onhold"].includes(
      subtaskStatus.toLowerCase(),
    );
  };

  const getStatusColor = (status?: string) => {
    if (!status) return "default";
    const normalized = status.toLowerCase().replace(/[\s_-]/g, "");
    const colorMap: Record<string, string> = {
      open: "blue",
      active: "blue",
      inprogress: "processing",
      underqc: "purple",
      onhold: "orange",
      closed: "green",
      completed: "green",
      archived: "default",
      cancelled: "red",
    };
    return colorMap[normalized] || "blue";
  };

  // Track if initial data has been loaded to prevent duplicate API calls
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [remainingWeightage, setRemainingWeightage] = useState<number | null>(
    null
  );

  const fetchTaskWeightages = async () => {
    try {
      const response: any = await tasksApis.getActivityTaskWeightage(
        Number(projectId),
        id ? Number(id) : undefined,
        phaseId ? Number(phaseId) : undefined
      );
      const {
        data: list,
        status,
      }: {
        status: boolean;
        data: { activityId: number; overallTaskWeightage: number }[];
      } = response || {};
      if (status) {
        const activityIdNum = id ? Number(id) : undefined;
        const matched =
          (list || []).find(
            (item) => Number(item?.activityId) === activityIdNum
          ) || (list || [])[0];
        const used = Number(matched?.overallTaskWeightage) || 0;
        setRemainingWeightage(Math.max(0, Number((100 - used).toFixed(2))));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    // Only load dropdown data when in edit mode (isEditMode is true)
    // For subtasks: isSubtaskUpdate false = edit mode, isSubtaskUpdate true = view mode
    // For regular tasks: always load data when modal opens
    const shouldLoadData =
      !initialDataLoaded &&
      !isLoadingData &&
      (showTaskCreationModal || isSubtaskUpdate); // Load data for both tasks and subtasks

    if (shouldLoadData && (showTaskCreationModal || isSubtaskUpdate)) {
      setIsLoadingData(true);

      const loadData = async () => {
        try {
          // Load common data for both tasks and subtasks
          await Promise.all([
            fetchPriority(),
            fetchWorkCatgoriesList(),
            fetchProjectUsers(),
            fetchProjectTags(),
            // Only fetch status list when we are in subtask view/update mode
            ...(isSubtaskUpdate ? [fetchTaskStatusList()] : []),
          ]);

          // Only load task-specific data for regular tasks (not subtasks)
          if (!isSubtask) {
            await Promise.all([
              fetchActivityBoqs(),
              fetchActivityResourcesList(),
              fetchBudgetLines(),
              fetchActivityBudgetInfo(),
              fetchToolsAndMachineryTypes(),
              fetchToolsAndMachinery(),
              fetchMachineryMaterials(),
              fetchTaskWeightages(),
            ]);
          }

          setInitialDataLoaded(true);
        } catch (error) {
          console.error("Error loading initial data:", error);
        } finally {
          setIsLoadingData(false);
        }
      };

      loadData();
    }
    return () => {
      form.resetFields();
    };
  }, [
    isSubtask,
    isSubtaskUpdate,
    initialDataLoaded,
    showTaskCreationModal,
    isLoadingData,
  ]); // Added isLoadingData to dependencies

  // Reset initial data loaded flag when modal is closed
  useEffect(() => {
    if (!showTaskCreationModal) {
      setInitialDataLoaded(false);
    }
  }, [showTaskCreationModal]);

  // Re-fetch project users and tags when projectId becomes available after initial load
  useEffect(() => {
    if (
      projectId &&
      (showTaskCreationModal || isSubtaskUpdate) &&
      projectUsers.length === 0
    ) {
      fetchProjectUsers();
      fetchProjectTags();
    }
  }, [projectId]);

  // Store the original subtaskAllowEdit value when component loads
  useEffect(() => {
    if (
      subtaskAllowEdit !== undefined &&
      originalSubtaskAllowEdit === undefined
    ) {
      setOriginalSubtaskAllowEdit(subtaskAllowEdit);
    }
  }, [subtaskAllowEdit, originalSubtaskAllowEdit]);

  // Monitor subtask status changes and update subtaskAllowEdit accordingly
  useEffect(() => {
    if (
      selectedSubtaskDetails?.subTaskStatus &&
      originalSubtaskAllowEdit !== undefined
    ) {
      const isSubtaskEditable = isSubtaskStatusEditable(
        selectedSubtaskDetails.subTaskStatus,
      );

      // Update subtaskAllowEdit in store based on current subtask status and original subtaskAllowEdit
      const updatedSubtaskAllowEdit =
        isSubtaskEditable && originalSubtaskAllowEdit;

      if (updatedSubtaskAllowEdit !== subtaskAllowEdit) {
        dispatch(
          updateTaskListDetailsModalSlice({
            subtaskAllowEdit: updatedSubtaskAllowEdit,
          }),
        );

        // If subtask is no longer editable and we're in edit mode, exit edit mode
        if (!updatedSubtaskAllowEdit && isEditMode) {
          setIsEditMode(false);
        }
      }
    }
  }, [
    selectedSubtaskDetails?.subTaskStatus,
    originalSubtaskAllowEdit,
    subtaskAllowEdit,
    dispatch,
    isEditMode,
    messageApi,
    isSubtaskStatusEditable,
  ]);

  const { handleSubmit, setValue, getValues, watch, reset } = methods;

  // Task numbering: auto-generate when a "Tasks" sequence is configured for
  // this project, otherwise fall back to manual entry so creation isn't blocked.
  const {
    numberingMode: taskNumberingMode,
    showNumberingModal,
    tempNumberingMode,
    setTempNumberingMode,
    activeSequences,
    currentSequence,
    canAuto,
    fetchSequenceAccess,
    openModal: openNumberingModal,
    closeModal: closeNumberingModal,
    saveModalSelection,
  } = useSequenceNumbering({
    moduleName: "Tasks",
    fieldName: "taskSequenceId",
    level: "Project",
    projectId,
    setValue: setValue as any,
    getValues: getValues as any,
  });

  useEffect(() => {
    if (projectId && showTaskCreationModal && !isSubtask) {
      fetchSequenceAccess();
    }
  }, [projectId, showTaskCreationModal, isSubtask, fetchSequenceAccess]);

  const startDateWatch = watch("taskPlannedStartDate");
  const boqLineIdsWatch = watch("boqLineIds");
  const selectedTeam = watch("projectUserId") || [];
  const selectedOwnerId = watch("taskOwnerId");
  const selectedContractorId = watch("contractorId");
  const approvalRequired = watch("approvalRequired");
  const documentsRequired = watch("documentsRequired");
  const workTypeWatch = watch("workType");
  const budgetLineIdWatch = watch("budgetLineId");
  const toolTypeWatch = watch("toolType");
  const toolMachineryWatch = watch("toolMachinery");
  const workLevelSetting = watch("workLevelSetting");
  const selectedUnitId = watch("siteStructureUnitId");
  const selectedFloorNo = watch("floorNo");
  const selectedSubUnitId = watch("siteStructureSubUnitId");
  const selectedLocationId = watch("locationId");

  // Structure type from the Site Breakdown Structure master (e.g. "Infra",
  // "High Rise", "Villa"). Infra projects don't use the site hierarchy at
  // task level, so the whole section (and its Location) is hidden for them.
  const [siteProjectTypeName, setSiteProjectTypeName] = useState<string | null>(
    null
  );
  const isInfraStructure = siteProjectTypeName?.toLowerCase() === "infra";

  // Hierarchy constants
  const HIERARCHY_UNIT_TITLES: Record<string, string> = {
    TOWER_BLOCK: "Tower / Block",
    VILLA: "Villa",
    FLOOR: "Tower / Block",
    FLAT: "Tower / Block",
  };

  // Map workLevelSetting to segmentLevel format expected by backend
  const getSegmentLevel = (workLevelSetting?: string): string | undefined => {
    if (!workLevelSetting) return undefined;
    const segmentLevelMap: Record<string, string> = {
      TOWER_BLOCK: "Tower",
      VILLA: "Villa",
      FLOOR: "Floor",
      FLAT: "Flat",
    };
    return segmentLevelMap[workLevelSetting];
  };

  // Map API response (segmentLevel) to workLevelSetting format for form
  const getWorkLevelSettingFromSegmentLevel = (
    segmentLevel?: string,
  ): string | undefined => {
    if (!segmentLevel) return undefined;
    const workLevelSettingMap: Record<string, string> = {
      Tower: "TOWER_BLOCK",
      Villa: "VILLA",
      Floor: "FLOOR",
      Flat: "FLAT",
    };
    return workLevelSettingMap[segmentLevel];
  };

  const shouldShowHierarchy = useMemo(
    () => !!workLevelSetting && workLevelSetting !== "NONE",
    [workLevelSetting],
  );

  const hierarchyFieldLabel = useMemo(() => {
    if (!workLevelSetting) {
      return "Tower / Block";
    }
    return HIERARCHY_UNIT_TITLES[workLevelSetting] ?? "Tower / Block";
  }, [workLevelSetting]);

  const hierarchyFieldPlaceholder = useMemo(() => {
    switch (workLevelSetting) {
      case "VILLA":
        return "Select Villa";
      case "TOWER_BLOCK":
      case "FLOOR":
      case "FLAT":
        return "Select Tower / Block";
      default:
        return "Select Tower / Block / Villa";
    }
  }, [workLevelSetting]);

  // Auto-set isBillable to "Billable" when workType is "Quantity"
  useEffect(() => {
    if (workTypeWatch === "Quantity") {
      setValue("isBillable", "Billable", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [workTypeWatch, setValue]);

  useEffect(() => {
    if (selectedOwnerId && selectedTeam.includes(selectedOwnerId)) {
      const updatedTeam = selectedTeam.filter(
        (id: number) => id !== selectedOwnerId,
      );
      setValue("projectUserId", updatedTeam);
    }
  }, [selectedOwnerId, selectedTeam, setValue]);

  // If the owner is changed to someone already chosen as an approver, drop
  // them from the saved approval hierarchy — a user can't approve their own
  // task. Handles the case where approvals were set before the owner.
  useEffect(() => {
    if (selectedOwnerId == null) return;
    setApprovals((prev: any) => {
      if (!prev.data?.length) return prev;
      const filtered = prev.data.filter(
        (row: any) => Number(row.userId) !== Number(selectedOwnerId),
      );
      return filtered.length === prev.data.length
        ? prev
        : { ...prev, data: filtered };
    });
  }, [selectedOwnerId]);

  const teamOptions = useMemo(() => {
    return projectUsers
      .filter((user) => Number(user.value) !== Number(selectedOwnerId))
      .map((i: any) => {
        return { ...i, label: i.label, value: i.projectUserId };
      });
  }, [projectUsers, selectedOwnerId]);

  // Mutual exclusion: hide users already chosen in Associated Team from the
  // Owner dropdown (Owner uses userId as value, Team uses projectUserId).
  // Normalize with Number() because form values arrive as strings.
  const ownerOptions = useMemo(() => {
    return projectUsers.filter(
      (user: any) =>
        !selectedTeam.some(
          (id: any) => Number(id) === Number(user.projectUserId),
        ),
    );
  }, [projectUsers, selectedTeam]);

  const [isLoadingProjectUsers, setIsLoadingProjectUsers] = useState(false);
  const [isLoadingProjectTags, setIsLoadingProjectTags] = useState(false);
  const [isLoadingPriority, setIsLoadingPriority] = useState(false);
  const [isLoadingWorkCategories, setIsLoadingWorkCategories] = useState(false);
  const [isLoadingActivityResources, setIsLoadingActivityResources] =
    useState(false);
  const [isLoadingActivityBoqs, setIsLoadingActivityBoqs] = useState(false);
  const [isLoadingTaskStatus, setIsLoadingTaskStatus] = useState(false);
  const [workUomsList, setWorkUomsList] = useState<SelectOption[]>([]);

  // Tools & Machinery state
  const [toolsAndMachineryTypes, setToolsAndMachineryTypes] = useState<
    SelectOption[]
  >([]);
  const [toolsAndMachineryList, setToolsAndMachineryList] = useState<
    SelectOption[]
  >([]);
  const [materialOptions, setMaterialOptions] = useState<SelectOption[]>([]);
  // Per-tool split-materials drawer (scoped to one tool so the page stays light
  // even with many tools, each holding many materials).
  const [materialsDrawer, setMaterialsDrawer] = useState<{
    open: boolean;
    toolIndex: number | null;
  }>({ open: false, toolIndex: null });
  const [selectedToolType, setSelectedToolType] = useState<number | null>(null);
  const [selectedTool, setSelectedTool] = useState<number | null>(null);
  // Index of the tool row being edited (null = adding a new tool).
  const [editingToolIndex, setEditingToolIndex] = useState<number | null>(null);
  const [taskTools, setTaskTools] = useState<
    Array<{
      toolId: number;
      count?: number;
      serialNumber?: string;
      toolName?: string;
      typeName?: string;
      rate?: number;
      quantity?: number;
      workUomId?: number;
      totalAmount?: number;
      rateAnalysisQty?: number;
      rateAnalysisUomId?: number;
      rateAnalysisRate?: number;
      toolMaterials?: ToolMaterialItem[];
    }>
  >([]);

  // Hyperlinks state
  const [hyperlinks, setHyperlinks] = useState<
    Array<{
      name: string;
      link: string;
    }>
  >([]);

  // Site Hierarchy related state
  const [towerOptions, setTowerOptions] = useState<SelectOption[]>([]);
  const [floorOptions, setFloorOptions] = useState<SelectOption[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOption[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOption[]>([]);
  const [projectSiteId, setProjectSiteId] = useState<number | null>(null);

  // Budget line related state
  const [budgetLines, setBudgetLines] = useState<SelectOption[]>([]);
  const [activityBudgetInfo, setActivityBudgetInfo] =
    useState<ActivityBudgetInfo | null>(null);
  const [activityContractorInfo, setActivityContractorInfo] =
    useState<ActivityContractorInfo | null>(null);
  const [activityDateRange, setActivityDateRange] = useState<{
    start?: string;
    end?: string;
  }>({});
  const [hasProjectBudget, setHasProjectBudget] = useState(false);
  const [selectedBudgetValue, setSelectedBudgetValue] = useState<number | null>(
    null,
  );
  const [selectedBudgetLine, setSelectedBudgetLine] =
    useState<BudgetLine | null>(null);
  const [isLoadingBudgetLines, setIsLoadingBudgetLines] = useState(false);

  // Sync form values with state for tools
  useEffect(() => {
    if (toolTypeWatch !== selectedToolType) {
      setSelectedToolType(toolTypeWatch || null);
      if (toolTypeWatch !== selectedToolType) {
        setSelectedTool(null);
        setValue("toolMachinery", null);
      }
    }
  }, [toolTypeWatch, selectedToolType, setValue]);

  useEffect(() => {
    if (toolMachineryWatch !== selectedTool) {
      setSelectedTool(toolMachineryWatch || null);
    }
  }, [toolMachineryWatch, selectedTool]);

  // Auto-populate Budget Allocation Amount = Work Quantity * Rate
  const workQuantityWatch = watch("workQuantity");
  const rateWatch = watch("rate");
  const outputQuantityWatch = watch("outputQuantity");
  const workUomIdWatch = watch("workUomId");
  const outputBasisUomIdWatch = watch("outputBasisUomId");
  // The rate the user types is GST-inclusive. Base / GST / cess / Basic Rate are
  // derived here for preview only — the server recomputes and stores them.
  const gstPercentWatch = watch("gstPercent");
  const cessPercentWatch = watch("cessPercent");
  const priceBreakdown = useMemo(
    () =>
      computeTaskPriceBreakdown(
        rateWatch,
        gstPercentWatch ?? DEFAULT_GST_PERCENT,
        cessPercentWatch ?? DEFAULT_CESS_PERCENT,
      ),
    [rateWatch, gstPercentWatch, cessPercentWatch],
  );
  // Scale factor used to pre-fill overall quantities from rate-analysis (unit)
  // quantities: e.g. task qty 1000 / output basis 100 = factor 10.
  const outputFactor = useMemo(() => {
    const wq = Number(workQuantityWatch);
    const oq = Number(outputQuantityWatch);
    if (!wq || !oq) return 0;
    return wq / oq;
  }, [workQuantityWatch, outputQuantityWatch]);

  // UOM names for the rate-analysis / overall section labels.
  const workUomName = useMemo(
    () =>
      workUomsList.find((u: SelectOption) => u.value === workUomIdWatch)
        ?.label || "",
    [workUomsList, workUomIdWatch],
  );
  const outputBasisUomName = useMemo(
    () =>
      workUomsList.find((u: SelectOption) => u.value === outputBasisUomIdWatch)
        ?.label || "",
    [workUomsList, outputBasisUomIdWatch],
  );
  // e.g. "Rate Analysis per 100 CUM per Day" (or "...per 100 CUM" with no basis).
  const rateAnalysisLabel = useMemo(() => {
    if (!outputQuantityWatch) return "Rate Analysis";
    let label = `Rate Analysis per ${outputQuantityWatch}`;
    if (workUomName) label += ` ${workUomName}`;
    if (outputBasisUomName) label += ` per ${outputBasisUomName}`;
    return label;
  }, [outputQuantityWatch, workUomName, outputBasisUomName]);
  // e.g. "Overall (for 1000 Cubic Meter)".
  const overallLabel = useMemo(() => {
    if (workQuantityWatch && workUomName)
      return `Overall (for ${workQuantityWatch} ${workUomName})`;
    if (workQuantityWatch) return `Overall (for ${workQuantityWatch})`;
    if (workUomName) return `Overall (${workUomName})`;
    return "Overall";
  }, [workQuantityWatch, workUomName]);
  // Rate-analysis (unit-basis) capture only applies to Quantity work type once
  // an output/productivity qty is given; otherwise only overall is captured.
  const showRateAnalysis = useMemo(
    () =>
      workTypeWatch === "Quantity" &&
      !!outputQuantityWatch &&
      Number(outputQuantityWatch) > 0,
    [workTypeWatch, outputQuantityWatch],
  );
  useEffect(() => {
    if (isSubtask) return;
    const qty = parseFloat(workQuantityWatch as any);
    // Budget is costed on the Basic Rate — GST is a pass-through and labour cess is
    // withheld by the client, so neither is money available to spend on the task.
    const rate = priceBreakdown.uploadRate;
    if (!isNaN(qty) && qty > 0 && rate > 0) {
      const amount = +(qty * rate).toFixed(2);
      setValue("allocatedBudgetAmount", amount, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [workQuantityWatch, priceBreakdown.uploadRate, isSubtask, setValue]);

  // Auto-populate budget line if activity has budget info
  useEffect(() => {
    if (activityBudgetInfo?.activityBudgetLineId && budgetLines.length > 0) {
      setValue("budgetLineId", activityBudgetInfo.activityBudgetLineId);
      setSelectedBudgetValue(activityBudgetInfo.budgetValue || null);
      // Find and set the selected budget line from budget lines
      const selectedBudget = budgetLines.find(
        (budget) => budget.value === activityBudgetInfo.activityBudgetLineId,
      );
      if (selectedBudget?.extra) {
        setSelectedBudgetLine({
          lineId: selectedBudget.value as number,
          headerId: 0,
          budgetId: selectedBudget.extra.budgetId,
          budgetLineName: selectedBudget.extra.budgetLineName,
          budgetValue: selectedBudget.extra.budgetValue,
          allocatedLineValue: selectedBudget.extra.allocatedLineValue,
        });
      }
    }
  }, [activityBudgetInfo, budgetLines, setValue]);

  // Handle budget line selection to update budget value
  useEffect(() => {
    if (budgetLineIdWatch && budgetLines.length > 0) {
      const selectedBudget = budgetLines.find(
        (budget) => budget.value === budgetLineIdWatch,
      );
      if (selectedBudget?.extra) {
        setSelectedBudgetValue(selectedBudget.extra.budgetValue);
        setSelectedBudgetLine({
          lineId: selectedBudget.value as number,
          headerId: 0, // Not needed for display
          budgetId: selectedBudget.extra.budgetId,
          budgetLineName: selectedBudget.extra.budgetLineName,
          budgetValue: selectedBudget.extra.budgetValue,
          allocatedLineValue: selectedBudget.extra.allocatedLineValue,
        });
      }
    } else {
      setSelectedBudgetValue(null);
      setSelectedBudgetLine(null);
    }
  }, [budgetLineIdWatch, budgetLines]);

  const fetchProjectUsers = async () => {
    if (isLoadingProjectUsers) return;
    setIsLoadingProjectUsers(true);
    try {
      const response = await projectsApis.fetchProjectUsers(
        projectResponse?.id,
      );
      const { data: usersList, status } = response || {};

      if (status && usersList?.length > 0) {
        const formatted = usersList.map((user: ProjectUser) => ({
          label: user.userName,
          value: Number(user.userId),
          projectUserId: user.projectUserId,
          roleName: user.roleName,
        }));
        setProjectUsers(formatted);
      } else {
        messageApi.error("No project users available");
        setProjectUsers([]);
      }
    } catch (error) {
      console.error("Error fetching project users:", error);
      messageApi.error("Failed to fetch project users");
      setProjectUsers([]);
    } finally {
      setIsLoadingProjectUsers(false);
    }
  };

  const fetchContractorList = useCallback(async (siteIds: string) => {
    try {
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: siteIds, // Pass all site IDs (comma-separated)
      });

      if (status && data) {
        const _contractorOptions = data.map((item: any) => ({
          label: item?.companyName,
          value: item?.contractorID?.toString(),
        }));

        setContractorOptions(_contractorOptions);
      } else {
        setContractorOptions([]);
      }
    } catch (error) {
      setContractorOptions([]);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const options = currentUser.siteAccessibility.map((item: any) => ({
          label: item.siteName,
          value: item.siteID,
        }));
        setSiteOptions(options);

        const allIds = siteAccessibility
          .map((item: any) => item.siteID)
          .join(",");
        setAllSiteIds(allIds);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (allSiteIds) {
      fetchContractorList(allSiteIds);
    }
  }, [allSiteIds, fetchContractorList]);

  const fetchProjectTags = async () => {
    if (isLoadingProjectTags) return;
    setIsLoadingProjectTags(true);
    try {
      const response = await tasksApis.fetchProjectTags(projectResponse?.id);
      const { data, status }: { data: ProjectTag[]; status: boolean } =
        response || {};
      if (status) {
        setProjectTagsList(
          data?.map((item) => ({
            label: item.tagName,
            value: item.tagId,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching project tags:", error);
      messageApi.error("Failed to fetch project tags");
    } finally {
      setIsLoadingProjectTags(false);
    }
  };

  const fetchPriority = async () => {
    if (isLoadingPriority) return;
    setIsLoadingPriority(true);
    try {
      const response = await tasksApis.priorityList();
      const { data, status }: { data: Priority[]; status: boolean } =
        response || {};
      if (status) {
        setPriorityList(
          data?.map((item) => ({
            label: item.priorityName,
            value: item.priorityId,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching priority list:", error);
      messageApi.error("Failed to fetch priority list");
    } finally {
      setIsLoadingPriority(false);
    }
  };

  const fetchWorkCatgoriesList = async () => {
    if (isLoadingWorkCategories) return;
    setIsLoadingWorkCategories(true);
    try {
      const response = await tasksApis.fetchWorkCatgoriesList();
      const { data, status }: { data: WorkCategory[]; status: boolean } =
        response || {};
      if (status) {
        setWorkCatgoriesList(
          data?.map((item) => ({
            label: item.workCategoryName,
            value: item.workCategoryId,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching work categories:", error);
      messageApi.error("Failed to fetch work categories");
    } finally {
      setIsLoadingWorkCategories(false);
    }
  };

  const fetchBudgetLines = async () => {
    if (isLoadingBudgetLines) return;
    setIsLoadingBudgetLines(true);
    try {
      const response = await projectsApis.fetchBudgetLines(projectId, {
        budgetFor: "Service",
      });
      const { data, status }: { data: BudgetLine[]; status: boolean } =
        response || {};
      if (status && data?.length > 0) {
        setHasProjectBudget(true);
        setBudgetLines(
          data?.map((item) => ({
            label: item.budgetLineName,
            value: item.lineId,
            extra: {
              budgetValue: item.budgetValue,
              allocatedLineValue: item.allocatedLineValue,
              budgetId: item.budgetId,
              budgetLineName: item.budgetLineName,
            },
          })),
        );
      } else {
        // Even if no budget lines, keep hasProjectBudget true since activity budget line is mandatory
        setHasProjectBudget(true);
        setBudgetLines([]);
      }
    } catch (error) {
      console.error("Error fetching budget lines:", error);
      setHasProjectBudget(false);
      setBudgetLines([]);
    } finally {
      setIsLoadingBudgetLines(false);
    }
  };

  const fetchActivityBudgetInfo = async () => {
    try {
      // Fetch activity details to check if it has a budget line
      const response = await activityApis.fetchActivityById(
        id,
        derivedProjectStatus
      );
      const { data, status } = response || {};
      if (status && data) {
        // Capture activity date range so the task date pickers can clamp to it
        if (data.activityStartDate || data.activityEndDate) {
          setActivityDateRange({
            start: data.activityStartDate,
            end: data.activityEndDate,
          });
        }
        if (data.activityBudgetLineId) {
          const budgetInfo: ActivityBudgetInfo = {
            activityBudgetLineId: data.activityBudgetLineId,
            budgetLineName: data.budgetLineName,
            budgetValue: data.budgetValue,
            allocatedAmount: data.allocatedAmount,
          };
          setActivityBudgetInfo(budgetInfo);

          // Auto-populate the form if activity has budget line
          setValue("budgetLineId", data.activityBudgetLineId);
          setSelectedBudgetValue(data.budgetValue);
        }

        // Auto-populate and lock the contractor when the parent activity
        // already has one — same inheritance pattern as the budget line.
        const inheritedContractorId = data.contractorId ?? data.activityContractorId;
        if (inheritedContractorId) {
          setActivityContractorInfo({
            activityContractorId: Number(inheritedContractorId),
            contractorName: data.contractorName,
          });
          setValue("contractorId", Number(inheritedContractorId));
        }
      }
    } catch (error) {
      console.error("Error fetching activity budget info:", error);
      setActivityBudgetInfo(null);
      setActivityContractorInfo(null);
    }
  };

  const fetchActivityResourcesList = async () => {
    if (isLoadingActivityResources) return;
    setIsLoadingActivityResources(true);
    try {
      const response = await tasksApis.fetchActivityResources(id as string);
      const { data, status }: { data: any[]; status: boolean } = response || {};
      if (status) {
        // Map laborCategoryName to laborCategory
        const formattedData = data?.map((resource: any) => ({
          ...resource,
          laborCategory: resource.laborCategoryName,
        }));
        setActivityResourcesList(formattedData || []);
      }
    } catch (error) {
      console.error("Error fetching activity resources:", error);
      messageApi.error("Failed to fetch activity resources");
    } finally {
      setIsLoadingActivityResources(false);
    }
  };
  const updateSubTask = async (data: any) => {
    try {
      setDisableSubmit(true);
      const {
        activityResourceIds,
        projectUserId,
        taskPlannedStartDate,
        taskPlannedEndDate,
        boqLineIds,
        taskOwnerId,
        taskName,
        taskDescription,
        priorityId,
        isBillable,
      } = data;

      const { dirtyFields } = methods.formState; // Get dirtyFields from useForm

      // Get current projectUserId value from form (may be more up-to-date than data param)
      const currentProjectUserId =
        watch("projectUserId") || projectUserId || [];

      // === Process Task Users ===
      // Note: projectUserId from form contains projectUserId values (not userId)
      // because teamOptions uses projectUserId as the value
      const selectedTaskUsers =
        currentProjectUserId
          ?.map((id: number) => {
            // Find user by projectUserId since that's what the form field contains
            const projectUser = projectUsers.find(
              (pu: ProjectUserOption) =>
                Number(pu.projectUserId) === Number(id),
            );
            if (!projectUser) return null;
            return {
              projectUserId: Number(projectUser.projectUserId),
              subTaskUserType: "Resource Head",
            };
          })
          .filter(Boolean) || [];

      // Filter out Owner from existing users since Associated Team doesn't include Owner
      const existingUsers = (selectedSubtaskDetails?.subTaskUsers || []).filter(
        (user: any) => user.subTaskUserType !== "Owner",
      );

      const newUsers = selectedTaskUsers.filter(
        (user: any) =>
          !existingUsers.some(
            (eu: any) => eu.projectUserId === user.projectUserId,
          ),
      );

      // Fix: When selectedTaskUsers is empty, all existing users should be deleted
      const deletedUsers =
        selectedTaskUsers.length === 0
          ? existingUsers
              .filter((eu: any) => eu.projectUserId) // filter out undefined/null
              .map((eu: any) => ({
                subTaskUserId: eu.subTaskUserId || eu.projectUserId,
              }))
          : existingUsers
              .filter(
                (eu: any) =>
                  !selectedTaskUsers.some(
                    (su: any) => su.projectUserId === eu.projectUserId,
                  ),
              )
              .filter((eu: any) => eu.projectUserId) // filter out undefined/null
              .map((eu: any) => ({
                subTaskUserId: eu.subTaskUserId || eu.projectUserId,
              }));

      // === Process Tags ===
      const existingTags = selectedSubtaskDetails?.subTaskTags || [];
      const newTags = tags
        .filter(
          (tag: any) =>
            typeof tag.value === "string" ||
            !existingTags.some((et: any) => et.tagId === tag.value),
        )
        .map((tag: any) => ({
          tagName: tag.label || tag.value,
          ...(typeof tag.value !== "string" && { tagId: Number(tag.value) }),
        }));

      const deletedTags = existingTags
        .filter((et: any) => !tags.some((t: any) => t.value === et.tagId))
        .filter((et: any) => et.subTasksTagId) // ensure valid id exists
        .map((et: any) => ({ subTasksTagId: et.subTasksTagId }));

      // === Process Reminders ===
      const formattedReminders = remainders
        ? {
            ...remainders,
            reminderUsers: {
              newLines:
                remainders.reminderUsers?.map((user: any) => ({
                  userId: user.userId,
                })) || [],
              deletedLines:
                remainders.deletedReminderUsers?.map((user: any) => ({
                  reminderUserId: user.reminderUserId,
                })) || [],
            },
          }
        : undefined;

      // === Check for Changes ===
      const hasTagChanges =
        tags.length !== existingTags.length ||
        tags.some((tag, index) => tag.value !== existingTags[index]?.tagId);

      const hasReminderChanges =
        remainders &&
        JSON.stringify(remainders) !==
          JSON.stringify(selectedSubtaskDetails?.reminders);

      // Check if there are actual user changes (new or deleted users)
      // Also check if the arrays are different even if no new/deleted users
      const existingUserIds = existingUsers
        .map((eu: any) => eu.projectUserId)
        .sort((a: number, b: number) => a - b);
      const selectedUserIds = selectedTaskUsers
        .map((su: any) => su.projectUserId)
        .sort((a: number, b: number) => a - b);
      const hasUserChanges =
        newUsers.length > 0 ||
        deletedUsers.length > 0 ||
        JSON.stringify(existingUserIds) !== JSON.stringify(selectedUserIds);

      // Check if there are any dirty fields or changes in tags/reminders/users
      const hasChanges =
        Object.keys(dirtyFields).length > 0 ||
        hasTagChanges ||
        hasReminderChanges ||
        hasUserChanges;

      if (!hasChanges) {
        messageApi.open({
          type: "info",
          content: "No changes detected. Nothing to update.",
        });
        setDisableSubmit(false);
        return;
      }

      // === Construct Payload with Only Dirty Fields ===
      const payload: any = {
        taskId: selectedSubtaskDetails?.taskId,
        subTaskId: selectedSubtaskDetails.subTaskId,
        projectId,
        phaseId,
        activityId: id,
        approvalRequired: data.approvalRequired === "yes",
        autoApprovalRemarks:
          data.approvalRequired === "no" ? data.autoApprovalRemarks : undefined,
        stateApprovalHierarchy: approvals.data,
      };

      // Add only dirty fields to the payload
      if (dirtyFields.taskName) {
        payload.subTaskName = taskName;
      }
      if (dirtyFields.taskDescription) {
        payload.subTaskDescription = taskDescription;
      }
      if (dirtyFields.taskOwnerId) {
        payload.subTaskOwnerId = Number(taskOwnerId);
      }
      if (
        dirtyFields.taskPlannedStartDate &&
        dayjs(taskPlannedStartDate).isValid()
      ) {
        payload.subTaskPlannedStartDate =
          dayjs(taskPlannedStartDate).format("YYYY-MM-DD");
      }
      if (
        dirtyFields.taskPlannedEndDate &&
        dayjs(taskPlannedEndDate).isValid()
      ) {
        payload.subTaskPlannedEndDate =
          dayjs(taskPlannedEndDate).format("YYYY-MM-DD");
      }
      if (dirtyFields.priorityId) {
        payload.priorityId = Number(priorityId);
      }
      if (dirtyFields.isBillable) {
        payload.isBillable = isBillable === "Billable" ? true : false;
      }
      // Include subTaskUsers if there are actual changes (new or deleted users)
      // or if the field is marked as dirty
      if (hasUserChanges || dirtyFields.projectUserId) {
        payload.subTaskUsers = {
          newLines: newUsers,
          deletedLines: deletedUsers,
        };
      }
      if (hasTagChanges) {
        payload.subTaskTags = {
          newLines: newTags,
          deletedLines: deletedTags,
        };
      }
      if (hasReminderChanges) {
        payload.reminderFlag = !!formattedReminders;
        if (formattedReminders) {
          payload.reminders = {
            ...formattedReminders,
            specificTime: formattedReminders?.specificTime?.slice(0, 5), // "HH:MM"
            moduleType: "SubTask",
            daysOffset: formattedReminders.daysOffset || 0,
          };
        }
      }

      // === Send API Call ===
      const response: ApiResponse = await tasksApis.updateSubTaskbyId(payload);
      const { data: subTask, status, error, message } = response || {};

      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: subTask?.message,
          confirmButtonText: "Ok",
        }).then(() => {
          // Reset form and states to clear dirtyFields and prevent accumulation
          reset({
            taskName: selectedSubtaskDetails?.subTaskName || "",
            taskDescription: selectedSubtaskDetails?.subTaskDescription || "",
            taskOwnerId: selectedSubtaskDetails?.subTaskOwnerId
              ? Number(selectedSubtaskDetails?.subTaskOwnerId)
              : null,
            contractorId: selectedSubtaskDetails?.contractorId
              ? Number(selectedSubtaskDetails?.contractorId)
              : null,
            taskPlannedStartDate:
              selectedSubtaskDetails?.subTaskPlannedStartDate
                ? dayjs(
                    selectedSubtaskDetails?.subTaskPlannedStartDate,
                    "YYYY-MM-DD",
                  )
                : null,
            taskPlannedEndDate: selectedSubtaskDetails?.subTaskPlannedEndDate
              ? dayjs(
                  selectedSubtaskDetails?.subTaskPlannedEndDate,
                  "YYYY-MM-DD",
                )
              : null,
            priorityId: selectedSubtaskDetails?.priorityId
              ? Number(selectedSubtaskDetails?.priorityId)
              : null,
            isBillable:
              selectedSubtaskDetails?.isBillable !== undefined
                ? selectedSubtaskDetails.isBillable === true
                  ? "Billable"
                  : "Non Billable"
                : "",
            projectUserId:
              selectedSubtaskDetails?.subTaskUsers
                ?.filter((user: any) => user.subTaskUserType !== "Owner")
                ?.map((user: any) => Number(user.projectUserId)) || [],
            taskWeightage: null,
          });
          setTags(
            selectedSubtaskDetails?.subTaskTags?.map((tag: any) => ({
              label: tag.tagName,
              value: tag.tagId,
            })) || [],
          );
          setRemainders(selectedSubtaskDetails?.reminders || null);
          // Refresh subtask list immediately after update
          dispatch(
            updateTaskListDetailsModalSlice({ refreshSubTaskList: true }),
          );

          // Close the drawer to show updated data
          dispatch(
            updateTaskListDetailsModalSlice({
              showTaskCreationModal: false,
              isSubtask: false,
              isSubtaskUpdate: false,
              currentSubtaskId: null,
              selectedSubtaskDetails: null,
              subtaskOpenInEdit: false,
            }),
          );

          setIsEditMode(false);
          setApprovals({ open: false, data: [] });
          setDocuments({ open: false, data: [] });
        });
      } else {
        setErrorData({ error, message });
        setApprovals({ open: false, data: [] });
        setDocuments({ open: false, data: [] });
        setTimeout(() => {
          setErrorData(undefined);
        }, 10000);
      }
    } catch (error) {
      console.error("Error updating subtask:", error);
    } finally {
      setDisableSubmit(false);
    }
  };

  const fetchSubtaskDetails = async () => {
    if (TaskListInDetailModal.id) {
      try {
        const response: any = await tasksApis.fetchSubtaskDetailsById(
          TaskListInDetailModal.currentSubtaskId as number,
          derivedProjectStatus
        );
        const { data, status } = response || {};
        if (status) {
          const initialTags =
            data.subTaskTags?.map((tag: any) => ({
              label: tag.tagName,
              value: tag.tagId,
            })) || [];
          setRemainders(data.reminders);
          setTags(initialTags);
          reset({
            taskName: data?.subTaskName || "",
            taskDescription: data?.subTaskDescription || "",
            taskOwnerId: data?.subTaskOwnerId
              ? Number(data?.subTaskOwnerId)
              : null,
            contractorId: data?.contractorId
              ? Number(data?.contractorId)
              : null,
            taskPlannedStartDate: data?.subTaskPlannedStartDate
              ? dayjs(data?.subTaskPlannedStartDate, "YYYY-MM-DD")
              : null,
            taskPlannedEndDate: data?.subTaskPlannedEndDate
              ? dayjs(data?.subTaskPlannedEndDate, "YYYY-MM-DD")
              : null,
            priorityId: data?.priorityId ? Number(data?.priorityId) : null,
            isBillable:
              data?.isBillable !== undefined
                ? data.isBillable === true
                  ? "Billable"
                  : "Non Billable"
                : "",
            projectUserId:
              data?.subTaskUsers
                ?.filter((user: any) => user.subTaskUserType !== "Owner")
                ?.map((user: any) => Number(user.projectUserId)) || [],
            taskWeightage: null,
          });
          dispatch(
            updateTaskListDetailsModalSlice({
              selectedSubtaskDetails: data,
            }),
          );
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const fetchActivityBoqs = () => {
    if (isLoadingActivityBoqs) return;
    setIsLoadingActivityBoqs(true);
    tasksApis
      .fetchActivityBoqs(id as string)
      .then((response: any) => {
        // API returns { status, data, message } - extract data array
        const apiData = response?.data ?? response;
        const data = Array.isArray(apiData) ? apiData : response?.data;
        const status = response?.status ?? true;
        if (status && data) {
          const allBoqs =
            data?.map((item: ActivityBoq) => {
              // Map lines to include activityBoqLineId (use materialId as fallback when not present)
              const mappedLines = (item.lines || []).map((line: any) => ({
                ...line,
                activityBoqLineId:
                  line.activityBoqLineId ?? line.materialId ?? line.uomId,
              }));
              return {
                label: `${
                  mappedLines?.[0]?.materialName || ""
                } - ${mappedLines?.[0]?.categoryName || ""}`,
                value: item.activityBoqId,
                activityBoqId: item.activityBoqId,
                boqTargetNumber: item.boqTargetNumber,
                boqTitle: item.boqTitle,
                boqLineName: (item as any).boqLineName,
                lines: mappedLines,
                // Preserve site hierarchy fields from API response (at BOQ level)
                siteStructureUnitId: (item as any).siteStructureUnitId,
                siteStructureSubUnitId: (item as any).siteStructureSubUnitId,
                floorNo: (item as any).floorNo,
                locationId: (item as any).locationId,
              };
            }) || [];
          setAllActivityBoqList(allBoqs);
          // Filter based on site hierarchy if available
          filterBoqListBySiteHierarchy(allBoqs);
        }
      })
      .catch((error) => {
        console.error("Error fetching activity BOQs:", error);
        messageApi.error("Failed to fetch activity BOQs");
      })
      .finally(() => {
        setIsLoadingActivityBoqs(false);
      });
  };

  // Filter BOQ list based on site hierarchy selection
  const filterBoqListBySiteHierarchy = (boqList: ActivityBoqOption[]) => {
    const unitId = selectedUnitId;
    const floorNo = selectedFloorNo;
    const subUnitId = selectedSubUnitId;
    const locationId = selectedLocationId;
    const currentWorkLevelSetting = workLevelSetting;

    // If site hierarchy is not selected or is NONE, show all BOQs
    if (
      !currentWorkLevelSetting ||
      currentWorkLevelSetting === "NONE" ||
      !unitId
    ) {
      setActivityBoqList(boqList);
      return;
    }

    // Filter BOQs based on site hierarchy (site hierarchy fields are at BOQ level, not line level)
    const filteredBoqs = boqList.filter((boq: ActivityBoqOption) => {
      // Check if BOQ matches the selected site hierarchy
      // BOQs without site hierarchy data are included (available for all - e.g. API may not return these fields)
      // Only filter out BOQs that HAVE site hierarchy data but don't match the selection

      // Always check unitId (Tower/Block or Villa) when BOQ has this field
      if (unitId) {
        // Convert both to numbers for comparison (handles string/number mismatch)
        const boqUnitId = boq.siteStructureUnitId
          ? Number(boq.siteStructureUnitId)
          : null;
        const selectedUnitIdNum = Number(unitId);

        // If BOQ doesn't have site hierarchy data, include it (available for all)
        if (boqUnitId === null || boqUnitId === undefined || isNaN(boqUnitId)) {
          return true; // BOQ has no unitId - available for all, include it
        }
        if (boqUnitId !== selectedUnitIdNum) {
          return false; // UnitId doesn't match, exclude it
        }
      }

      // For FLOOR and FLAT, check floorNo - only filter when BOQ has this field
      if (
        (currentWorkLevelSetting === "FLOOR" ||
          currentWorkLevelSetting === "FLAT") &&
        floorNo
      ) {
        const boqFloorNo = boq.floorNo ? String(boq.floorNo).trim() : null;
        const selectedFloorNoStr = String(floorNo).trim();

        // If BOQ doesn't have floorNo, include it (available for all)
        if (!boqFloorNo || boqFloorNo === "") {
          return true; // BOQ has no floorNo - available for all, include it
        }
        if (boqFloorNo !== selectedFloorNoStr) {
          return false; // FloorNo doesn't match, exclude it
        }
      }

      // For FLAT, check subUnitId (Flat) - only filter when BOQ has this field
      if (currentWorkLevelSetting === "FLAT" && subUnitId) {
        const boqSubUnitId = boq.siteStructureSubUnitId
          ? Number(boq.siteStructureSubUnitId)
          : null;
        const selectedSubUnitIdNum = Number(subUnitId);

        // If BOQ doesn't have subUnitId, include it (available for all)
        if (
          boqSubUnitId === null ||
          boqSubUnitId === undefined ||
          isNaN(boqSubUnitId)
        ) {
          return true; // BOQ has no subUnitId - available for all, include it
        }
        if (boqSubUnitId !== selectedSubUnitIdNum) {
          return false; // SubUnitId doesn't match, exclude it
        }
      }

      // Check locationId if provided - filter BOQs by location
      // BOQs without locationId are included (available for all locations)
      if (locationId) {
        const boqLocationId = boq.locationId ? Number(boq.locationId) : null;
        const selectedLocationIdNum = Number(locationId);

        // Include BOQs that don't have locationId (available for any location)
        if (
          boqLocationId === null ||
          boqLocationId === undefined ||
          isNaN(boqLocationId)
        ) {
          return true; // BOQ has no location - available for all locations
        }
        // Exclude only BOQs that have a different locationId
        if (boqLocationId !== selectedLocationIdNum) {
          return false; // LocationId doesn't match
        }
      }

      return true; // All checks passed, include this BOQ with all its lines
    });

    setActivityBoqList(filteredBoqs);
  };

  // Check if site hierarchy is complete based on workLevelSetting
  const isSiteHierarchyComplete = () => {
    // Infra projects have no site hierarchy on tasks — never block on it
    if (isInfraStructure) {
      return true;
    }

    // NONE (Site level) - Location is the only hierarchy field; complete when location is selected
    if (workLevelSetting === "NONE") {
      return !!selectedLocationId;
    }

    if (!workLevelSetting) {
      return false;
    }

    // TOWER_BLOCK and VILLA require only unitId
    if (workLevelSetting === "TOWER_BLOCK" || workLevelSetting === "VILLA") {
      return !!selectedUnitId;
    }

    // FLOOR requires unitId and floorNo
    if (workLevelSetting === "FLOOR") {
      return !!selectedUnitId && !!selectedFloorNo;
    }

    // FLAT requires unitId, floorNo, and subUnitId
    if (workLevelSetting === "FLAT") {
      return !!selectedUnitId && !!selectedFloorNo && !!selectedSubUnitId;
    }

    return false;
  };

  // Track structural hierarchy to avoid clearing BOQ selection when only location changes
  const prevStructuralHierarchyRef = useRef({
    workLevelSetting,
    selectedUnitId,
    selectedFloorNo,
    selectedSubUnitId,
  });

  // Filter BOQ lines when site hierarchy changes
  useEffect(() => {
    if (allActivityBoqList.length > 0 && !isSubtask) {
      filterBoqListBySiteHierarchy(allActivityBoqList);
      // Only clear BOQ selection when structural hierarchy changes (unit, floor, flat)
      // Do NOT clear when only location changes - preserves user's BOQ/material selection
      const structuralChanged =
        prevStructuralHierarchyRef.current.workLevelSetting !==
          workLevelSetting ||
        prevStructuralHierarchyRef.current.selectedUnitId !== selectedUnitId ||
        prevStructuralHierarchyRef.current.selectedFloorNo !==
          selectedFloorNo ||
        prevStructuralHierarchyRef.current.selectedSubUnitId !==
          selectedSubUnitId;

      prevStructuralHierarchyRef.current = {
        workLevelSetting,
        selectedUnitId,
        selectedFloorNo,
        selectedSubUnitId,
      };

      if (structuralChanged && boqLineIdsWatch && boqLineIdsWatch.length > 0) {
        setValue("boqLineIds", []);
      }
    }
  }, [
    workLevelSetting,
    selectedUnitId,
    selectedFloorNo,
    selectedSubUnitId,
    selectedLocationId,
    allActivityBoqList,
    isSubtask,
  ]);

  const closeCreateTaskDrawer = () => {
    // Reset the initial data loaded flag when closing the modal
    setInitialDataLoaded(false);
    reset({
      taskName: "",
      taskDescription: "",
      taskOwnerId: null,
      taskPlannedStartDate: null,
      taskPlannedEndDate: null,
      priorityId: null,
      isBillable: "",
      projectUserId: [],
      taskWeightage: null,
      budgetLineId: null,
      allocatedBudgetAmount: undefined,
      rate: undefined,
      workLevelSetting: undefined,
      siteStructureUnitId: null,
      floorNo: null,
      siteStructureSubUnitId: null,
      locationId: null,
    });
    setTags([]);
    setRemainders(null);
    setActivityBudgetInfo(null);
    setActivityContractorInfo(null);
    setDocuments({ open: false, data: [] });
    setDocumentFiles([]);
    setApprovals({ open: false, data: [] });
    setSelectedBudgetValue(null);
    setSelectedBudgetLine(null);
    setSelectedResourceKeys([]);
    setEditingResourceKeys([]);
    setTaskTools([]);
    setSelectedToolType(null);
    setSelectedTool(null);
    setHyperlinks([]);
    setTowerOptions([]);
    setFloorOptions([]);
    setFlatOptions([]);
    setLocationOptions([]);
    setAllActivityBoqList([]);
    form.resetFields();
    resourceForm.resetFields();
    if (isPageMode) {
      // The page owns navigation; never touch the drawer slices from here.
      return;
    }
    dispatch(
      updateTaskListDetailsModalSlice({
        isSubtask: false,
        showTaskCreationModal: false,
        open: isSubtask ? true : false,
        isSubtaskUpdate: false,
        currentSubtaskId: null,
        selectedSubtaskDetails: null,
        subtaskOpenInEdit: false,
      }),
    );
  };

  const fetchWorkUomsList = async () => {
    try {
      const response = await tasksApis.workUom({});
      const { data, status } = response || {};
      if (status) {
        setWorkUomsList(
          data?.map((item: any) => ({
            label: item.uomName,
            value: item.workUomId,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Error fetching work UOMs:", error);
      messageApi.error("Failed to fetch work UOMs");
    }
  };

  const fetchToolsAndMachineryTypes = async () => {
    try {
      const response = await tasksApis.fetchToolsAndMachineryTypes();
      const { data, status } = response || {};
      if (status) {
        setToolsAndMachineryTypes(
          data?.map((item: any) => ({
            label: item.typeName,
            value: item.toolTypeId,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Error fetching tools & machinery types:", error);
      messageApi.error("Failed to fetch tools & machinery types");
    }
  };

  const fetchToolsAndMachinery = async () => {
    try {
      const response = await tasksApis.fetchToolsAndMachinery();
      const { data, status } = response || {};
      if (status) {
        setToolsAndMachineryList(
          data?.map((item: any) => ({
            label: item.toolName,
            value: item.id,
            extra: {
              typeName: item.typeName,
              typeId: item.typeId,
            },
          })) || [],
        );
      }
    } catch (error) {
      console.error("Error fetching tools & machinery:", error);
      messageApi.error("Failed to fetch tools & machinery");
    }
  };

  const fetchMachineryMaterials = async () => {
    try {
      const response = await tasksApis.fetchMachineryMaterialDropDown();
      const { data, status } = response || {};
      if (status) {
        setMaterialOptions(
          (data || []).map((item: any) => ({
            label: item.materialName ?? item.name ?? item.label,
            value: item.id ?? item.materialId ?? item.value,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching machinery materials:", error);
    }
  };

  useEffect(() => {
    fetchWorkUomsList();
  }, []);

  // Site Hierarchy API functions
  const loadTowerOptions = useCallback(
    async (siteIdParam?: string | number) => {
      if (!siteIdParam) {
        setTowerOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchUnitsBySiteId({
          siteId: siteIdParam,
        });
        if (response?.status && Array.isArray(response.data)) {
          setTowerOptions(
            response.data.map((unit: any) => ({
              label: unit.unitName,
              value: unit.id?.toString(),
            })),
          );
        } else {
          setTowerOptions([]);
        }
      } catch (error) {
        console.error("Error fetching tower options:", error);
        setTowerOptions([]);
      }
    },
    [],
  );

  const loadFloorOptions = useCallback(
    async (siteIdParam?: string | number, unitId?: string | number) => {
      if (!siteIdParam || !unitId) {
        setFloorOptions([]);
        return;
      }
      try {
        const response =
          await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
            siteId: siteIdParam,
            unitId,
          });
        if (response?.status && Array.isArray(response.data)) {
          setFloorOptions(
            response.data.map((floor: any) => ({
              label: floor.floorNo,
              value: floor.floorNo?.toString(),
            })),
          );
        } else {
          setFloorOptions([]);
        }
      } catch (error) {
        console.error("Error fetching floor options:", error);
        setFloorOptions([]);
      }
    },
    [],
  );

  const loadFlatOptions = useCallback(
    async (
      siteIdParam?: string | number,
      unitId?: string | number,
      floorNoValue?: string | null,
    ) => {
      if (!siteIdParam || !unitId) {
        setFlatOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchSubUnitsByUnitId({
          siteId: siteIdParam,
          unitId,
          floorNo: floorNoValue ?? undefined,
        });
        if (response?.status && Array.isArray(response.data)) {
          setFlatOptions(
            response.data.map((flat: any) => ({
              label: flat.subUnitName || flat.unitName,
              value: flat.id?.toString(),
            })),
          );
        } else {
          setFlatOptions([]);
        }
      } catch (error) {
        console.error("Error fetching flat options:", error);
        setFlatOptions([]);
      }
    },
    [],
  );

  const loadLocationOptions = useCallback(
    async (siteIdParam?: string | number) => {
      if (!siteIdParam) {
        setLocationOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
          siteId: siteIdParam,
        });
        if (response?.status && Array.isArray(response.data)) {
          setLocationOptions(
            response.data.map((loc: any) => ({
              label: loc.locationName,
              value: loc.id?.toString(),
            })),
          );
        } else {
          setLocationOptions([]);
        }
      } catch (error) {
        console.error("Error fetching location options:", error);
        setLocationOptions([]);
      }
    },
    [],
  );

  // Load location options when projectSiteId changes
  useEffect(() => {
    if (projectSiteId) {
      loadLocationOptions(Number(projectSiteId));
    } else {
      setLocationOptions([]);
    }
  }, [projectSiteId, loadLocationOptions]);

  // Load tower options when hierarchy is enabled and site is selected
  useEffect(() => {
    if (!shouldShowHierarchy || !projectSiteId) {
      setTowerOptions([]);
      setFloorOptions([]);
      setFlatOptions([]);
      setValue("siteStructureUnitId", null);
      setValue("floorNo", null);
      setValue("siteStructureSubUnitId", null);
      return;
    }
    loadTowerOptions(Number(projectSiteId));
  }, [shouldShowHierarchy, projectSiteId, loadTowerOptions, setValue]);

  // Load floor options when unit is selected and hierarchy is FLOOR or FLAT
  useEffect(() => {
    if (
      !shouldShowHierarchy ||
      !projectSiteId ||
      !selectedUnitId ||
      (workLevelSetting !== "FLOOR" && workLevelSetting !== "FLAT")
    ) {
      setFloorOptions([]);
      if (watch("floorNo")) {
        setValue("floorNo", null);
      }
      return;
    }
    loadFloorOptions(Number(projectSiteId), selectedUnitId);
  }, [
    shouldShowHierarchy,
    projectSiteId,
    selectedUnitId,
    workLevelSetting,
    loadFloorOptions,
    setValue,
    watch,
  ]);

  // Load flat options when unit and floor are selected and hierarchy is FLAT
  useEffect(() => {
    if (
      !shouldShowHierarchy ||
      !projectSiteId ||
      workLevelSetting !== "FLAT" ||
      !selectedUnitId
    ) {
      setFlatOptions([]);
      if (watch("siteStructureSubUnitId")) {
        setValue("siteStructureSubUnitId", null);
      }
      return;
    }
    loadFlatOptions(
      Number(projectSiteId),
      selectedUnitId,
      selectedFloorNo ?? null,
    );
  }, [
    shouldShowHierarchy,
    projectSiteId,
    workLevelSetting,
    selectedUnitId,
    selectedFloorNo,
    loadFlatOptions,
    setValue,
    watch,
  ]);

  // Fetch project siteId when projectId is available
  useEffect(() => {
    const fetchProjectSiteId = async () => {
      if (projectId) {
        try {
          const response: ApiResponse = await projectsApis.getProjectById(
            projectId.toString(),
            undefined,
          );
          if (response?.status && response?.data?.siteId) {
            setProjectSiteId(response.data.siteId);
            // Set siteId in form for submission
            setValue("siteId", response.data.siteId);
          }
        } catch (error) {
          console.error("Error fetching project siteId:", error);
        }
      }
    };
    fetchProjectSiteId();
  }, [projectId, setValue]);

  // Fetch segment level by siteId and auto-populate Site Hierarchy
  useEffect(() => {
    const fetchSegmentLevel = async () => {
      if (projectSiteId && !isSubtask) {
        try {
          const response =
            await sitebreakdownMasterApis.fetchSegmentLevelBySiteId({
              siteId: projectSiteId,
            });
          setSiteProjectTypeName(response?.projectTypeName || null);
          // Infra projects don't capture site hierarchy on tasks — leave
          // workLevelSetting unset so the section stays hidden and no
          // hierarchy keys are sent in the payload.
          if (response?.projectTypeName?.toLowerCase() === "infra") {
            setValue("workLevelSetting", undefined, {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });
            return;
          }
          if (response?.status && response?.data) {
            const segmentLevel = response.data;
            const workLevelSetting =
              getWorkLevelSettingFromSegmentLevel(segmentLevel);
            if (workLevelSetting) {
              setValue("workLevelSetting", workLevelSetting, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
              });
            } else {
              // If no valid mapping, set to "NONE" (Default Site)
              setValue("workLevelSetting", "NONE", {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
              });
            }
          } else {
            // If no data from API, set to "NONE" (Default Site)
            setValue("workLevelSetting", "NONE", {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });
          }
        } catch (error) {
          console.error("Error fetching segment level:", error);
          // On error, set to "NONE" (Default Site)
          setValue("workLevelSetting", "NONE", {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
      }
    };
    fetchSegmentLevel();
  }, [projectSiteId, isSubtask, setValue]);

  // ---- Section total helpers ----
  // Sum of (allocated qty * rate) across all material BOQ lines in the table.
  const computeBoqSectionTotal = useCallback(() => {
    const values = form.getFieldsValue();
    return boqLinesDataSource.reduce((sum, record) => {
      const id = record.activityBoqId;
      const qty = Number(values[`allocatedQuantity_${id}`]) || 0;
      const rate = Number(values[`rate_${id}`]) || 0;
      return sum + qty * rate;
    }, 0);
  }, [form, boqLinesDataSource]);

  // Sum of (quantity * rate * resourceCount) across all selected resources.
  const computeResourceSectionTotal = useCallback(() => {
    const values = resourceForm.getFieldsValue();
    return selectedResourceKeys.reduce<number>((sum, key) => {
      const qty = Number(values[`quantity_${key}`]) || 0;
      const rate = Number(values[`rate_${key}`]) || 0;
      const count = Number(values[`resourceCount_${key}`]) || 0;
      return sum + qty * rate * count;
    }, 0);
  }, [resourceForm, selectedResourceKeys]);

  // Sum of (rate * quantity * count) across all added tools.
  const computeToolsSectionTotal = useCallback(() => {
    return taskTools.reduce((sum, tool) => {
      const rate = Number(tool.rate) || 0;
      const qty = Number(tool.quantity) || 0;
      const count = Number(tool.count) || 1;
      return sum + rate * qty * count;
    }, 0);
  }, [taskTools]);

  // Rate-analysis (unit-basis) section totals: sum of (RA qty * RA rate).
  const computeBoqRateAnalysisTotal = useCallback(() => {
    const values = form.getFieldsValue();
    return boqLinesDataSource.reduce((sum, record) => {
      const id = record.activityBoqId;
      const qty = Number(values[`rateAnalysisQty_${id}`]) || 0;
      const rate = Number(values[`rateAnalysisRate_${id}`]) || 0;
      return sum + qty * rate;
    }, 0);
  }, [form, boqLinesDataSource]);

  const computeResourceRateAnalysisTotal = useCallback(() => {
    const values = resourceForm.getFieldsValue();
    return selectedResourceKeys.reduce<number>((sum, key) => {
      const qty = Number(values[`rateAnalysisQty_${key}`]) || 0;
      const rate = Number(values[`rateAnalysisRate_${key}`]) || 0;
      return sum + qty * rate;
    }, 0);
  }, [resourceForm, selectedResourceKeys]);

  const computeToolsRateAnalysisTotal = useCallback(() => {
    return taskTools.reduce((sum, tool) => {
      const qty = Number(tool.rateAnalysisQty) || 0;
      const rate = Number(tool.rateAnalysisRate) || 0;
      return sum + qty * rate;
    }, 0);
  }, [taskTools]);

  // Grand totals across Materials + Resources + Tools.
  const computeOverallGrandTotal = useCallback(
    () =>
      computeBoqSectionTotal() +
      computeResourceSectionTotal() +
      computeToolsSectionTotal(),
    [
      computeBoqSectionTotal,
      computeResourceSectionTotal,
      computeToolsSectionTotal,
    ],
  );
  const computeRateAnalysisGrandTotal = useCallback(
    () =>
      computeBoqRateAnalysisTotal() +
      computeResourceRateAnalysisTotal() +
      computeToolsRateAnalysisTotal(),
    [
      computeBoqRateAnalysisTotal,
      computeResourceRateAnalysisTotal,
      computeToolsRateAnalysisTotal,
    ],
  );

  // Quantity-based work total (qty * rate), only when workType is "Quantity".
  const computeQuantityWorkTotal = useCallback(() => {
    if (workTypeWatch !== "Quantity") return 0;
    const qty = Number(watch("workQuantity")) || 0;
    // Basic Rate, not the GST-inclusive rate — see the budget auto-fill above.
    return qty * priceBreakdown.uploadRate;
  }, [workTypeWatch, watch, priceBreakdown.uploadRate]);

  const formatCurrency = (value: number) =>
    `₹${value.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Small reusable footer that shows a section's total amount.
  const SectionTotal: React.FC<{ label: string; amount: number }> = ({
    label,
    amount,
  }) => (
    <div
      className="d-flex justify-content-end align-items-center mt-2"
      style={{ gap: "8px" }}
    >
      <Text strong style={{ color: "#004293" }}>
        {label}:
      </Text>
      <Text strong style={{ color: "#004293", fontSize: "15px" }}>
        {formatCurrency(amount)}
      </Text>
    </div>
  );

  const submitNewTask = async (data: any) => {
    try {
      setDisableSubmit(true);

      // Check if approval is required but no approval data is set
      if (data.approvalRequired === "yes" && approvals.data.length === 0) {
        await Swal.fire({
          icon: "error",
          title: "Approval Required",
          text: 'Please set approval hierarchy by clicking "Set Approvals"',
          confirmButtonText: "OK",
        });
        setDisableSubmit(false);
        return;
      }

      // Check if documents is required but no documents data is set
      if (data.documentsRequired === "yes" && documents.data.length === 0) {
        await Swal.fire({
          icon: "error",
          title: "Documents Required",
          text: 'Please set documents by clicking "Set Documents"',
          confirmButtonText: "OK",
        });
        setDisableSubmit(false);
        return;
      }

      const {
        activityResourceIds,
        projectUserId,
        taskPlannedStartDate,
        taskPlannedEndDate,
        taskOwnerId,
        contractorId,
        taskName,
        taskDescription,
        priorityId,
        isBillable,
        workCategoryId,
        taskWeightage,
        workType,
        workQuantity,
        workUomId,
        budgetLineId,
        allocatedBudgetAmount,
        rate,
        gstPercent,
        cessPercent,
      } = data;

      if (
        !taskOwnerId ||
        (!isSubtask && !contractorId) ||
        (!isSubtask && !workCategoryId) ||
        (!isSubtask && !data.internalTaskNumber?.trim()) ||
        !taskName
      ) {
        messageApi.open({
          type: "error",
          content: "Please fill all required fields",
        });
        setDisableSubmit(false);
        return;
      }

      // Validate that the total allocated budget covers the sum of section line
      // totals: materials + resources + tools. (Excludes task qty * rate.)
      if (
        !isSubtask &&
        allocatedBudgetAmount !== undefined &&
        allocatedBudgetAmount !== null &&
        (allocatedBudgetAmount as any) !== ""
      ) {
        const boqTotal = computeBoqSectionTotal();
        const resourceTotal = computeResourceSectionTotal();
        const toolsTotal = computeToolsSectionTotal();
        const sectionsTotal = boqTotal + resourceTotal + toolsTotal;
        const allocated = Number(allocatedBudgetAmount) || 0;

        // Use a small epsilon to avoid floating-point rounding false positives.
        if (allocated + 0.001 < sectionsTotal) {
          messageApi.open({
            type: "error",
            content: `Total allocated budget (${formatCurrency(
              allocated,
            )}) must be greater than or equal to the sum of all section totals (${formatCurrency(
              sectionsTotal,
            )}).`,
          });
          setDisableSubmit(false);
          return;
        }

        // Independent check: allocated budget must also cover task qty * rate.
        const quantityWorkTotal = computeQuantityWorkTotal();
        if (quantityWorkTotal > 0 && allocated + 0.001 < quantityWorkTotal) {
          messageApi.open({
            type: "error",
            content: `Total allocated budget (${formatCurrency(
              allocated,
            )}) must be greater than or equal to the task value (Work Qty × Rate = ${formatCurrency(
              quantityWorkTotal,
            )}).`,
          });
          setDisableSubmit(false);
          return;
        }
      }

      // Check if checklist is required but not set
      const isChecklistRequired = watch("ChecklistRequired") === "yes";

      // Filter out incomplete checklist entries
      const validChecklistData = (Checklists.data || []).filter(
        (item: any) => item.status && item.checklist,
      );

      if (
        isChecklistRequired &&
        (!validChecklistData || validChecklistData.length === 0)
      ) {
        messageApi.open({
          type: "error",
          content: "Please set required checklists before saving",
        });
        setDisableSubmit(false);
        return;
      }

      if (!projectUsers.length) {
        console.warn("projectUsers is empty");
        messageApi.open({
          type: "error",
          content: "No project users available. Please try again.",
        });
        setDisableSubmit(false);
        return;
      }

      // Check if we have meaningful reminder data
      // Backend expects recurrence fields when reminders exist, so we need to include them
      const hasReminders = !!remainders && remainders.reminderType;
      if (hasReminders) {
        // Check if we have recurrence data but missing reminder data
        const hasRecurrenceData =
          remainders.recurrenceType &&
          remainders.recurrenceType !== "None" &&
          remainders.recurrenceInterval &&
          remainders.maxRecurrenceCount;

        const hasReminderData =
          remainders.reminderType && remainders.specificTime;

        // If we have recurrence data but no reminder data, show error
        if (hasRecurrenceData && !hasReminderData) {
          messageApi.open({
            type: "error",
            content: "Please set reminder details before setting recurrence",
          });
          setDisableSubmit(false);
          return;
        }

        // Ensure required fields are present
        if (!remainders.reminderType) {
          messageApi.open({
            type: "error",
            content: "Reminder type is required",
          });
          setDisableSubmit(false);
          return;
        }

        if (!remainders.specificTime) {
          messageApi.open({
            type: "error",
            content: "Reminder time is required",
          });
          setDisableSubmit(false);
          return;
        }

        // Only validate recurrence fields if recurrenceType is not "None"
        if (remainders.recurrenceType && remainders.recurrenceType !== "None") {
          if (
            !remainders.recurrenceInterval ||
            !remainders.maxRecurrenceCount
          ) {
            messageApi.open({
              type: "error",
              content:
                "Recurrence interval and count are required when setting recurrence",
            });
            setDisableSubmit(false);
            return;
          }
        }

        // Additional validation for offset-based reminders
        if (
          (["Before", "After"].includes(remainders.reminderType) ||
            (remainders.reminderType === "On" &&
              remainders.offsetFrom !== "Specific Date")) &&
          (!remainders.daysOffset || !remainders.offsetFrom)
        ) {
          messageApi.open({
            type: "error",
            content:
              "Offset-based reminders need 'daysOffset' and 'offsetFrom'",
          });
          setDisableSubmit(false);
          return;
        }

        // For "On" reminder type, specificDate is only required if offsetFrom is "Specific Date"
        if (
          remainders.reminderType === "On" &&
          remainders.offsetFrom === "Specific Date" &&
          !remainders.specificDate
        ) {
          messageApi.open({
            type: "error",
            content:
              "Reminder type 'On' requires a specific date when 'Specific Date' is selected as offset",
          });
          setDisableSubmit(false);
          return;
        }
      }

      let selectedBoqsForPayload: TaskBoq[] = [];
      if (!isSubtask) {
        const boqFormValues = form.getFieldsValue();
        selectedBoqsForPayload = Object.entries(boqFormValues)
          .filter(([key]) => key.startsWith("allocatedQuantity_"))
          .filter(
            ([, value]) =>
              value !== undefined && value !== null && value !== "",
          )
          .map(([key, value]) => {
            const [_, activityBoqId] = key.split("_");
            const rate = boqFormValues[`rate_${activityBoqId}`];
            const rateAnalysisQty =
              boqFormValues[`rateAnalysisQty_${activityBoqId}`];
            const rateAnalysisRate =
              boqFormValues[`rateAnalysisRate_${activityBoqId}`];
            return {
              activityBoqId: Number(activityBoqId),
              allotedQuantity: Number(value),
              ...(rate !== undefined && rate !== null && rate !== ""
                ? { rate: Number(rate) }
                : {}),
              ...(rateAnalysisQty !== undefined &&
              rateAnalysisQty !== null &&
              rateAnalysisQty !== ""
                ? { rateAnalysisQty: Number(rateAnalysisQty) }
                : {}),
              ...(rateAnalysisRate !== undefined &&
              rateAnalysisRate !== null &&
              rateAnalysisRate !== ""
                ? { rateAnalysisRate: Number(rateAnalysisRate) }
                : {}),
            };
          })
          .filter(
            (item) =>
              !Number.isNaN(item.allotedQuantity) && item.allotedQuantity >= 0,
          );
      }

      // Get resource counts from resourceForm - collect only selected resources (checkbox)
      const resourceFormValues = resourceForm.getFieldsValue();
      const selectedActivityResources: TaskResource[] = isSubtask
        ? []
        : selectedResourceKeys
            .map((key: React.Key) => {
              const activityResourceId = Number(key);
              const resourceCount = resourceFormValues[`resourceCount_${key}`];
              const rate = resourceFormValues[`rate_${key}`];
              const quantity = resourceFormValues[`quantity_${key}`];
              const workUomId = resourceFormValues[`workUomId_${key}`];
              const rateAnalysisQty =
                resourceFormValues[`rateAnalysisQty_${key}`];
              const rateAnalysisUomId =
                resourceFormValues[`rateAnalysisUomId_${key}`];
              const rateAnalysisRate =
                resourceFormValues[`rateAnalysisRate_${key}`];
              // Only include if resource count is provided and > 0
              if (resourceCount && Number(resourceCount) > 0) {
                return {
                  activityResourceId,
                  resourceCount: Number(resourceCount),
                  ...(rate !== undefined && rate !== null && rate !== ""
                    ? { rate: Number(rate) }
                    : {}),
                  ...(quantity !== undefined &&
                  quantity !== null &&
                  quantity !== ""
                    ? { quantity: Number(quantity) }
                    : {}),
                  ...(workUomId !== undefined && workUomId !== null
                    ? { workUomId: Number(workUomId) }
                    : {}),
                  // Rate analysis (unit-basis) — independent editable fields.
                  ...(rateAnalysisQty !== undefined &&
                  rateAnalysisQty !== null &&
                  rateAnalysisQty !== ""
                    ? { rateAnalysisQty: Number(rateAnalysisQty) }
                    : {}),
                  ...(rateAnalysisUomId !== undefined &&
                  rateAnalysisUomId !== null
                    ? { rateAnalysisUomId: Number(rateAnalysisUomId) }
                    : {}),
                  ...(rateAnalysisRate !== undefined &&
                  rateAnalysisRate !== null &&
                  rateAnalysisRate !== ""
                    ? { rateAnalysisRate: Number(rateAnalysisRate) }
                    : {}),
                };
              }
              return null;
            })
            .filter(
              (resource: any): resource is TaskResource => resource !== null,
            );

      // Map the selected user/owner ids to their projectUserId. The form's
      // taskOwnerId holds userId (Owner Select options use value: userId),
      // whereas the projectUserId field holds membership ids (teamOptions
      // uses value: projectUserId). Match against either to stay correct.
      const findProjectUser = (id: number | string) =>
        projectUsers.find(
          (user: ProjectUserOption) =>
            Number(user.projectUserId) === Number(id) ||
            Number(user.value) === Number(id),
        );

      const owner = findProjectUser(taskOwnerId as any);
      const ownerProjectUserId = owner
        ? Number(owner.projectUserId)
        : Number(taskOwnerId);
      if (!owner) {
        console.warn(
          `projectUserId not found for owner id: ${taskOwnerId}. Using fallback: ${ownerProjectUserId}`,
        );
      }

      const selectedTaskUsers: TaskUser[] = [
        {
          projectUserId: ownerProjectUserId,
          [isSubtask ? "subTaskUserType" : "taskUserType"]: "Owner",
        },
        ...(projectUserId?.map((id: number) => {
          const user = findProjectUser(id);
          const projectUserIdMapped = user
            ? Number(user.projectUserId)
            : Number(id);
          if (!user) {
            console.warn(
              `projectUserId not found for team id: ${id}. Using fallback: ${projectUserIdMapped}`,
            );
          }
          return {
            projectUserId: projectUserIdMapped,
            [isSubtask ? "subTaskUserType" : "taskUserType"]: "Resource Head",
          };
        }) || []),
      ];

      const formattedTags: TaskTag[] =
        tags
          ?.filter((tag) => tag.label)
          ?.map((tag) => ({
            tagName: tag.label,
          })) || [];

      // Format the reminders payload conditionally
      const formattedReminders = hasReminders
        ? {
            // Set default reminderType if not provided (required by API)
            reminderType: remainders.reminderType || "Daily",
            // Set default specificTime if not provided (required by API)
            specificTime: remainders.specificTime || "09:00",
            // Backend requires recurrenceType to always be present
            // If no recurrence is selected, default to "None"
            recurrenceType: remainders.recurrenceType || "None",
            // Only include recurrenceInterval and maxRecurrenceCount if recurrenceType is not "None"
            ...(remainders.recurrenceType &&
              remainders.recurrenceType !== "None" && {
                recurrenceInterval: Number(remainders.recurrenceInterval),
                maxRecurrenceCount: Number(remainders.maxRecurrenceCount),
              }),
            reminderUsers: remainders.reminderUsers || [],
            moduleType: isSubtask ? "SubTask" : "Task",
            ...(remainders.reminderType === "On" &&
              remainders.offsetFrom === "Specific Date" && {
                specificDate: remainders.specificDate,
              }),
            ...(["Before", "After"].includes(remainders.reminderType) && {
              daysOffset: Number(remainders.daysOffset),
              offsetFrom: remainders.offsetFrom,
            }),
            ...(remainders.reminderType === "On" &&
              remainders.offsetFrom !== "Specific Date" && {
                daysOffset: Number(remainders.daysOffset),
                offsetFrom: remainders.offsetFrom,
              }),
          }
        : null;

      const checklistRequired = watch("ChecklistRequired") === "yes";

      // Filter out incomplete checklist entries (missing status or checklist)
      const validChecklistDataForPayload = (Checklists.data || []).filter(
        (item: any) => item.status && item.checklist,
      );

      const checklistLinks = validChecklistDataForPayload.map((item: any) => ({
        checklistId: item.checklist,
        isMandatory: item.mandatory,
        status: item.status,
      }));

      const payload: TaskCreationPayload | SubTaskCreationPayload = isSubtask
        ? {
            projectId: projectId,
            phaseId: phaseId,
            activityId: id,
            taskId: taskId,
            subTaskName: taskName,
            ...(taskDescription &&
              taskDescription.trim() && {
                subTaskDescription: taskDescription,
              }),
            subTaskOwnerId: Number(taskOwnerId),
            ...(taskPlannedStartDate &&
              dayjs(taskPlannedStartDate).isValid() && {
                subTaskPlannedStartDate:
                  dayjs(taskPlannedStartDate).format("YYYY-MM-DD"),
              }),
            ...(taskPlannedEndDate &&
              dayjs(taskPlannedEndDate).isValid() && {
                subTaskPlannedEndDate:
                  dayjs(taskPlannedEndDate).format("YYYY-MM-DD"),
              }),
            ...(taskPlannedStartDate &&
              dayjs(taskPlannedStartDate).isValid() && {
                subTaskActualStartDate:
                  dayjs(taskPlannedStartDate).format("YYYY-MM-DD"),
              }),
            ...(taskPlannedEndDate &&
              dayjs(taskPlannedEndDate).isValid() && {
                subTaskActualEndDate:
                  dayjs(taskPlannedEndDate).format("YYYY-MM-DD"),
              }),
            ...(priorityId && { priorityId: Number(priorityId) }),
            ...(isBillable && {
              isBillable: isBillable === "Billable" ? true : false,
            }),
            workHoursInSec: 1, // Default work hours in seconds
            reminderFlag: !!hasReminders, // Ensure boolean
            subTaskTags: formattedTags,
            subTaskUsers: selectedTaskUsers,
            ...(hasReminders && { reminders: formattedReminders }),
            approvalRequired: data.approvalRequired === "yes", // Convert to boolean
            ...(data.approvalRequired === "yes" && {
              stateApprovalHierarchy: approvals.data,
            }),
            ...(data.approvalRequired === "no" && {
              autoApprovalRemarks: data.autoApprovalRemarks,
            }),
            documentsRequired: data.documentsRequired === "yes", // Convert to boolean
            ...(data.documentsRequired === "yes" && {
              documentsData: documents.data,
            }),
            // Include site hierarchy fields if provided
            ...(data.workLevelSetting && {
              workLevelSetting: data.workLevelSetting,
              segmentLevel: getSegmentLevel(data.workLevelSetting), // Segment Level is mandatory for Site Structure Validation
              ...(data.siteId && { siteId: Number(data.siteId) }), // Site ID is required for site structure validation
            }),
            ...(data.siteStructureUnitId && {
              siteStructureUnitId: Number(data.siteStructureUnitId),
            }),
            ...(data.floorNo && {
              floorNo: data.floorNo,
            }),
            ...(data.siteStructureSubUnitId && {
              siteStructureSubUnitId: Number(data.siteStructureSubUnitId),
            }),
            ...(data.locationId && {
              locationId: Number(data.locationId),
            }),
          }
        : {
            projectId,
            phaseId,
            activityId: id,
            taskName,
            // Send the task number only when entered manually (auto mode lets
            // the server assign it from the sequence master).
            ...(taskNumberingMode === "manual" &&
              data.taskSequenceId && {
                taskSequenceId: data.taskSequenceId,
              }),
            internalTaskNumber: (data.internalTaskNumber || "").trim(),
            ...(taskDescription &&
              taskDescription.trim() && { taskDescription }),
            taskOwnerId: Number(taskOwnerId),
            contractorId: Number(contractorId),
            workType: workType || "Open", // Default to "Open" if not provided
            ...(workType === "Quantity" &&
              workQuantity && {
                workQuantity: Number(workQuantity),
              }),
            ...(workType === "Quantity" &&
              workUomId && {
                workUomId: Number(workUomId),
              }),
            ...(workType === "Quantity" &&
              data.outputQuantity !== undefined &&
              data.outputQuantity !== null &&
              data.outputQuantity !== "" && {
                outputQuantity: Number(data.outputQuantity),
              }),
            ...(workType === "Quantity" &&
              data.outputBasisUomId && {
                outputBasisUomId: Number(data.outputBasisUomId),
              }),
            ...(taskPlannedStartDate &&
              dayjs(taskPlannedStartDate).isValid() && {
                taskPlannedStartDate:
                  dayjs(taskPlannedStartDate).format("YYYY-MM-DD"),
              }),
            ...(taskPlannedEndDate &&
              dayjs(taskPlannedEndDate).isValid() && {
                taskPlannedEndDate:
                  dayjs(taskPlannedEndDate).format("YYYY-MM-DD"),
              }),
            ...(priorityId && { priorityId: Number(priorityId) }),
            ...(isBillable && {
              isBillable: isBillable === "Billable" ? true : false,
            }),
            reminderFlag: hasReminders,
            workCategoryId: Number(workCategoryId),
            taskTags: formattedTags,
            // BOQ delinked — materials come from norms, not task BOQ.
            taskBoqs: [],
            taskResources: selectedActivityResources,
            taskUsers: selectedTaskUsers,
            ...(taskTools.length > 0 && {
              taskTools: taskTools.map((tool) => ({
                toolId: tool.toolId,
                ...(tool.serialNumber
                  ? { serialNumber: tool.serialNumber }
                  : {}),
                ...(tool.rate !== undefined && tool.rate !== null
                  ? { rate: Number(tool.rate) }
                  : {}),
                ...(tool.quantity !== undefined && tool.quantity !== null
                  ? { quantity: Number(tool.quantity) }
                  : {}),
                ...(tool.workUomId !== undefined && tool.workUomId !== null
                  ? { workUomId: Number(tool.workUomId) }
                  : {}),
                ...(tool.rateAnalysisQty !== undefined &&
                tool.rateAnalysisQty !== null
                  ? { rateAnalysisQty: Number(tool.rateAnalysisQty) }
                  : {}),
                ...(tool.rateAnalysisUomId !== undefined &&
                tool.rateAnalysisUomId !== null
                  ? { rateAnalysisUomId: Number(tool.rateAnalysisUomId) }
                  : {}),
                ...(tool.rateAnalysisRate !== undefined &&
                tool.rateAnalysisRate !== null
                  ? { rateAnalysisRate: Number(tool.rateAnalysisRate) }
                  : {}),
                ...(tool.toolMaterials && tool.toolMaterials.length > 0
                  ? {
                      toolMaterials: tool.toolMaterials.map((m) => ({
                        materialId: Number(m.materialId),
                        ...(m.subMaterialId != null
                          ? { subMaterialId: Number(m.subMaterialId) }
                          : {}),
                        ...(m.quantity != null
                          ? { quantity: Number(m.quantity) }
                          : {}),
                        ...(m.workUomId != null
                          ? { workUomId: Number(m.workUomId) }
                          : {}),
                        ...(m.rate != null ? { rate: Number(m.rate) } : {}),
                        ...(m.rateAnalysisQty != null
                          ? { rateAnalysisQty: Number(m.rateAnalysisQty) }
                          : {}),
                        ...(m.rateAnalysisUomId != null
                          ? { rateAnalysisUomId: Number(m.rateAnalysisUomId) }
                          : {}),
                        ...(m.rateAnalysisRate != null
                          ? { rateAnalysisRate: Number(m.rateAnalysisRate) }
                          : {}),
                      })),
                    }
                  : {}),
              })),
            }),
            ...(hyperlinks.length > 0 && {
              hyperlinks: hyperlinks.map((hyperlink) => ({
                name: hyperlink.name,
                link: hyperlink.link,
              })),
            }),
            ...(hasReminders && { reminders: formattedReminders }),
            taskWeightagePercentage: Number(taskWeightage) || 0,
            checklistRequired,
            checklistLinks,
            approvalRequired: data.approvalRequired === "yes", // Convert to boolean
            ...(data.approvalRequired === "yes" && {
              stateApprovalHierarchy: approvals.data,
            }),
            ...(data.approvalRequired === "no" && {
              autoApprovalRemarks: data.autoApprovalRemarks,
            }),
            documentsRequired: data.documentsRequired === "yes", // Convert to boolean
            ...(data.documentsRequired === "yes" &&
              documents.data.length > 0 && {
                docKeys: documents.data,
              }),
            // Include budget line information if available
            ...(budgetLineId && {
              budgetLineId: Number(budgetLineId),
            }),
            ...(allocatedBudgetAmount && {
              budgetValue: Number(allocatedBudgetAmount),
            }),
            // Include rate if provided. Only the GST-inclusive rate and the two
            // percentages are sent — the server derives and stores base/GST/cess/upload.
            ...(rate && {
              rate: Number(rate),
            }),
            ...(gstPercent !== "" && gstPercent !== undefined && gstPercent !== null
              ? { gstPercent: Number(gstPercent) }
              : {}),
            ...(cessPercent !== "" && cessPercent !== undefined && cessPercent !== null
              ? { cessPercent: Number(cessPercent) }
              : {}),
            // Include site hierarchy fields if provided (never for Infra
            // projects — their tasks don't capture site hierarchy/location)
            ...(!isInfraStructure && {
              ...(data.workLevelSetting && {
                workLevelSetting: data.workLevelSetting,
                segmentLevel: getSegmentLevel(data.workLevelSetting), // Segment Level is mandatory for Site Structure Validation
                ...(data.siteId && { siteId: Number(data.siteId) }), // Site ID is required for site structure validation
              }),
              ...(data.siteStructureUnitId && {
                siteStructureUnitId: Number(data.siteStructureUnitId),
              }),
              ...(data.floorNo && {
                floorNo: data.floorNo,
              }),
              ...(data.siteStructureSubUnitId && {
                siteStructureSubUnitId: Number(data.siteStructureSubUnitId),
              }),
              ...(data.locationId && {
                locationId: Number(data.locationId),
              }),
            }),
          };

      // If documents with files are present, use FormData
      let response: ApiResponse;
      if (documentFiles.length > 0 && documents.data.length > 0) {
        const formDataToSend = new FormData();

        // Append files to FormData
        documentFiles.forEach((file) => {
          formDataToSend.append("files", file, file.name);
        });

        // Append the payload as JSON string with key "body"
        formDataToSend.append("body", JSON.stringify(payload));

        // Make API call with FormData
        response = isSubtask
          ? await tasksApis.createSubTask(formDataToSend)
          : await tasksApis.createNewTask(formDataToSend);
      } else {
        // For subtasks without files, send direct JSON payload
        // For tasks without files, use FormData with body field
        if (isSubtask) {
          response = await tasksApis.createSubTask(payload);
        } else {
          const formDataToSend = new FormData();
          formDataToSend.append("body", JSON.stringify(payload));
          response = await tasksApis.createNewTask(formDataToSend);
        }
      }

      const { data: task, status, error, message } = response || {};

      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: task?.message || "Task created successfully",
          confirmButtonText: "Ok",
        }).then(() => {
          reset();
          setTags([]);
          form.resetFields();
          setTaskTools([]);
          setSelectedToolType(null);
          setSelectedTool(null);
          setHyperlinks([]);
          if (!isSubtask && refreshData) {
            refreshData(); // Safe call
          }
          if (!isPageMode) {
            dispatch(
              updateTaskListDetailsModalSlice({ refreshSubTaskList: true }),
            );
          }
          closeCreateTaskDrawer();
          if (isPageMode) {
            onCreated?.();
          }
        });
      } else {
        setErrorData({ error, message });
        setTimeout(() => {
          setErrorData(undefined);
        }, 10000);
      }
    } catch (error) {
      console.error("Task creation error:", error);
    } finally {
      setDisableSubmit(false);
    }
  };
  useEffect(() => {
    // Fetch subtask details when opening subtask view (either for viewing or updating)
    if (isSubtask && currentSubtaskId) {
      fetchSubtaskDetails();
    }
  }, [isSubtask, currentSubtaskId]);

  useEffect(() => {
    if (selectedSubtaskDetails) {
      const initialTags =
        selectedSubtaskDetails.subTaskTags?.map((tag: any) => ({
          label: tag.tagName,
          value: tag.tagId,
        })) || [];
      setRemainders(selectedSubtaskDetails.reminders);
      reset({
        taskName: selectedSubtaskDetails?.subTaskName || "",
        taskDescription: selectedSubtaskDetails?.subTaskDescription || "",
        taskOwnerId: selectedSubtaskDetails?.subTaskOwnerId
          ? Number(selectedSubtaskDetails?.subTaskOwnerId)
          : null,
        taskPlannedStartDate: selectedSubtaskDetails?.subTaskPlannedStartDate
          ? dayjs(selectedSubtaskDetails?.subTaskPlannedStartDate, "YYYY-MM-DD")
          : null,
        taskPlannedEndDate: selectedSubtaskDetails?.subTaskPlannedEndDate
          ? dayjs(selectedSubtaskDetails?.subTaskPlannedEndDate, "YYYY-MM-DD")
          : null,
        priorityId: selectedSubtaskDetails?.priorityId
          ? Number(selectedSubtaskDetails?.priorityId)
          : null,
        isBillable:
          selectedSubtaskDetails?.isBillable !== undefined
            ? selectedSubtaskDetails.isBillable === true
              ? "Billable"
              : "Non Billable"
            : "",
        projectUserId:
          selectedSubtaskDetails?.subTaskUsers
            ?.filter((user: any) => user.subTaskUserType !== "Owner")
            ?.map((user: any) => Number(user.projectUserId)) || [],
        taskWeightage: null,
      });
      setTags(initialTags);
    }
  }, [selectedSubtaskDetails, reset]);

  const fetchTaskStatusList = async () => {
    if (isLoadingTaskStatus) return;
    setIsLoadingTaskStatus(true);
    try {
      const response = await tasksApis.fetchTaskStatusList("TaskStatusChange");
      const { data, status } = response || {};
      if (status) {
        // Define your desired order
        const desiredOrder = [
          "Open",
          "OnHold",
          "InProgress",
          "UnderQC",
          "Closed",
        ];
        // Map and filter the API data to match the desired order
        const formattedStatusList = desiredOrder
          .map((statusName) => {
            // Find the item in the API data (case-insensitive)
            const item = data.find(
              (d: string) => d.toLowerCase() === statusName.toLowerCase(),
            );
            if (item) {
              return {
                label: item,
                key: item,
                value: item,
              };
            }
            return null;
          })
          .filter(Boolean); // Remove any nulls if a status is missing in API

        setTaskStatusList(formattedStatusList);
      }
    } catch (error) {
      console.error("Error fetching task status list:", error);
      messageApi.error("Failed to fetch task status list");
    } finally {
      setIsLoadingTaskStatus(false);
    }
  };

  // Ensure status list is available in subtask view/update so the dropdown shows options
  useEffect(() => {
    if (isSubtaskUpdate && taskStatusList.length === 0) {
      fetchTaskStatusList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubtaskUpdate]);

  // Handle status change
  const handleStatusChange = async ({ key }: { key: string }) => {
    const newStatus = key;

    // Check if status is OnHold (case insensitive)
    if (newStatus.toLowerCase() === "onhold") {
      // Show confirmation modal for OnHold status
      Modal.confirm({
        title: "Task On Hold Confirmation",
        content: (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
              >
                Reason for putting task on hold: *
              </label>
              <Input.TextArea
                id="onhold-reason"
                placeholder="Please provide the reason for putting this task on hold..."
                rows={3}
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label
                style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
              >
                Expected date to resume: *
              </label>
              <DatePicker
                id="expected-resume-date"
                style={{ width: "100%" }}
                placeholder="Select expected resume date"
                format="YYYY-MM-DD"
                disabledDate={(current) => {
                  // Disable past dates
                  return current && current < dayjs().startOf("day");
                }}
              />
            </div>
          </div>
        ),
        width: 500,
        okText: "Confirm",
        cancelText: "Cancel",
        okButtonProps: { type: "primary" },
        cancelButtonProps: { type: "default" },
        onOk: async () => {
          const reason = (
            document.getElementById("onhold-reason") as HTMLTextAreaElement
          )?.value;
          const expectedResumeDate = (
            document.getElementById("expected-resume-date") as any
          )?.value;

          if (!reason || !reason.trim()) {
            messageApi.open({
              type: "error",
              content: "Please provide a reason for putting the task on hold.",
            });
            return Promise.reject();
          }

          if (!expectedResumeDate) {
            messageApi.open({
              type: "error",
              content: "Please select an expected resume date.",
            });
            return Promise.reject();
          }

          // Continue with status change API call
          try {
            setLoading(true);
            const response = await tasksApis.SubTaskstatusList(
              selectedSubtaskDetails.subTaskId,
              newStatus,
              {
                taskOnholdReason: reason.trim(),
                taskETAResolution:
                  dayjs(expectedResumeDate).format("YYYY-MM-DD"),
              },
            );
            const { status, message, error } = response || {};
            if (status) {
              Swal.fire({
                icon: "success",
                title: "Success",
                text: "Task status updated successfully!",
                confirmButtonText: "Ok",
              });
              // Update subtaskAllowEdit based on new status
              const isSubtaskEditable = isSubtaskStatusEditable(newStatus);
              const updatedSubtaskAllowEdit =
                isSubtaskEditable && originalSubtaskAllowEdit;

              dispatch(
                updateTaskListDetailsModalSlice({
                  selectedSubtaskDetails: {
                    ...selectedSubtaskDetails,
                    subTaskStatus: newStatus,
                  },
                  subtaskAllowEdit: updatedSubtaskAllowEdit,
                  refreshSubTaskList: true,
                }),
              );

              // If subtask is no longer editable and we're in edit mode, exit edit mode
              if (!updatedSubtaskAllowEdit && isEditMode) {
                setIsEditMode(false);
              }
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: message,
                confirmButtonText: "Ok",
              });
            }
          } catch (error) {
            console.error("Error updating task status:", error);
            messageApi.open({
              type: "error",
              content: "Failed to update task status. Please try again.",
            });
          } finally {
            setLoading(false);
          }
        },
        onCancel: () => {
          // User cancelled the action
        },
      });
    } else {
      // For non-OnHold statuses, proceed with normal flow
      try {
        setLoading(true);
        const response = await tasksApis.SubTaskstatusList(
          selectedSubtaskDetails.subTaskId,
          newStatus,
        );
        const { status, message, error } = response || {};
        if (status) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Task status updated successfully!",
            confirmButtonText: "Ok",
          });
          // Update subtaskAllowEdit based on new status
          const isSubtaskEditable = isSubtaskStatusEditable(newStatus);
          const updatedSubtaskAllowEdit =
            isSubtaskEditable && originalSubtaskAllowEdit;

          dispatch(
            updateTaskListDetailsModalSlice({
              selectedSubtaskDetails: {
                ...selectedSubtaskDetails,
                subTaskStatus: newStatus,
              },
              subtaskAllowEdit: updatedSubtaskAllowEdit,
              refreshSubTaskList: true,
            }),
          );

          // If subtask is no longer editable and we're in edit mode, exit edit mode
          if (!updatedSubtaskAllowEdit && isEditMode) {
            setIsEditMode(false);
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: message,
            confirmButtonText: "Ok",
          });
        }
      } catch (error) {
        console.error("catch block Error updating task status:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!isSubtask && boqLineIdsWatch?.length) {
      const filteredData: BoqLine[] = [];
      // Ensure all selected BOQ lines are from the same target
      const firstBoq = activityBoqList.find(
        (item: ActivityBoqOption) => item.activityBoqId === boqLineIdsWatch[0],
      );

      if (firstBoq) {
        const targetNumber = firstBoq.boqTargetNumber;
        // Only process BOQ lines from the same target
        boqLineIdsWatch.forEach((id: number) => {
          const boqLine = activityBoqList.find(
            (item: ActivityBoqOption) => item.activityBoqId === id,
          );
          if (boqLine && boqLine.boqTargetNumber === targetNumber) {
            const { lines, activityBoqId, boqTargetNumber, boqTitle } = boqLine;
            // Get boqLineName from BOQ level (it's at BOQ level in API response)
            const boqLineName = (boqLine as any)?.boqLineName;
            const boqLines = lines?.map((line: any) => {
              const lineId =
                line.activityBoqLineId ?? line.materialId ?? line.uomId;
              return {
                ...line,
                boqTargetNumber,
                boqTitle,
                boqLineName: boqLineName,
                activityBoqId,
                allotedQuantity: line.allotedQuantity,
                taskAllocatedQuantity: line.taskAllocatedQuantity,
                taskAvailableQuantity: line.taskAvailableQuantity,
                activityBoqLineId: lineId,
                key: `${activityBoqId}_${lineId}`,
              };
            });
            filteredData.push(...(boqLines || []));
          }
        });
      }
      setBoqLinesDataSource(filteredData);
    } else {
      setBoqLinesDataSource([]);
    }
  }, [boqLineIdsWatch, activityBoqList, isSubtask]);

  const onResourceSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows?: any,
  ) => {
    setSelectedResourceKeys(newSelectedRowKeys);
    if (!newSelectedRowKeys) {
      setEditingResourceKeys(() => []);
    }
    const editableRowKeys: any = [];
    newSelectedRowKeys?.forEach((key) => {
      const selectedRow = selectedRows.find(
        (row: any) => `${row.activityResourceId}` === key,
      );
      editableRowKeys.push(key);
      if (selectedRow?.resourceCount) {
        resourceForm.setFieldsValue({
          [`resourceCount_${key}`]: selectedRow?.resourceCount,
        });
      }
    });
    setEditingResourceKeys(editableRowKeys);
  };

  const isResourceEditing = (record: any) =>
    editingResourceKeys.includes(`${record.activityResourceId}`);

  // ---------- Page-mode (full-page stepper) support ----------
  useEffect(() => {
    onSubmittingChange?.(disableSubmit);
  }, [disableSubmit, onSubmittingChange]);

  // Re-seed step-1 basics after TaskCreatePage remounts us on activity change.
  const initialBasicsAppliedRef = useRef(false);
  useEffect(() => {
    if (isPageMode && initialBasics && !initialBasicsAppliedRef.current) {
      initialBasicsAppliedRef.current = true;
      Object.entries(initialBasics).forEach(([key, value]) => {
        if (value !== undefined) {
          setValue(key as any, value, { shouldValidate: false });
        }
      });
    }
  }, [isPageMode, initialBasics, setValue]);

  const BASIC_FIELD_NAMES = [
    "taskName",
    "taskSequenceId",
    "internalTaskNumber",
    "taskDescription",
    "workCategoryId",
    "workType",
    "workQuantity",
    "workUomId",
  ];

  // Fields whose react-hook-form rules gate the Next button per step. Fields
  // without rules (or currently unmounted) pass trigger() untouched — the
  // authoritative full validation still runs inside submitNewTask on Create.
  const STEP_FIELDS: string[][] = [
    BASIC_FIELD_NAMES,
    [
      "taskOwnerId",
      "taskPlannedStartDate",
      "taskPlannedEndDate",
      "priorityId",
      "isBillable",
      "taskWeightage",
      "rate",
      "gstPercent",
      "cessPercent",
      "contractorId",
      "budgetLineId",
      "allocatedBudgetAmount",
      "projectUserId",
      "workLevelSetting",
      "siteId",
      "siteStructureUnitId",
      "floorNo",
      "siteStructureSubUnitId",
      "locationId",
    ],
    ["outputQuantity", "outputBasisUomId"],
    [],
  ];

  // Page mode hides (never unmounts) the panels of inactive steps so
  // react-hook-form and the antd table forms keep their state across steps.
  // Drawer mode renders everything, exactly as before.
  const stepStyle = (step: number): React.CSSProperties | undefined =>
    isPageMode && activeStep !== step ? { display: "none" } : undefined;

  const validateStep = async (step: number): Promise<boolean> => {
    const fields = STEP_FIELDS[step] ?? [];
    if (fields.length > 0) {
      const rhfOk = await methods.trigger(fields as any);
      if (!rhfOk) return false;
    }
    if (step === 2) {
      // BOQ-materials / resources rows currently in edit live in antd forms.
      try {
        await form.validateFields();
        await resourceForm.validateFields();
      } catch {
        return false;
      }
    }
    return true;
  };

  const getBasics = () => {
    const values = getValues() as Record<string, any>;
    return BASIC_FIELD_NAMES.reduce<Record<string, any>>((acc, key) => {
      acc[key] = values[key];
      return acc;
    }, {});
  };

  // Keep the imperative surface fresh every render (closures over live state).
  useEffect(() => {
    if (isPageMode && apiRef) {
      apiRef.current = {
        validateStep,
        submit: handleSubmit(submitNewTask),
        getBasics,
      };
    }
  });

  // ---------- Step-4 review summary (page mode only) ----------
  const reviewOptionLabel = (options: any[] | undefined, value: any) =>
    value == null || value === ""
      ? "—"
      : options?.find((o) => String(o.value) === String(value))?.label ??
        String(value);
  const reviewDate = (value: any) =>
    value ? dayjs(value).format("DD MMM YYYY") : "—";
  const reviewMoney = (value: number) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;
  const reviewItem = (label: string, value: React.ReactNode) => (
    <div className="task-review-item" key={label}>
      <div className="task-review-label">{label}</div>
      <div className="task-review-value">
        {value === undefined || value === null || value === "" ? "—" : value}
      </div>
    </div>
  );

  const drawerTitle = (
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <strong>
                {isSubtaskUpdate
                  ? selectedSubtaskDetails?.subTaskName
                  : `New ${
                      isSubtask ? "Subtask" : "Task"
                    } Creation for ${projectName}`}
              </strong>
              {isSubtaskUpdate &&
                (isEditMode ? (
                  <Tag
                    color={getStatusColor(
                      selectedSubtaskDetails?.subTaskStatus,
                    )}
                    className="ms-2"
                    style={{
                      borderRadius: "12px",
                      padding: "2px 10px",
                      fontWeight: 500,
                      fontSize: "12px",
                      margin: 0,
                      marginLeft: "8px",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    {getLabel(
                      taskStatusList,
                      selectedSubtaskDetails?.subTaskStatus,
                    )}
                  </Tag>
                ) : (
                  <EditAccess moduleName="Tasks">
                    <Dropdown
                      menu={{
                        items: taskStatusList,
                        selectedKeys: [
                          isSubtaskUpdate
                            ? selectedSubtaskDetails?.subTaskStatus
                            : selectedTaskDetails?.taskStatus,
                        ],
                        onClick: handleStatusChange,
                      }}
                      trigger={["hover", "click"]}
                      placement="bottomLeft"
                      overlayStyle={{ zIndex: 1050 }}
                      getPopupContainer={() => document.body}
                      disabled={false}
                    >
                      <a
                        onClick={(e) => e.preventDefault()}
                        className="ms-2"
                        style={{ textDecoration: "none" }}
                      >
                        <Tag
                          color={getStatusColor(
                            selectedSubtaskDetails?.subTaskStatus,
                          )}
                          style={{
                            cursor: "pointer",
                            borderRadius: "12px",
                            padding: "2px 10px",
                            fontWeight: 500,
                            fontSize: "12px",
                            margin: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {getLabel(
                            taskStatusList,
                            selectedSubtaskDetails?.subTaskStatus,
                          )}
                          <DownOutlined style={{ fontSize: "10px" }} />
                        </Tag>
                      </a>
                    </Dropdown>
                  </EditAccess>
                ))}
            </div>
            <div style={{ textAlign: "right" }}>
              {disableSubmit ? (
                <Button type="primary" size="small" loading>
                  Save
                </Button>
              ) : (
                !isSubtaskUpdate && (
                  <Button
                    onClick={handleSubmit(submitNewTask)}
                    type="primary"
                    size="small"
                  >
                    Save
                  </Button>
                )
              )}
            </div>
          </div>
  );

  const bodyContent = (
    <>
        {contextHolder}
        <Alert
          isOpen={errorData != undefined}
          color="danger"
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <span className="fw-bold">{errorData?.error}: </span>
          <ReactTyped
            strings={[errorData?.message || ""]}
            typeSpeed={10}
            showCursor={false}
          />
        </Alert>
        {isSubtaskUpdate && (
          <div
            style={{
              position: "relative",
              top: 0,
              background: "#fff",
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
            }}
          >
            {!isEditMode ? (
              <EditAccess moduleName="Tasks">
                <Button
                  size="small"
                  onClick={async () => {
                    // Check if subtask is still editable before entering edit mode
                    if (
                      !isSubtaskStatusEditable(
                        selectedSubtaskDetails?.subTaskStatus,
                      )
                    ) {
                      return;
                    }

                    // Load dropdown data when Edit button is clicked for subtasks
                    if (isSubtask && !initialDataLoaded) {
                      setIsLoadingData(true);
                      try {
                        // Load common data for subtasks
                        await Promise.all([
                          fetchPriority(),
                          fetchWorkCatgoriesList(),
                          fetchProjectUsers(),
                          fetchProjectTags(),
                          // Fetch status list only if not already loaded
                          ...(taskStatusList.length === 0
                            ? [fetchTaskStatusList()]
                            : []),
                        ]);
                        setInitialDataLoaded(true);
                      } catch (error) {
                        console.error("Error loading dropdown data:", error);
                      } finally {
                        setIsLoadingData(false);
                      }
                    }
                    setIsEditMode(true);
                  }}
                  disabled={
                    isSubtaskUpdate ? subtaskAllowEdit === false : !allowEdit
                  }
                >
                  <span>Edit</span>
                </Button>
              </EditAccess>
            ) : (
              <>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="small"
                  onClick={handleSubmit(updateSubTask)}
                  disabled={disableSubmit}
                >
                  <Icon name="save" /> <span>Save</span>
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    Swal.fire({
                      title: "Are you sure?",
                      text: "You won't be able to revert this!",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#3085d6",
                      cancelButtonColor: "#d33",
                      confirmButtonText: "Yes, cancel it!",
                    }).then((result) => {
                      if (result.isConfirmed) {
                        Swal.fire({
                          title: "Cancelled",
                          icon: "success",
                          text: "Your changes have been cancelled.",
                        });
                        setIsEditMode(false);
                        reset();
                        setTaskTools([]);
                        setSelectedToolType(null);
                        setSelectedTool(null);
                        setHyperlinks([]);
                      }
                    });
                  }}
                >
                  <span>Cancel</span>
                </Button>
              </>
            )}
          </div>
        )}

        {(showTaskCreationModal || isSubtaskUpdate) && (
          <FormProvider {...methods}>
            <form className="content clearfix mt-2">
              <div className="row g-3" style={stepStyle(0)}>
                <div className="col-md-4">
                  <FormInput
                    name="taskName"
                    label="Task Name"
                    disabled={!isEditMode}
                    required={true}
                    type="text"
                    placeholder="Enter Task Name"
                  />
                </div>
                {!isSubtask && (
                  <div className="col-md-4">
                    <SequenceInput
                      name="taskSequenceId"
                      label="Task Number"
                      numberingMode={taskNumberingMode}
                      onSettingsClick={openNumberingModal}
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormInput
                      name="internalTaskNumber"
                      label="Internal Task Number"
                      disabled={!isEditMode}
                      required={true}
                      type="text"
                      placeholder="Enter Internal Task Number"
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormSelect
                      name="workCategoryId"
                      label="Work Category"
                      disabled={!isEditMode}
                      options={workCatgoriesList}
                      required={true}
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormSelect
                      name="workType"
                      label="Work Type"
                      disabled={!isEditMode}
                      options={[
                        { label: "Open", value: "Open" },
                        { label: "Quantity", value: "Quantity" },
                      ]}
                      defaultValue="Open"
                      required={true}
                    />
                  </div>
                )}

                {!isSubtask && workTypeWatch === "Quantity" && (
                  <>
                    <div className="col-md-4">
                      <FormInput
                        name="workQuantity"
                        label="Work Quantity"
                        pattern={/^\d+$/}
                        disabled={!isEditMode}
                        type="number"
                        placeholder="Enter Work Quantity"
                        required={true}
                      />
                    </div>
                    <div className="col-md-4">
                      <FormSelect
                        name="workUomId"
                        label="Work UOM"
                        options={workUomsList}
                        placeholder="Select UOM"
                        required={true}
                      />
                    </div>
                  </>
                )}
                <div className="col-12">
                  <FormInput
                    name="taskDescription"
                    label="Add Description"
                    disabled={!isEditMode}
                    type="text"
                    minLength={2}
                    maxLength={200}
                    placeholder="Enter Description"
                  />
                </div>
              </div>

              <div className="row g-3" style={stepStyle(1)}>
                <div className="col-12">
                  <div
                    className="pms-eyebrow"
                    style={{ marginTop: 16, marginBottom: 8 }}
                  >
                    Task Information
                  </div>
                </div>
                <div className="col-md-4">
                  <FormSelect
                    name="taskOwnerId"
                    label="Owner"
                    placeholder="Select Owner"
                    disabled={!isEditMode}
                    options={ownerOptions}
                    required={true}
                  />
                </div>
                <div className="col-md-4">
                  <FormDatePicker
                    name="taskPlannedStartDate"
                    label="Planned Start Date"
                    disabled={!isEditMode}
                    minDate={
                      isSubtask
                        ? selectedTaskDetails?.taskPlannedStartDate
                          ? new Date(selectedTaskDetails.taskPlannedStartDate)
                          : activityDateRange.start || activityStartDate
                            ? new Date(
                                (activityDateRange.start ||
                                  activityStartDate) as string,
                              )
                            : undefined
                        : activityDateRange.start || activityStartDate
                          ? new Date(
                              (activityDateRange.start ||
                                activityStartDate) as string,
                            )
                          : undefined
                    }
                    maxDate={
                      isSubtask
                        ? selectedTaskDetails?.taskPlannedEndDate
                          ? new Date(selectedTaskDetails.taskPlannedEndDate)
                          : activityDateRange.end || activityEndDate
                            ? new Date(
                                (activityDateRange.end ||
                                  activityEndDate) as string,
                              )
                            : undefined
                        : activityDateRange.end || activityEndDate
                          ? new Date(
                              (activityDateRange.end ||
                                activityEndDate) as string,
                            )
                          : undefined
                    }
                  />
                </div>
                <div className="col-md-4">
                  <FormDatePicker
                    name="taskPlannedEndDate"
                    label="Planned End Date"
                    disabled={!startDateWatch || !isEditMode}
                    minDate={startDateWatch}
                    maxDate={
                      isSubtask
                        ? selectedTaskDetails?.taskPlannedEndDate
                          ? new Date(selectedTaskDetails.taskPlannedEndDate)
                          : activityDateRange.end || activityEndDate
                            ? new Date(
                                (activityDateRange.end ||
                                  activityEndDate) as string,
                              )
                            : undefined
                        : activityDateRange.end || activityEndDate
                          ? new Date(
                              (activityDateRange.end ||
                                activityEndDate) as string,
                            )
                          : undefined
                    }
                    defaultPickerValue={
                      activityDateRange.end || activityEndDate
                        ? new Date(
                            (activityDateRange.end ||
                              activityEndDate) as string,
                          )
                        : undefined
                    }
                  />
                </div>
                <div className="col-md-4">
                  <FormSelect
                    name="priorityId"
                    label="Priority"
                    options={priorityList}
                    disabled={!isEditMode}
                  />
                </div>
                <div className="col-md-4">
                  <FormSelect
                    name="isBillable"
                    label="Billing Type"
                    disabled={
                      !isEditMode ||
                      (!isSubtask && workTypeWatch === "Quantity")
                    }
                    options={BILLABLE_OPTIONS}
                  />
                </div>
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormInput
                      name="taskWeightage"
                      label={
                        <span>
                          Weightage
                          {remainingWeightage !== null && (
                            <span
                              style={{
                                color: "#52c41a",
                                marginLeft: "8px",
                                fontWeight: "normal",
                              }}
                            >
                              (Avl: {remainingWeightage}%)
                            </span>
                          )}
                        </span>
                      }
                      placeholder="Enter Weightage %"
                      type="text"
                      pattern={/^(?:100(?:\.0{1,2})?|\d{1,2}(?:\.\d{1,2})?)$/}
                      patternErrorMessage={`Enter a value between 0.01 and ${
                        remainingWeightage ?? 100
                      } (up to 2 decimals)`}
                      customValidation={{
                        validate: (value: string) => {
                          if (
                            value === undefined ||
                            value === null ||
                            value === ""
                          )
                            return true;
                          const n = Number(value);
                          if (Number.isNaN(n)) return true;
                          if (n <= 0)
                            return "Weightage must be greater than 0";
                          if (remainingWeightage === null) return true;
                          return (
                            n <= remainingWeightage ||
                            `Cannot exceed available weightage (${remainingWeightage}%)`
                          );
                        },
                      }}
                    />
                  </div>
                )}
                {/* Rate, Budget Line, and Allocation Amount in same row - Only for tasks (not subtasks) */}
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormInput
                      name="rate"
                      label="Rate incl. GST (₹)"
                      placeholder="Enter Rate"
                      type="number"
                      required={true}
                      pattern={/^\d+(\.\d{1,2})?$/}
                      patternErrorMessage="Enter a valid rate"
                      customValidation={{
                        validate: (value: string) => {
                          const amount = parseFloat(value);
                          if (isNaN(amount))
                            return "Please enter a valid number";
                          if (amount <= 0) return "Rate must be greater than 0";
                          return true;
                        },
                      }}
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-2">
                    <FormInput
                      name="gstPercent"
                      label="GST %"
                      placeholder={String(DEFAULT_GST_PERCENT)}
                      type="number"
                      customValidation={{
                        validate: (value: string) => {
                          if (value === "" || value === null || value === undefined)
                            return true;
                          const pct = parseFloat(value);
                          if (isNaN(pct)) return "Please enter a valid number";
                          if (pct < 0 || pct > 100) return "GST % must be 0-100";
                          return true;
                        },
                      }}
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-2">
                    <FormInput
                      name="cessPercent"
                      label="Labour Cess %"
                      placeholder={String(DEFAULT_CESS_PERCENT)}
                      type="number"
                      customValidation={{
                        validate: (value: string) => {
                          if (value === "" || value === null || value === undefined)
                            return true;
                          const pct = parseFloat(value);
                          if (isNaN(pct)) return "Please enter a valid number";
                          if (pct < 0 || pct > 100) return "Cess % must be 0-100";
                          return true;
                        },
                      }}
                    />
                  </div>
                )}
                {/* Derived, read-only. GST is a pass-through and labour cess is withheld
                    by the client, so the Basic Rate is what the project actually earns
                    and what every costing / P&L report is based on. */}
                {!isSubtask && (
                  <div className="col-md-12">
                    <TaskPriceBreakdownPanel
                      breakdown={priceBreakdown}
                      gstPercent={Number(gstPercentWatch ?? DEFAULT_GST_PERCENT)}
                      cessPercent={Number(cessPercentWatch ?? DEFAULT_CESS_PERCENT)}
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormSelect
                      name="contractorId"
                      label="Contractor"
                      placeholder="Select Contractor"
                      disabled={
                        !isEditMode ||
                        !!activityContractorInfo?.activityContractorId
                      }
                      options={contractorOptions}
                      required={true}
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormSelect
                      name="budgetLineId"
                      label={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Budget Line</span>
                          <span style={{ color: "red" }}>*</span>
                          {selectedBudgetLine && (
                            <Popconfirm
                              placement="right"
                              title={<></>}
                              icon={<></>}
                              showCancel={false}
                              trigger="hover"
                              okButtonProps={{
                                style: {
                                  display: "none",
                                },
                              }}
                              description={
                                <div className="d-flex flex-column">
                                  <Descriptions
                                    title=""
                                    bordered
                                    column={1}
                                    size="small"
                                    className="custom-description-title"
                                  >
                                    <Descriptions.Item label="Budget Value">
                                      ₹
                                      {selectedBudgetLine.budgetValue?.toLocaleString(
                                        "en-IN",
                                      )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Allocated Value">
                                      ₹
                                      {selectedBudgetLine.allocatedLineValue?.toLocaleString(
                                        "en-IN",
                                      )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Remaining">
                                      ₹
                                      {(
                                        selectedBudgetLine.budgetValue -
                                        selectedBudgetLine.allocatedLineValue
                                      )?.toLocaleString("en-IN")}
                                    </Descriptions.Item>
                                  </Descriptions>
                                </div>
                              }
                            >
                              <Icon
                                name="info"
                                className="ms-1"
                                style={{
                                  color: "#004293",
                                  cursor: "pointer",
                                  fontSize: "16px",
                                }}
                              />
                            </Popconfirm>
                          )}
                        </div>
                      }
                      placeholder="Select Budget Line"
                      disabled={
                        !isEditMode ||
                        !!activityBudgetInfo?.activityBudgetLineId
                      }
                      options={budgetLines}
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormInput
                      name="allocatedBudgetAmount"
                      label="Budget Allocation Amount"
                      placeholder="Enter amount to allocate for this task"
                      type="number"
                      disabled={!isEditMode || !budgetLineIdWatch}
                      required={true}
                      pattern={new RegExp(`^(?:[1-9]\\d*)(?:\\.\\d{1,2})?$`)}
                      patternErrorMessage="Amount must be greater than 0"
                      customValidation={{
                        validate: (value: string) => {
                          const amount = parseFloat(value);
                          if (isNaN(amount))
                            return "Please enter a valid number";

                          const remainingBudget = selectedBudgetLine
                            ? selectedBudgetLine.budgetValue -
                              selectedBudgetLine.allocatedLineValue
                            : selectedBudgetValue || 0;

                          if (amount > remainingBudget) {
                            return `Amount cannot exceed remaining budget of ₹${remainingBudget?.toLocaleString(
                              "en-IN",
                            )}`;
                          }

                          return true;
                        },
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Site and Site Hierarchy Section - Only for tasks, not subtasks.
                  Hidden for Infra projects: their tasks don't capture site
                  hierarchy or location. */}
              {!isSubtask && !isInfraStructure && (
                <div style={stepStyle(1)}>
                  {/* Row 1: Site Hierarchy, Tower/Block, and Floor (for FLOOR/FLAT) or Location (for TOWER_BLOCK/VILLA) */}
                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <FormSelect
                        name="workLevelSetting"
                        label="Site Hierarchy"
                        disabled={true}
                        options={[
                          { label: "Tower / Block", value: "TOWER_BLOCK" },
                          { label: "Villa", value: "VILLA" },
                          { label: "Floor", value: "FLOOR" },
                          { label: "Flat", value: "FLAT" },
                          { label: "Site", value: "NONE" },
                        ]}
                        placeholder="Loading Site Hierarchy..."
                      />
                    </div>
                    {shouldShowHierarchy && (
                      <div className="col-md-4">
                        <FormSelect
                          name="siteStructureUnitId"
                          label={hierarchyFieldLabel}
                          disabled={!isEditMode}
                          options={towerOptions}
                          placeholder={hierarchyFieldPlaceholder}
                          required={true}
                        />
                      </div>
                    )}
                    {/* Show Floor in first row for FLOOR/FLAT, Location for TOWER_BLOCK/VILLA */}
                    {shouldShowHierarchy &&
                      (workLevelSetting === "FLOOR" ||
                        workLevelSetting === "FLAT") && (
                        <div className="col-md-4">
                          <FormSelect
                            name="floorNo"
                            label="Floor"
                            disabled={!isEditMode}
                            options={floorOptions}
                            placeholder="Select Floor"
                            required={true}
                          />
                        </div>
                      )}
                    {shouldShowHierarchy &&
                      (workLevelSetting === "TOWER_BLOCK" ||
                        workLevelSetting === "VILLA") && (
                        <div className="col-md-4">
                          <FormSelect
                            name="locationId"
                            label="Location"
                            disabled={!isEditMode}
                            options={locationOptions}
                            placeholder="Select Location"
                          />
                        </div>
                      )}
                    {!shouldShowHierarchy && (
                      <div className="col-md-4">
                        <FormSelect
                          name="locationId"
                          label="Location"
                          disabled={!isEditMode}
                          options={locationOptions}
                          placeholder="Select Location"
                        />
                      </div>
                    )}
                    {!shouldShowHierarchy && <div className="col-md-4"></div>}
                  </div>

                  {/* Row 2: For FLOOR - Location, For FLAT - Flat and Location */}
                  {shouldShowHierarchy && workLevelSetting === "FLOOR" && (
                    <div className="row g-3 mt-1">
                      <div className="col-md-4">
                        <FormSelect
                          name="locationId"
                          label="Location"
                          disabled={!isEditMode}
                          options={locationOptions}
                          placeholder="Select Location"
                        />
                      </div>
                      <div className="col-md-4"></div>
                      <div className="col-md-4"></div>
                    </div>
                  )}

                  {shouldShowHierarchy && workLevelSetting === "FLAT" && (
                    <div className="row g-3 mt-1">
                      <div className="col-md-4">
                        <FormSelect
                          name="siteStructureSubUnitId"
                          label="Flat"
                          disabled={!isEditMode}
                          options={flatOptions}
                          placeholder="Select Flat"
                          required={true}
                        />
                      </div>
                      <div className="col-md-4">
                        <FormSelect
                          name="locationId"
                          label="Location"
                          disabled={!isEditMode}
                          options={locationOptions}
                          placeholder="Select Location"
                        />
                      </div>
                      <div className="col-md-4"></div>
                    </div>
                  )}
                </div>
              )}

              <div className="row g-3 mt-1" style={stepStyle(1)}>
                <div className="col-md-4">
                  <label className="form-label">Tags</label>
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Type and press enter to create or select a tag"
                    labelInValue
                    value={tags}
                    onChange={(value) => setTags(value)}
                    options={projectTagsList}
                    tokenSeparators={[","]}
                    tagRender={tagRender}
                    filterOption={false}
                    disabled={!isEditMode}
                  />
                </div>
              </div>

              <div className="row g-3 mt-1" style={stepStyle(3)}>
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormSelect
                      name="ChecklistRequired"
                      label="Associated Checklist"
                      options={[
                        { label: "Yes", value: "yes" },
                        { label: "No", value: "no" },
                      ]}
                      placeholder="Select"
                    />
                    {watch("ChecklistRequired") == "yes" && (
                      <Popover
                        content="Click here to set required Checklist"
                        title="Checklist Required"
                      >
                        <a
                          href="#"
                          className="success"
                          onClick={(e) => {
                            e.preventDefault();
                            setChecklists((pre) => {
                              return {
                                ...pre,
                                open: true,
                              };
                            });
                          }}
                        >
                          <EditOutlined /> Set Checklist &nbsp;
                          {Checklists?.data?.length == 0 ? (
                            <span className="mandatory">
                              Please set Checklist
                            </span>
                          ) : (
                            <></>
                          )}
                        </a>
                      </Popover>
                    )}
                  </div>
                )}

                {!isSubtask && (
                  <div className="col-md-4">
                    <FormSelect
                      name="approvalRequired"
                      label="Work Approval Required ?"
                      options={[
                        { label: "Yes", value: "yes" },
                        { label: "No", value: "no" },
                      ]}
                      placeholder="Select"
                      required={true}
                    />
                    <div style={{ display: "none" }}>
                      <FormInput
                        name="stateApprovalHierarchy"
                        label=""
                        type="text"
                      />
                    </div>
                    {watch("approvalRequired") == "yes" && !isSubtask && (
                      <Popover
                        content="Click here to set required approvals"
                        title="Approval Required"
                      >
                        <a
                          href="#"
                          className="success"
                          onClick={(e) => {
                            e.preventDefault();
                            setApprovals((pre) => {
                              return {
                                ...pre,
                                open: true,
                              };
                            });
                          }}
                        >
                          <EditOutlined /> Set Approvals{" "}
                          {approvals?.data?.length == 0 ? (
                            <span className="mandatory">
                              Please set approvals
                            </span>
                          ) : (
                            <></>
                          )}
                        </a>
                      </Popover>
                    )}
                  </div>
                )}
                {!isSubtask && watch("approvalRequired") == "no" && (
                  <div className="col-12">
                    <FormInput
                      name="autoApprovalRemarks"
                      label="Remarks for No Approval"
                      type="textarea"
                      rows={2}
                      required={true}
                      minLength={2}
                      maxLength={500}
                      placeholder="Enter reason for not requiring approvals"
                    />
                  </div>
                )}
                {!isSubtask && (
                  <div className="col-md-4">
                    <FormSelect
                      name="documentsRequired"
                      label="Documents Required ?"
                      options={[
                        { label: "Yes", value: "yes" },
                        { label: "No", value: "no" },
                      ]}
                      placeholder="Select"
                      required={true}
                    />
                    <div style={{ display: "none" }}>
                      <FormInput name="documentsData" label="" type="text" />
                    </div>
                    {watch("documentsRequired") == "yes" && !isSubtask && (
                      <Popover
                        content="Click here to set required documents"
                        title="Documents Required"
                      >
                        <a
                          href="#"
                          className="success"
                          onClick={(e) => {
                            e.preventDefault();
                            setDocuments((pre) => {
                              return {
                                ...pre,
                                open: true,
                              };
                            });
                          }}
                        >
                          <EditOutlined /> Set Documents{" "}
                          {documents?.data?.length == 0 ? (
                            <span className="mandatory">
                              Please set documents
                            </span>
                          ) : (
                            <></>
                          )}
                        </a>
                      </Popover>
                    )}
                  </div>
                )}

                <div className="col-md-4">
                  <label
                    className="form-label"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Reminder
                  </label>
                  <ReminderPopper
                    setRemainders={setRemainders}
                    users={projectUsers}
                    selectedTaskDetails={
                      isSubtaskUpdate ? selectedSubtaskDetails : null
                    }
                    isSubtaskUpdate={!isEditMode}
                  />
                </div>
                {remainders?.reminderType && remainders?.specificTime && (
                  <div className="col-md-4">
                    <label
                      className="form-label"
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      Recurrence
                    </label>
                    <RecurringFrequencyPopper
                      setRemainders={setRemainders}
                      selectedTaskDetails={
                        isSubtaskUpdate ? selectedSubtaskDetails : null
                      }
                      isSubtaskUpdate={!isEditMode}
                    />
                  </div>
                )}
              </div>

              <div className="row g-3 mt-1" style={stepStyle(2)}>
                {!isSubtask && workTypeWatch === "Quantity" && (
                  <div className="col-12">
                    <div
                      className="card"
                      style={{
                        marginBottom: "24px",
                        border: "1px solid #e8e8e8",
                        borderRadius: "8px",
                      }}
                    >
                      <div className="card-body" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center mb-3">
                          <Icon
                            name="reports"
                            style={{
                              fontSize: "20px",
                              color: "#004293",
                              marginRight: "8px",
                            }}
                          />
                          <h6
                            className="title mb-0"
                            style={{ color: "#004293", fontWeight: "600" }}
                          >
                            Rate Analysis
                          </h6>
                        </div>
                        <p
                          className="text-soft mb-3"
                          style={{ fontSize: "13px" }}
                        >
                          Define the standard output (productivity) for this
                          work. The resource, tool and material rates below are
                          then captured per this output.
                        </p>
                        <div className="row g-3 align-items-end">
                          <div className="col-5">
                            <label className="form-label">
                              Output / Productivity Qty
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="e.g. 100"
                                min={0}
                                max={9999999999}
                                step="0.01"
                                onKeyDown={handleNumberKeyDown}
                                disabled={!isEditMode}
                                style={{ height: "38px" }}
                                {...methods.register("outputQuantity", {
                                  validate: (value: any) => {
                                    if (
                                      value === undefined ||
                                      value === null ||
                                      `${value}` === ""
                                    )
                                      return true;
                                    const out = parseFloat(value);
                                    if (isNaN(out))
                                      return "Please enter a valid number";
                                    if (out <= 0)
                                      return "Output qty must be greater than 0";
                                    const wq = Number(
                                      methods.getValues("workQuantity"),
                                    );
                                    if (wq && out > wq)
                                      return "Output qty cannot exceed Work Quantity";
                                    return true;
                                  },
                                })}
                              />
                              <span className="input-group-text">
                                {workUomName || "Work UOM"}
                              </span>
                              <span className="input-group-text">per</span>
                            </div>
                            {methods.formState.errors.outputQuantity && (
                              <span
                                className="text-danger"
                                style={{ fontSize: "12px" }}
                              >
                                {String(
                                  methods.formState.errors.outputQuantity
                                    .message || "",
                                )}
                              </span>
                            )}
                          </div>
                          <div className="col-md-4">
                            <FormSelect
                              name="outputBasisUomId"
                              label="Output Basis"
                              options={workUomsList}
                              placeholder="e.g. Day / Shift / Blast"
                              disabled={!isEditMode}
                            />
                          </div>
                        </div>
                        {outputQuantityWatch ? (
                          <div
                            className="mt-2"
                            style={{ fontSize: "13px", color: "#004293" }}
                          >
                            Rates will be captured per{" "}
                            <strong>
                              {outputQuantityWatch} {workUomName}
                              {outputBasisUomName
                                ? ` per ${outputBasisUomName}`
                                : ""}
                            </strong>
                            .
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}
                {/* Materials (BOQ) allocation removed — materials now come
                    from norms (Apply Norm), not task BOQ. */}
                {!isSubtask && (
                  <div className="col-12">
                    <div
                      className="card"
                      style={{
                        marginBottom: "24px",
                        border: "1px solid #e8e8e8",
                        borderRadius: "8px",
                      }}
                    >
                      <div className="card-body" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center mb-3">
                          <Icon
                            name="users"
                            style={{
                              fontSize: "20px",
                              color: "#004293",
                              marginRight: "8px",
                            }}
                          />
                          <h6
                            className="title mb-0"
                            style={{ color: "#004293", fontWeight: "600" }}
                          >
                            Allocate Manpower
                          </h6>
                        </div>
                        <Form
                          form={resourceForm}
                          component={false}
                          onValuesChange={bumpCostTick}
                        >
                          <Table
                            rowKey={(record) => `${record.activityResourceId}`}
                            columns={[
                              {
                                title: "Labour Type",
                                dataIndex: "laborTypeName",
                                key: "laborTypeName",
                                width: 200,
                              },
                              {
                                title: "Labour Category",
                                dataIndex: "laborCategory",
                                key: "laborCategory",
                                width: 200,
                              },
                              {
                                title: "Resource Count",
                                dataIndex: "resourceCount",
                                key: "resourceCount",
                                width: 150,
                                onCell: (record: any) => ({
                                  record,
                                  dataIndex: "resourceCount",
                                  title: "Resource Count",
                                  form: resourceForm,
                                  editing: isResourceEditing(record),
                                }),
                                render: (text: any) => text || "",
                              },
                              ...(showRateAnalysis
                                ? [
                              {
                                title: rateAnalysisLabel,
                                className: "rate-analysis-group",
                                align: "center" as const,
                                onHeaderCell: () => ({
                                  style: {
                                    background: "#e6f4ff",
                                    textAlign: "center" as const,
                                    fontWeight: 600,
                                  },
                                }),
                                children: [
                                  {
                                    title: "Qty",
                                    dataIndex: "rateAnalysisQty",
                                    key: "rateAnalysisQty",
                                    width: 130,
                                    className: "rate-analysis-group",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "rateAnalysisQty",
                                      title: "Rate Analysis Qty",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                      outputFactor,
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "UOM",
                                    dataIndex: "rateAnalysisUomId",
                                    key: "rateAnalysisUomId",
                                    width: 160,
                                    className: "rate-analysis-group",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "rateAnalysisUomId",
                                      title: "UOM",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                      workUomsList,
                                    }),
                                    render: (text: any) =>
                                      workUomsList.find(
                                        (u: SelectOption) => u.value === text,
                                      )?.label || "",
                                  },
                                  {
                                    title: "Rate",
                                    dataIndex: "rateAnalysisRate",
                                    key: "rateAnalysisRate",
                                    width: 130,
                                    className: "rate-analysis-group",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "rateAnalysisRate",
                                      title: "Rate",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Total Amount",
                                    dataIndex: "rateAnalysisTotalAmount",
                                    key: "rateAnalysisTotalAmount",
                                    width: 140,
                                    className: "rate-analysis-group",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "rateAnalysisTotalAmount",
                                      title: "Total Amount",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                    }),
                                  },
                                ],
                              },
                                  ]
                                : []),
                              {
                                title: overallLabel,
                                className: "overall-group",
                                align: "center" as const,
                                onHeaderCell: () => ({
                                  style: {
                                    background: "#f6ffed",
                                    textAlign: "center" as const,
                                    fontWeight: 600,
                                  },
                                }),
                                children: [
                                  {
                                    title: "Qty",
                                    dataIndex: "resourceQuantity",
                                    key: "resourceQuantity",
                                    width: 130,
                                    className: "overall-group",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "resourceQuantity",
                                      title: "Quantity",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "UOM",
                                    dataIndex: "workUomId",
                                    key: "workUomId",
                                    width: 160,
                                    className: "overall-group",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "workUomId",
                                      title: "UOM",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                      workUomsList,
                                    }),
                                    render: (text: any) =>
                                      workUomsList.find(
                                        (u: SelectOption) => u.value === text,
                                      )?.label || "",
                                  },
                                  {
                                    title: "Rate",
                                    dataIndex: "resourceRate",
                                    key: "resourceRate",
                                    width: 130,
                                    className: "overall-group",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "resourceRate",
                                      title: "Rate",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                    }),
                                    render: (text: any) => text || "",
                                  },
                                  {
                                    title: "Total Amount",
                                    dataIndex: "resourceTotalAmount",
                                    key: "resourceTotalAmount",
                                    width: 140,
                                    className: "overall-group",
                                    onCell: (record: any) => ({
                                      record,
                                      dataIndex: "resourceTotalAmount",
                                      title: "Total Amount",
                                      form: resourceForm,
                                      editing: isResourceEditing(record),
                                    }),
                                  },
                                ],
                              },
                            ]}
                            rowSelection={{
                              selectedRowKeys: selectedResourceKeys,
                              onChange: onResourceSelectChange,
                            }}
                            components={{
                              body: {
                                cell: ResourceEditableCell,
                              },
                            }}
                            dataSource={activityResourcesList}
                            pagination={false}
                            bordered
                            size="middle"
                            scroll={{ x: 600 }}
                          />
                          <Form.Item shouldUpdate noStyle>
                            {() => (
                              <SectionTotal
                                label="Manpower Total"
                                amount={computeResourceSectionTotal()}
                              />
                            )}
                          </Form.Item>
                        </Form>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tools & Machinery Section */}
                {!isSubtask && (
                  <div className="col-12">
                    <div
                      className="card"
                      style={{
                        marginBottom: "24px",
                        border: "1px solid #e8e8e8",
                        borderRadius: "8px",
                      }}
                    >
                      <div className="card-body" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center mb-3">
                          <Icon
                            name="building"
                            style={{
                              fontSize: "20px",
                              color: "#004293",
                              marginRight: "8px",
                            }}
                          />
                          <h6
                            className="title mb-0"
                            style={{ color: "#004293", fontWeight: "600" }}
                          >
                            Machinery
                          </h6>
                        </div>
                        <div className="row g-3 mt-2 align-items-end">
                          <div className="col-3">
                            <FormSelect
                              name="toolType"
                              label="Tool Type"
                              placeholder="Select Tool Type"
                              options={toolsAndMachineryTypes}
                              disabled={!isEditMode}
                            />
                          </div>
                          <div className="col-3">
                            <FormSelect
                              name="toolMachinery"
                              label="Machinery *"
                              placeholder="Select Machinery"
                              options={
                                selectedToolType
                                  ? toolsAndMachineryList.filter(
                                      (tool: any) =>
                                        tool.extra?.typeId === selectedToolType,
                                    )
                                  : toolsAndMachineryList
                              }
                              disabled={!isEditMode || !selectedToolType}
                            />
                          </div>
                          <div className="col-3">
                            <div className="form-group">
                              <label className="form-label">Serial Number</label>
                              <div className="form-control-wrap">
                                <ToolSerialNumberInput
                                  control={methods.control}
                                  setValue={methods.setValue}
                                  disabled={!isEditMode || !selectedTool}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Rate Analysis + Overall groups side by side */}
                        <div className="row g-3 mt-2">
                          {showRateAnalysis && (
                          <div className="col-6">
                            <div
                              className="p-2 h-100"
                              style={{
                                background: "#e6f4ff",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                className="mb-2"
                                style={{ fontWeight: 600, color: "#004293" }}
                              >
                                {rateAnalysisLabel}
                              </div>
                              <div className="row g-2 align-items-end">
                                <div className="col-3">
                                  <div className="form-group">
                                    <label className="form-label">Qty</label>
                                    <div className="form-control-wrap">
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Unit qty"
                                        min={0}
                                        max={9999999999}
                                        onKeyDown={handleNumberKeyDown}
                                        step="0.01"
                                        disabled={!isEditMode || !selectedTool}
                                        style={{ height: "38px" }}
                                        {...(() => {
                                          const ra = methods.register(
                                            "toolRateAnalysisQty",
                                          );
                                          return {
                                            ...ra,
                                            onChange: (e: any) => {
                                              ra.onChange(e);
                                              const f =
                                                Number(outputFactor) || 0;
                                              const v = Number(e.target.value);
                                              if (f > 0 && !isNaN(v)) {
                                                methods.setValue(
                                                  "toolQuantity",
                                                  String(
                                                    Number((f * v).toFixed(2)),
                                                  ),
                                                );
                                              }
                                            },
                                          };
                                        })()}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <FormSelect
                                    name="toolRateAnalysisUomId"
                                    label="UOM"
                                    placeholder="UOM"
                                    options={workUomsList}
                                    disabled={!isEditMode || !selectedTool}
                                  />
                                </div>
                                <div className="col-3">
                                  <div className="form-group">
                                    <label className="form-label">Rate</label>
                                    <div className="input-group">
                                      <span className="input-group-text">₹</span>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Unit rate"
                                        min={0}
                                        max={9999999999}
                                        onKeyDown={handleNumberKeyDown}
                                        step="0.01"
                                        disabled={!isEditMode || !selectedTool}
                                        style={{ height: "38px" }}
                                        {...(() => {
                                          const ra = methods.register(
                                            "toolRateAnalysisRate",
                                          );
                                          return {
                                            ...ra,
                                            onChange: (e: any) => {
                                              ra.onChange(e);
                                              methods.setValue(
                                                "toolRate",
                                                e.target.value,
                                              );
                                            },
                                          };
                                        })()}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="form-group">
                                    <label className="form-label">Total</label>
                                    <div className="form-control-wrap">
                                      <ToolRateAnalysisTotalPreview
                                        control={methods.control}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          )}

                          <div className={showRateAnalysis ? "col-6" : "col-12"}>
                            <div
                              className="p-2 h-100"
                              style={{
                                background: "#f6ffed",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                className="mb-2"
                                style={{ fontWeight: 600, color: "#004293" }}
                              >
                                {overallLabel}
                              </div>
                              <div className="row g-2 align-items-end">
                                <div className="col-3">
                                  <div className="form-group">
                                    <label className="form-label">
                                      Qty <span style={{ color: "#ff4d4f" }}>*</span>
                                    </label>
                                    <div className="form-control-wrap">
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Qty"
                                        min={0}
                                        max={9999999999}
                                        onKeyDown={handleNumberKeyDown}
                                        step="0.01"
                                        disabled={!isEditMode || !selectedTool}
                                        style={{ height: "38px" }}
                                        {...methods.register("toolQuantity")}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <FormSelect
                                    name="toolWorkUomId"
                                    label="UOM *"
                                    placeholder="UOM"
                                    options={workUomsList}
                                    disabled={!isEditMode || !selectedTool}
                                  />
                                </div>
                                <div className="col-3">
                                  <div className="form-group">
                                    <label className="form-label">
                                      Rate <span style={{ color: "#ff4d4f" }}>*</span>
                                    </label>
                                    <div className="input-group">
                                      <span className="input-group-text">₹</span>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Rate"
                                        min={0}
                                        max={9999999999}
                                        onKeyDown={handleNumberKeyDown}
                                        step="0.01"
                                        disabled={!isEditMode || !selectedTool}
                                        style={{ height: "38px" }}
                                        {...methods.register("toolRate")}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="form-group">
                                    <label className="form-label">Total</label>
                                    <div className="form-control-wrap">
                                      <ToolTotalPreview
                                        control={methods.control}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3">
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => {
                              const formToolSerialNumber =
                                methods.getValues("toolSerialNumber");
                              const formToolRate =
                                methods.getValues("toolRate");
                              const formToolQuantity =
                                methods.getValues("toolQuantity");
                              const formToolWorkUomId =
                                methods.getValues("toolWorkUomId");
                              const formToolRaQty = methods.getValues(
                                "toolRateAnalysisQty",
                              );
                              const formToolRaRate = methods.getValues(
                                "toolRateAnalysisRate",
                              );
                              const formToolRaUomId = methods.getValues(
                                "toolRateAnalysisUomId",
                              );
                              // Mandatory fields (also required by backend).
                              if (!selectedTool) {
                                messageApi.error("Please select a Tool / Machinery");
                                return;
                              }
                              if (
                                formToolQuantity === undefined ||
                                formToolQuantity === null ||
                                formToolQuantity === ""
                              ) {
                                messageApi.error("Please enter Quantity");
                                return;
                              }
                              if (
                                formToolWorkUomId === undefined ||
                                formToolWorkUomId === null
                              ) {
                                messageApi.error("Please select UOM");
                                return;
                              }
                              if (
                                formToolRate === undefined ||
                                formToolRate === null ||
                                formToolRate === ""
                              ) {
                                messageApi.error("Please enter Rate");
                                return;
                              }
                              const toolData = toolsAndMachineryList.find(
                                (t: any) => t.value === selectedTool,
                              );
                              const typeData = toolsAndMachineryTypes.find(
                                (t: any) => t.value === selectedToolType,
                              );

                              // Duplicate check (ignore the row being edited).
                              if (
                                taskTools.some(
                                  (t, i) =>
                                    t.toolId === selectedTool &&
                                    i !== editingToolIndex,
                                )
                              ) {
                                messageApi.error("Tool already added");
                                return;
                              }

                              const builtTool = {
                                toolId: selectedTool,
                                toolName: toolData?.label,
                                typeName: typeData?.label,
                                // Preserve split-materials when editing a tool.
                                ...(editingToolIndex !== null
                                  ? {
                                      toolMaterials:
                                        taskTools[editingToolIndex]
                                          ?.toolMaterials || [],
                                    }
                                  : {}),
                                ...(formToolSerialNumber
                                  ? { serialNumber: formToolSerialNumber }
                                  : {}),
                                ...(formToolRate !== undefined &&
                                formToolRate !== null &&
                                formToolRate !== ""
                                  ? { rate: Number(formToolRate) }
                                  : {}),
                                ...(formToolQuantity !== undefined &&
                                formToolQuantity !== null &&
                                formToolQuantity !== ""
                                  ? { quantity: Number(formToolQuantity) }
                                  : {}),
                                ...(formToolWorkUomId !== undefined &&
                                formToolWorkUomId !== null
                                  ? { workUomId: Number(formToolWorkUomId) }
                                  : {}),
                                ...(formToolRaQty !== undefined &&
                                formToolRaQty !== null &&
                                formToolRaQty !== ""
                                  ? { rateAnalysisQty: Number(formToolRaQty) }
                                  : {}),
                                ...(formToolRaUomId !== undefined &&
                                formToolRaUomId !== null
                                  ? {
                                      rateAnalysisUomId: Number(formToolRaUomId),
                                    }
                                  : {}),
                                ...(formToolRaRate !== undefined &&
                                formToolRaRate !== null &&
                                formToolRaRate !== ""
                                  ? { rateAnalysisRate: Number(formToolRaRate) }
                                  : {}),
                              };

                              if (editingToolIndex !== null) {
                                setTaskTools((prev) =>
                                  prev.map((t, i) =>
                                    i === editingToolIndex ? builtTool : t,
                                  ),
                                );
                              } else {
                                setTaskTools([...taskTools, builtTool]);
                              }

                              setEditingToolIndex(null);
                              setSelectedTool(null);
                              setSelectedToolType(null);
                              methods.setValue("toolType", null);
                              methods.setValue("toolMachinery", null);
                              methods.setValue("toolSerialNumber", "");
                              methods.setValue("toolRate", "");
                              methods.setValue("toolQuantity", "");
                              methods.setValue("toolWorkUomId", null);
                              methods.setValue("toolRateAnalysisQty", "");
                              methods.setValue("toolRateAnalysisRate", "");
                              methods.setValue("toolRateAnalysisUomId", null);
                            }}
                            disabled={!isEditMode}
                          >
                            {editingToolIndex !== null ? "Update Tool" : "Add Tool"}
                          </Button>
                          {editingToolIndex !== null && (
                            <Button
                              size="small"
                              className="ms-2"
                              onClick={() => {
                                setEditingToolIndex(null);
                                setSelectedTool(null);
                                setSelectedToolType(null);
                                methods.setValue("toolType", null);
                                methods.setValue("toolMachinery", null);
                                methods.setValue("toolSerialNumber", "");
                                methods.setValue("toolRate", "");
                                methods.setValue("toolQuantity", "");
                                methods.setValue("toolWorkUomId", null);
                                methods.setValue("toolRateAnalysisQty", "");
                                methods.setValue("toolRateAnalysisRate", "");
                                methods.setValue("toolRateAnalysisUomId", null);
                              }}
                              disabled={!isEditMode}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>

                        {/* Display added tools */}
                        {taskTools.length > 0 && (
                          <div className="mt-3">
                            <Table
                              dataSource={taskTools}
                              rowKey="toolId"
                              pagination={
                                taskTools.length > 10
                                  ? { pageSize: 10, size: "small" }
                                  : false
                              }
                              size="small"
                              bordered
                              scroll={{ x: "max-content" }}
                              columns={[
                                {
                                  title: "Tool Type",
                                  dataIndex: "typeName",
                                  key: "typeName",
                                  width: 300,
                                  align: "center" as const,
                                },
                                {
                                  title: "Machinery",
                                  dataIndex: "toolName",
                                  key: "toolName",
                                  width: 350,
                                  align: "center" as const,
                                },
                                {
                                  title: "Serial Number",
                                  dataIndex: "serialNumber",
                                  key: "serialNumber",
                                  width: 150,
                                  align: "center" as const,
                                  render: (text: any) => text ?? "",
                                },
                                ...(showRateAnalysis
                                  ? [
                                {
                                  title: rateAnalysisLabel,
                                  align: "center" as const,
                                  onHeaderCell: () => ({
                                    style: {
                                      background: "#e6f4ff",
                                      textAlign: "center" as const,
                                      fontWeight: 600,
                                    },
                                  }),
                                  children: [
                                    {
                                      title: "Qty",
                                      dataIndex: "rateAnalysisQty",
                                      key: "rateAnalysisQty",
                                      width: 110,
                                      align: "center" as const,
                                      render: (text: any) => text ?? "",
                                    },
                                    {
                                      title: "UOM",
                                      dataIndex: "rateAnalysisUomId",
                                      key: "rateAnalysisUomId",
                                      width: 130,
                                      align: "center" as const,
                                      render: (text: any) =>
                                        workUomsList.find(
                                          (u: SelectOption) => u.value === text,
                                        )?.label || "",
                                    },
                                    {
                                      title: "Rate",
                                      dataIndex: "rateAnalysisRate",
                                      key: "rateAnalysisRate",
                                      width: 110,
                                      align: "center" as const,
                                      render: (text: any) => text ?? "",
                                    },
                                    {
                                      title: "Total Amount",
                                      key: "raToolTotalAmount",
                                      width: 130,
                                      align: "center" as const,
                                      render: (_: any, record: any) => {
                                        const total =
                                          (Number(record.rateAnalysisRate) ||
                                            0) *
                                          (Number(record.rateAnalysisQty) || 0);
                                        return total ? total.toFixed(2) : "";
                                      },
                                    },
                                  ],
                                },
                                  ]
                                  : []),
                                {
                                  title: overallLabel,
                                  align: "center" as const,
                                  onHeaderCell: () => ({
                                    style: {
                                      background: "#f6ffed",
                                      textAlign: "center" as const,
                                      fontWeight: 600,
                                    },
                                  }),
                                  children: [
                                    {
                                      title: "Qty",
                                      dataIndex: "quantity",
                                      key: "quantity",
                                      width: 110,
                                      align: "center" as const,
                                      render: (text: any) => text ?? "",
                                    },
                                    {
                                      title: "UOM",
                                      dataIndex: "workUomId",
                                      key: "workUomId",
                                      width: 130,
                                      align: "center" as const,
                                      render: (text: any) =>
                                        workUomsList.find(
                                          (u: SelectOption) => u.value === text,
                                        )?.label || "",
                                    },
                                    {
                                      title: "Rate",
                                      dataIndex: "rate",
                                      key: "rate",
                                      width: 110,
                                      align: "center" as const,
                                      render: (text: any) => text ?? "",
                                    },
                                    {
                                      title: "Total Amount",
                                      key: "toolTotalAmount",
                                      width: 130,
                                      align: "center" as const,
                                      render: (_: any, record: any) => {
                                        const total =
                                          (Number(record.rate) || 0) *
                                          (Number(record.quantity) || 0) *
                                          (Number(record.count) || 1);
                                        return total ? total.toFixed(2) : "";
                                      },
                                    },
                                  ],
                                },
                                {
                                  title: "Materials",
                                  key: "materials",
                                  width: 170,
                                  align: "center" as const,
                                  render: (_: any, record: any, index: number) => {
                                    const mats = record.toolMaterials || [];
                                    const matTotal = mats.reduce(
                                      (s: number, m: ToolMaterialItem) =>
                                        s +
                                        (Number(m.quantity) || 0) *
                                          (Number(m.rate) || 0),
                                      0,
                                    );
                                    return (
                                      <Button
                                        type={mats.length ? "primary" : "default"}
                                        size="small"
                                        onClick={() =>
                                          setMaterialsDrawer({
                                            open: true,
                                            toolIndex: index,
                                          })
                                        }
                                      >
                                        <em className="icon ni ni-list" />
                                        {mats.length
                                          ? ` ${mats.length} · ₹${matTotal.toFixed(2)}`
                                          : " Add"}
                                      </Button>
                                    );
                                  },
                                },
                                {
                                  title: "Action",
                                  key: "action",
                                  width: 110,
                                  align: "center" as const,
                                  render: (
                                    _: any,
                                    record: any,
                                    index: number,
                                  ) => (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "12px",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <em
                                        className="icon ni ni-edit"
                                        title="Edit tool"
                                        onClick={() => {
                                          if (!isEditMode) return;
                                          // Load this tool's values into the add-row.
                                          setSelectedToolType(
                                            record.typeName
                                              ? toolsAndMachineryTypes.find(
                                                  (t: any) =>
                                                    t.label === record.typeName,
                                                )?.value ?? null
                                              : null,
                                          );
                                          setSelectedTool(record.toolId);
                                          methods.setValue(
                                            "toolType",
                                            toolsAndMachineryTypes.find(
                                              (t: any) =>
                                                t.label === record.typeName,
                                            )?.value ?? null,
                                          );
                                          methods.setValue(
                                            "toolMachinery",
                                            record.toolId,
                                          );
                                          methods.setValue(
                                            "toolSerialNumber",
                                            record.serialNumber ?? "",
                                          );
                                          methods.setValue(
                                            "toolQuantity",
                                            record.quantity != null
                                              ? String(record.quantity)
                                              : "",
                                          );
                                          methods.setValue(
                                            "toolWorkUomId",
                                            record.workUomId ?? null,
                                          );
                                          methods.setValue(
                                            "toolRate",
                                            record.rate != null
                                              ? String(record.rate)
                                              : "",
                                          );
                                          methods.setValue(
                                            "toolRateAnalysisQty",
                                            record.rateAnalysisQty != null
                                              ? String(record.rateAnalysisQty)
                                              : "",
                                          );
                                          methods.setValue(
                                            "toolRateAnalysisUomId",
                                            record.rateAnalysisUomId ?? null,
                                          );
                                          methods.setValue(
                                            "toolRateAnalysisRate",
                                            record.rateAnalysisRate != null
                                              ? String(record.rateAnalysisRate)
                                              : "",
                                          );
                                          setEditingToolIndex(index);
                                        }}
                                        style={{
                                          cursor: isEditMode
                                            ? "pointer"
                                            : "not-allowed",
                                          fontSize: "16px",
                                          color: "#004293",
                                        }}
                                      ></em>
                                      <em
                                        className="icon ni ni-trash"
                                        title="Remove tool"
                                        onClick={() => {
                                          if (!isEditMode) return;
                                          setTaskTools(
                                            taskTools.filter(
                                              (_t, i) => i !== index,
                                            ),
                                          );
                                          if (editingToolIndex === index) {
                                            setEditingToolIndex(null);
                                          }
                                        }}
                                        style={{
                                          cursor: isEditMode
                                            ? "pointer"
                                            : "not-allowed",
                                          fontSize: "16px",
                                          color: "#ff4d4f",
                                        }}
                                      ></em>
                                    </div>
                                  ),
                                },
                              ]}
                            />
                            <SectionTotal
                              label="Machinery Total"
                              amount={computeToolsSectionTotal()}
                            />
                          </div>
                        )}

                        {/* Per-tool split-materials drawer */}
                        <Drawer
                          title={
                            materialsDrawer.toolIndex != null
                              ? `Split Materials — ${
                                  taskTools[materialsDrawer.toolIndex]
                                    ?.toolName || "Tool"
                                }`
                              : "Split Materials"
                          }
                          placement="right"
                          width={Math.min(
                            980,
                            typeof window !== "undefined"
                              ? window.innerWidth - 80
                              : 980,
                          )}
                          open={materialsDrawer.open}
                          onClose={() =>
                            setMaterialsDrawer({ open: false, toolIndex: null })
                          }
                          destroyOnClose
                        >
                          {materialsDrawer.toolIndex != null &&
                            taskTools[materialsDrawer.toolIndex] && (
                              <ToolMaterialsManager
                                materials={
                                  taskTools[materialsDrawer.toolIndex]
                                    .toolMaterials || []
                                }
                                toolOverallTotal={
                                  (Number(
                                    taskTools[materialsDrawer.toolIndex].rate,
                                  ) || 0) *
                                  (Number(
                                    taskTools[materialsDrawer.toolIndex]
                                      .quantity,
                                  ) || 0) *
                                  (Number(
                                    taskTools[materialsDrawer.toolIndex].count,
                                  ) || 1)
                                }
                                materialOptions={materialOptions}
                                workUomsList={workUomsList}
                                showRateAnalysis={showRateAnalysis}
                                outputFactor={outputFactor}
                                disabled={!isEditMode}
                                rateAnalysisLabel={rateAnalysisLabel}
                                overallLabel={overallLabel}
                                onChange={(updatedMaterials) => {
                                  const idx = materialsDrawer.toolIndex as number;
                                  setTaskTools((prev) =>
                                    prev.map((t, i) =>
                                      i === idx
                                        ? { ...t, toolMaterials: updatedMaterials }
                                        : t,
                                    ),
                                  );
                                }}
                              />
                            )}
                        </Drawer>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cost Summary (Materials + Resources + Tools) */}
                {!isSubtask && (
                  <div className="col-12">
                    <div
                      className="card"
                      style={{
                        marginBottom: "24px",
                        border: "1px solid #004293",
                        borderRadius: "8px",
                      }}
                    >
                      <div className="card-body" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center mb-3">
                          <Icon
                            name="coin"
                            style={{
                              fontSize: "20px",
                              color: "#004293",
                              marginRight: "8px",
                            }}
                          />
                          <h6
                            className="title mb-0"
                            style={{ color: "#004293", fontWeight: "600" }}
                          >
                            Cost Summary
                          </h6>
                        </div>
                        <div className="row">
                          {showRateAnalysis && (
                            <div className="col-md-6">
                              <div
                                className="p-3 h-100"
                                style={{
                                  background: "#e6f4ff",
                                  borderRadius: "6px",
                                }}
                              >
                                <div
                                  className="mb-2"
                                  style={{
                                    fontWeight: 600,
                                    color: "#004293",
                                  }}
                                >
                                  {rateAnalysisLabel}
                                </div>
                                <SectionTotal
                                  label="Materials"
                                  amount={computeBoqRateAnalysisTotal()}
                                />
                                <SectionTotal
                                  label="Manpower"
                                  amount={computeResourceRateAnalysisTotal()}
                                />
                                <SectionTotal
                                  label="Machinery"
                                  amount={computeToolsRateAnalysisTotal()}
                                />
                                <hr className="my-2" />
                                <SectionTotal
                                  label="Total (per unit basis)"
                                  amount={computeRateAnalysisGrandTotal()}
                                />
                              </div>
                            </div>
                          )}
                          <div
                            className={showRateAnalysis ? "col-md-6" : "col-12"}
                          >
                            <div
                              className="p-3 h-100"
                              style={{
                                background: "#f6ffed",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                className="mb-2"
                                style={{ fontWeight: 600, color: "#004293" }}
                              >
                                {overallLabel}
                              </div>
                              <SectionTotal
                                label="Materials"
                                amount={computeBoqSectionTotal()}
                              />
                              <SectionTotal
                                label="Manpower"
                                amount={computeResourceSectionTotal()}
                              />
                              <SectionTotal
                                label="Machinery"
                                amount={computeToolsSectionTotal()}
                              />
                              <hr className="my-2" />
                              <SectionTotal
                                label="Overall Total"
                                amount={computeOverallGrandTotal()}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="row g-3 mt-1" style={stepStyle(3)}>
                {/* Hyperlinks Section */}
                {!isSubtask && (
                  <div className="col-12">
                    <div
                      className="card"
                      style={{
                        marginBottom: "24px",
                        border: "1px solid #e8e8e8",
                        borderRadius: "8px",
                      }}
                    >
                      <div className="card-body" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center mb-3">
                          <Icon
                            name="link"
                            style={{
                              fontSize: "20px",
                              color: "#004293",
                              marginRight: "8px",
                            }}
                          />
                          <h6
                            className="title mb-0"
                            style={{ color: "#004293", fontWeight: "600" }}
                          >
                            Hyperlinks
                          </h6>
                        </div>
                        <div className="row g-3 mt-2">
                          <div className="col-md-4">
                            <div className="form-group">
                              <label className="form-label">Link Name</label>
                              <div className="form-control-wrap">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter link name"
                                  disabled={!isEditMode}
                                  style={{ height: "38px" }}
                                  {...methods.register("hyperlinkName")}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <label className="form-label">URL</label>
                              <div className="form-control-wrap">
                                <input
                                  type="url"
                                  className="form-control"
                                  placeholder="Enter URL"
                                  disabled={!isEditMode}
                                  style={{ height: "38px" }}
                                  {...methods.register("hyperlinkUrl")}
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="col-2 d-flex align-items-center justify-content-start"
                            style={{ marginTop: "45px" }}
                          >
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => {
                                const formHyperlinkName =
                                  methods.getValues("hyperlinkName");
                                const formHyperlinkUrl =
                                  methods.getValues("hyperlinkUrl");
                                if (
                                  !formHyperlinkName ||
                                  !formHyperlinkUrl ||
                                  !formHyperlinkName.trim() ||
                                  !formHyperlinkUrl.trim()
                                ) {
                                  messageApi.error(
                                    "Please enter both link name and URL",
                                  );
                                  return;
                                }

                                // Check if hyperlink already exists
                                if (
                                  hyperlinks.some(
                                    (h) => h.name === formHyperlinkName.trim(),
                                  )
                                ) {
                                  messageApi.error("Link name already exists");
                                  return;
                                }

                                setHyperlinks([
                                  ...hyperlinks,
                                  {
                                    name: formHyperlinkName.trim(),
                                    link: formHyperlinkUrl.trim(),
                                  },
                                ]);
                                methods.setValue("hyperlinkName", "");
                                methods.setValue("hyperlinkUrl", "");
                              }}
                              disabled={!isEditMode}
                            >
                              Add Link
                            </Button>
                          </div>
                        </div>

                        {/* Display added hyperlinks */}
                        {hyperlinks.length > 0 && (
                          <div className="mt-3">
                            <Table
                              dataSource={hyperlinks}
                              rowKey="name"
                              pagination={false}
                              size="small"
                              bordered
                              columns={[
                                {
                                  title: "Link Name",
                                  dataIndex: "name",
                                  key: "name",
                                  width: 300,
                                  align: "center" as const,
                                },
                                {
                                  title: "URL",
                                  dataIndex: "link",
                                  key: "link",
                                  width: 400,
                                  align: "center" as const,
                                  render: (text: string) => (
                                    <a
                                      href={text}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: "#004293" }}
                                    >
                                      {text}
                                    </a>
                                  ),
                                },
                                {
                                  title: "Action",
                                  key: "action",
                                  width: 100,
                                  align: "center" as const,
                                  render: (_: any, record: any) => (
                                    <em
                                      className="icon ni ni-trash"
                                      onClick={() => {
                                        setHyperlinks(
                                          hyperlinks.filter(
                                            (h) => h.name !== record.name,
                                          ),
                                        );
                                      }}
                                      style={{
                                        cursor: "pointer",
                                        fontSize: "16px",
                                        color: "#ff4d4f",
                                        padding: "4px",
                                      }}
                                    ></em>
                                  ),
                                },
                              ]}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="row g-3 mt-1" style={stepStyle(1)}>
                <div className="col-md-4">
                  <FormMultiSelect
                    name="projectUserId"
                    label="Associated Team"
                    disabled={!isEditMode}
                    placeholder="Select"
                    options={teamOptions}
                  />
                </div>
              </div>

              {isPageMode && (
                <div className="row g-3 mt-1" style={stepStyle(3)}>
                  <div className="col-12">
                    <div className="task-review-summary">
                      <div className="pms-eyebrow">Review</div>
                      <div className="task-review-grid">
                        {reviewItem("Task Name", watch("taskName"))}
                        {reviewItem(
                          "Activity",
                          pageContext?.phaseName
                            ? `${wbsName(pageContext?.activityName)} (${wbsName(
                                pageContext?.phaseName,
                              )})`
                            : wbsName(pageContext?.activityName),
                        )}
                        {reviewItem(
                          "Work Category",
                          reviewOptionLabel(
                            workCatgoriesList,
                            watch("workCategoryId"),
                          ),
                        )}
                        {reviewItem(
                          "Work",
                          watch("workType") === "Quantity"
                            ? `${watch("workQuantity") ?? "—"} ${reviewOptionLabel(
                                workUomsList,
                                watch("workUomId"),
                              )}`
                            : "Open",
                        )}
                        {reviewItem(
                          "Owner",
                          reviewOptionLabel(ownerOptions, watch("taskOwnerId")),
                        )}
                        {reviewItem(
                          "Contractor",
                          reviewOptionLabel(
                            contractorOptions,
                            watch("contractorId"),
                          ),
                        )}
                        {reviewItem(
                          "Team Members",
                          (watch("projectUserId") || []).length,
                        )}
                        {reviewItem(
                          "Planned Dates",
                          `${reviewDate(
                            watch("taskPlannedStartDate"),
                          )} → ${reviewDate(watch("taskPlannedEndDate"))}`,
                        )}
                        {reviewItem(
                          "Priority",
                          reviewOptionLabel(priorityList, watch("priorityId")),
                        )}
                        {reviewItem("Billing", watch("isBillable"))}
                        {reviewItem(
                          "Weightage",
                          watch("taskWeightage") != null &&
                            watch("taskWeightage") !== ("" as any)
                            ? `${watch("taskWeightage")}%`
                            : "—",
                        )}
                        {/* The rate the user typed, plus what the system derives from it.
                            Basic Rate is the number every costing / P&L report uses. */}
                        {reviewItem(
                          "Rate incl. GST",
                          priceBreakdown.rate
                            ? reviewMoney(priceBreakdown.rate)
                            : "—",
                        )}
                        {reviewItem(
                          "Rate excl. GST",
                          priceBreakdown.rate
                            ? reviewMoney(priceBreakdown.baseRate)
                            : "—",
                        )}
                        {reviewItem(
                          `GST ${Number(gstPercentWatch ?? DEFAULT_GST_PERCENT)}%`,
                          priceBreakdown.rate
                            ? reviewMoney(priceBreakdown.gstAmount)
                            : "—",
                        )}
                        {reviewItem(
                          `Labour Cess ${Number(
                            cessPercentWatch ?? DEFAULT_CESS_PERCENT,
                          )}%`,
                          priceBreakdown.rate
                            ? reviewMoney(priceBreakdown.cessAmount)
                            : "—",
                        )}
                        {reviewItem(
                          "Basic Rate",
                          priceBreakdown.rate
                            ? reviewMoney(priceBreakdown.uploadRate)
                            : "—",
                        )}
                        {reviewItem(
                          "Budget Line",
                          reviewOptionLabel(budgetLines, watch("budgetLineId")),
                        )}
                        {reviewItem(
                          "Allocated Budget",
                          watch("allocatedBudgetAmount")
                            ? reviewMoney(Number(watch("allocatedBudgetAmount")))
                            : "—",
                        )}
                        {reviewItem(
                          "Materials Total",
                          reviewMoney(computeBoqSectionTotal()),
                        )}
                        {reviewItem(
                          "Manpower Total",
                          reviewMoney(computeResourceSectionTotal()),
                        )}
                        {reviewItem(
                          "Machinery Total",
                          reviewMoney(computeToolsSectionTotal()),
                        )}
                        {reviewItem(
                          "Estimated Cost",
                          reviewMoney(
                            computeBoqSectionTotal() +
                              computeResourceSectionTotal() +
                              computeToolsSectionTotal(),
                          ),
                        )}
                        {reviewItem(
                          "Checklist Items",
                          watch("ChecklistRequired") === "yes"
                            ? Checklists?.data?.length ?? 0
                            : "Not required",
                        )}
                        {reviewItem(
                          "Approval Levels",
                          watch("approvalRequired") === "yes"
                            ? approvals?.data?.length ?? 0
                            : "Auto approved",
                        )}
                        {reviewItem(
                          "Documents",
                          watch("documentsRequired") === "yes"
                            ? documents?.data?.length ?? 0
                            : "Not required",
                        )}
                        {reviewItem("Hyperlinks", hyperlinks.length)}
                        {reviewItem(
                          "Reminder",
                          remainders?.reminderType ? "Set" : "None",
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
            <SequenceConfigModal
              moduleName="Tasks"
              fieldLabel="Task Number"
              show={showNumberingModal}
              tempNumberingMode={tempNumberingMode}
              setTempNumberingMode={setTempNumberingMode}
              canAuto={canAuto}
              currentSequence={currentSequence}
              activeSequences={activeSequences}
              onSave={saveModalSelection}
              onClose={closeNumberingModal}
            />
          </FormProvider>
        )}
    </>
  );

  return (
    <>
      {isPageMode ? (
        <div className="task-create-body">{bodyContent}</div>
      ) : (
        <Drawer
          zIndex={1000}
          maskClosable={false}
          maskStyle={{ zIndex: 999 }}
          title={drawerTitle}
          placement="right"
          width={960}
          style={{ maxWidth: "95vw" }}
          onClose={closeCreateTaskDrawer}
          open={showTaskCreationModal || isSubtaskUpdate}
          styles={{ body: { paddingBottom: 80 } }}
        >
          {bodyContent}
        </Drawer>
      )}
      {approvals.open && (
        <IssueApprovalDrawer
          open={approvals.open}
          projectId={projectId || 0}
          excludeUserId={selectedOwnerId ? Number(selectedOwnerId) : undefined}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any) => {
            setApprovals({
              open: false,
              data: data,
            });
          }}
          setApprovalSubmissionStatus={(status: boolean) => {}}
          approvalData={approvals.data} // Pass the current approval data
        />
      )}
      {Checklists.open && (
        <CheckListDrawer
          open={Checklists.open}
          onClose={() => {
            setChecklists((pre) => ({
              ...pre,
              open: false,
              data: [], // Clear data when drawer is closed without submission
            }));
          }}
          onSubmit={(data: any) => {
            // Additional validation to ensure only complete data is saved
            const validData = data.filter(
              (item: any) => item.status && item.checklist,
            );

            if (validData.length === 0) {
              console.warn("No valid checklist data to save");
              setChecklists({
                open: false,
                data: [],
              });
              return;
            }

            setChecklistLinks(
              validData.map((item: any) => ({
                checklistId: item.checklist,
                isMandatory: item.mandatory,
                status: item.status,
              })),
            );
            setChecklists({
              open: false,
              data: validData,
            });
          }}
          setChecklistSubmissionStatus={() => {}}
          ChecklistData={Checklists.data}
          siteId={0}
        />
      )}
      {documents.open && (
        <DocumentsTab
          linkType="task"
          linkToId={taskId || id || ""}
          linkToName={selectedTaskDetails?.taskName || watch("taskName") || ""}
          projectId={projectId}
          projectName={projectName}
          taskName={selectedTaskDetails?.taskName || watch("taskName") || ""}
          shouldLoadData={false}
          isFromWorkUI={false}
          openDrawer={documents.open}
          onDrawerClose={() => {
            setDocuments((pre) => ({
              ...pre,
              open: false,
            }));
          }}
          onDocumentsSubmit={(data: { docKeys: any[]; files: File[] }) => {
            setDocuments({
              open: false,
              data: data.docKeys,
            });
            setDocumentFiles(data.files);
          }}
          initialDocData={
            documents.data.length > 0 || documentFiles.length > 0
              ? { docKeys: documents.data, files: documentFiles }
              : undefined
          }
        />
      )}
    </>
  );
};

export default TaskCreation;
